import { API_BASE_URL } from "../config/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { appendAuthToken } from "../utils/auth";
import ReactSelect from "react-select";
import * as XLSX from "xlsx";

const buildEmptyItem = () => ({
  productId: "",
  itemName: "",
  itemCode: "",
  hsnCode: "",
  description: "",
  quantity: "",
  unit: "No",
  stockCategory: "sales",
  location: "",
  listPrice: "",
  discountType: "percent",
  discountValue: "",
  unitPrice: "",
  totalAmount: ""
});

const GENMECH_DELIVERY_ADDRESS = "Genmech Service, 63/2A2,L&T Bypass Road, Pattanam, Coimbatore - 641016";

// eslint-disable-next-line no-unused-vars
const CATEGORY_MAP = {
  "Electrical": [
    "MCCB", "MCB", "ACB", "VCB", "ELCB", "Contactor", "Capacitor Duty Contactor",
    "Relay", "Earth Fault Relay", "Differential Relay", "Reverse Power Relay",
    "Motor Protection Relay", "Under Voltage Relay", "Over Voltage Relay",
    "UVR", "Shunt Release", "Auxiliary Contact", "Mechanical Interlock", "Rotary Handle"
  ],
  "Automation": [
    "PLC", "HMI", "VFD", "Servo Drive", "SCADA Software", "Controller"
  ],
  "Instrumentation": [
    "Current Transformer (CT)", "Potential Transformer (PT)", "Multifunction Meter",
    "Ammeter", "Voltmeter", "Transducer", "Potentiometer"
  ],
  "Hardware & Fasteners": [
    "Bolt", "Nut", "Washer", "Screw", "Anchor Fastener", "Stud", "U Clamp"
  ],
  "Cable & Wiring": [
    "Cable", "Cable Gland", "Cable Lug", "Ferrule", "Heat Shrink"
  ],
  "Panel Accessories": [
    "Push Button", "Selector Switch", "Indication Lamp", "Cooling Fan",
    "Terminal Block", "Panel Lock"
  ],
  "Mechanical": [
    "Bearing", "Coupling", "Pulley", "Belt"
  ],
  "Consumables": [
    "Insulation Tape", "Cable Tie", "Grease", "Contact Cleaner", "Adhesive"
  ],
  "Generator Control": [
    "AVR", "AMF Controller", "Speed Controller", "Sensor", "Space Heater"
  ],
  "OEM Spares": [],
  "Others": []
};

const SUBCATEGORY_SPECS = {
  "MCCB": ["Frame Type", "Current Rating (A)", "Pole", "Breaking Capacity (kA)", "Voltage (V)", "Trip Unit", "Mounting Type"],
  "MCB": ["Current Rating (A)", "Pole", "Breaking Capacity (kA)", "Curve Type (B/C/D)", "Voltage (V)"],
  "ACB": ["Current Rating", "Pole", "Breaking Capacity", "Voltage", "Drawout/Fixed", "Trip Unit"],
  "Contactor": ["Current Rating", "Coil Voltage", "Pole", "Duty Type", "Auxiliary Contacts"],
  "Capacitor Duty Contactor": ["Current Rating", "Coil Voltage", "Capacitor Rating"],
  "Relay": ["Relay Type", "Voltage", "Current", "Application", "Protection Type"],
  "Earth Fault Relay": ["Voltage", "Current Setting", "Time Setting", "Protection Class"],
  "Differential Relay": ["Voltage", "CT Ratio", "Application"],
  "Reverse Power Relay": ["Voltage", "Frequency", "Power Setting"],
  "Motor Protection Relay": ["Motor Rating", "Voltage", "Current Range"],
  "Under Voltage Relay": ["Operating Voltage", "Reset Voltage"],
  "Current Transformer (CT)": ["Ratio", "Burden (VA)", "Accuracy Class", "Core Type", "Application"],
  "Potential Transformer (PT)": ["Ratio", "Burden", "Accuracy Class"],
  "AVR": ["Generator Type", "Voltage", "Frequency", "Excitation Type"],
  "AMF Controller": ["Voltage", "Frequency", "Engine Compatibility"],
  "Speed Controller": ["Supply Voltage", "Speed Range"],
  "Sensor": ["Sensor Type", "Range", "Voltage", "Output Type"],
  "Space Heater": ["Voltage", "Wattage"],
  "Multifunction Meter": ["Voltage", "Current", "Communication Protocol"],
  "Ammeter": ["Range", "Accuracy Class"],
  "Voltmeter": ["Range", "Accuracy Class"],
  "Transducer": ["Input Range", "Output Range"],
  "Potentiometer": ["Resistance Value"],
  "UVR": ["Voltage Range", "Compatible Breaker"],
  "Shunt Release": ["Voltage Range", "Compatible Breaker"],
  "Auxiliary Contact": ["Contact Type", "Compatible Breaker"],
  "Mechanical Interlock": ["Compatible Breaker"],
  "Rotary Handle": ["Compatible Breaker"],
  "Terminal Block": ["Current Rating", "Voltage Rating"],
  "Electrical Accessories": ["Type", "Specification"],
  "Bolt": ["Diameter", "Length", "Material", "Grade", "Thread Type", "Finish"],
  "Nut": ["Diameter", "Material", "Grade"],
  "Cable": ["Core", "Size", "Voltage Grade", "Conductor Material"],
  "Bearing": ["Bearing Number", "Type", "Size"]
};

const buildEmptyForm = () => ({
  entryType: "purchase",
  orderType: "supplier_order",
  documentNumber: "",
  poNumber: "",
  poDate: "",
  reference: "",
  referenceDate: "",
  transactionDate: new Date().toISOString().slice(0, 10),
  dealerId: "",
  customerId: "",
  counterpartyName: "",
  counterpartyPhone: "",
  counterpartyEmail: "",
  counterpartyGst: "",
  supplierGst: "",
  endUserGst: "",
  counterpartyAddress: "",
  deliveryAddressType: "billing_address",
  otherDeliveryAddress: "",
  sourceQuotationId: "",
  sourceQuotationNo: "",
  sourceDeliveryChallanId: "",
  sourceDeliveryChallanNo: "",
  sourceDeliveryChallanType: "",
  skipInventoryDeduction: false,
  location: "",
  notes: "",
  payment: "",
  paymentMode: "",
  roundOffEnabled: "no",
  roundOffAmount: "",
  finalTotal: "",
  freightType: "paid",
  dispatchMode: "by_road",
  expectedDeliveryDate: "",
  deliveryAt: "",
  deliveryNote: "",
  dispatchThrough: "",
  ewayBillNo: "",
  otherReferences: "",
  dispatchDocNo: "",
  deliveryNoteDate: "",
  destination: "",
  billOfLading: "",
  motorVehicleNo: "",
  deliveryTime: "Within 2-3 weeks after confirmed purchase order",
  paymentTerms: "50% advance along with PO, balance before dispatch",
  warranty: "12 months against manufacturing defects",
  terms: "Prices are valid for the agreed document date. Taxes are extra as applicable. Freight and installation are extra unless mentioned.",
  showDiscountInPdf: false,
  commonDiscountType: "percent",
  billingStatus: "not_billed",
  paymentStatus: "not_done",
  gstType: "cgst_sgst",
  sgst: "9",
  cgst: "9",
  igst: "18",
  gst: "18",
  advanceAmount: "",
  balanceAmount: "",
  payDate: "",
  entries: [buildEmptyItem()]
});

const buildEmptyFormForEntryType = (entryType = "") => {
  const form = buildEmptyForm();
  form.entryType = entryType || form.entryType;

  if (form.entryType === "order") {
    form.documentNumber = "";
    form.deliveryAddressType = "genmech_service";
    form.counterpartyAddress = GENMECH_DELIVERY_ADDRESS;
    form.roundOffEnabled = "no";
  }

  if (form.entryType === "sale") {
    form.deliveryAddressType = "work_address";
  }

  return form;
};

const buildPrefilledDocumentForm = (prefill = {}, entryType = "", customers = []) => {
  const normalizedEntryType = String(entryType || prefill.entryType || "").trim().toLowerCase();
  const baseForm = buildEmptyFormForEntryType(normalizedEntryType);
  const matchedCustomer = normalizedEntryType === "sale"
    ? customers.find((customer) => String(customer._id || "") === String(prefill.customerId || ""))
      || customers.find((customer) =>
        [customer.name, customer.customerId]
          .filter(Boolean)
          .some((entry) => entry.toLowerCase() === String(prefill.counterpartyName || "").trim().toLowerCase())
      )
    : null;

  const resolvedBillingAddress = matchedCustomer
    ? formatAddressObject(matchedCustomer.billingAddress || matchedCustomer.address)
    : "";
  const resolvedShipToAddress = normalizedEntryType === "sale"
    ? (prefill.deliveryAddressType === "billing_address"
      ? (resolvedBillingAddress || prefill.counterpartyAddress || "")
      : prefill.counterpartyAddress || "")
    : (prefill.counterpartyAddress || "");

  return {
    ...baseForm,
    ...prefill,
    entryType: normalizedEntryType || baseForm.entryType,
    customerId: prefill.customerId || matchedCustomer?._id || "",
    counterpartyName: prefill.counterpartyName || matchedCustomer?.name || "",
    counterpartyPhone: prefill.counterpartyPhone || matchedCustomer?.phone || "",
    counterpartyEmail: prefill.counterpartyEmail || matchedCustomer?.email || "",
    counterpartyGst: prefill.counterpartyGst || matchedCustomer?.gstNumber || "",
    transactionDate: prefill.transactionDate || new Date().toISOString().slice(0, 10),
    payDate: prefill.payDate ? new Date(prefill.payDate).toISOString().slice(0, 10) : "",
    referenceDate: prefill.referenceDate || "",
    deliveryAddressType: prefill.deliveryAddressType || (normalizedEntryType === "sale" ? "billing_address" : baseForm.deliveryAddressType),
    counterpartyAddress: resolvedShipToAddress,
    showDiscountInPdf: Boolean(prefill.showDiscountInPdf),
    commonDiscountType: Array.isArray(prefill.entries) && prefill.entries.length > 0
      && prefill.entries.every((entry) => (entry.discountType || "percent") === (prefill.entries[0]?.discountType || "percent"))
      ? (prefill.entries[0]?.discountType || "percent")
      : "percent",
    entries: Array.isArray(prefill.entries) && prefill.entries.length
      ? prefill.entries.map((entry) => ({
          ...buildEmptyItem(),
          ...entry,
          productId: entry.productId || "",
          unit: formatUnit(entry.unit),
          stockCategory: normalizeStockCategory(entry.stockCategory),
          location: entry.location || "",
          discountType: entry.discountType || "percent"
        }))
      : [buildEmptyItem()]
  };
};

const mapQuotationToSalesInvoicePrefill = (quotation = {}) => ({
  source: "quotation",
  entryType: "sale",
  customerId: quotation.customerId || "",
  counterpartyName: quotation.customerName || "",
  counterpartyPhone: quotation.phone || "",
  counterpartyEmail: quotation.email || "",
  counterpartyGst: quotation.gstNumber || "",
  counterpartyAddress: quotation.billingAddress || "",
  deliveryAddressType: "billing_address",
  otherDeliveryAddress: "",
  transactionDate: new Date().toISOString().slice(0, 10),
  payDate: "",
  reference: quotation.quotationNo || "",
  sourceQuotationId: quotation._id || "",
  sourceQuotationNo: quotation.quotationNo || "",
  sourceDeliveryChallanId: "",
  sourceDeliveryChallanNo: "",
  sourceDeliveryChallanType: "",
  skipInventoryDeduction: false,
  notes: quotation.notes || "",
  paymentTerms: quotation.paymentTerms || "",
  warranty: quotation.warranty || "",
  terms: quotation.terms || "",
  gstType: quotation.gstType || "cgst_sgst",
  sgst: String(quotation.sgst ?? 9),
  cgst: String(quotation.cgst ?? 9),
  igst: String(quotation.igst ?? getDefaultIntegratedGst(quotation.gstType)),
  entries: (quotation.items || []).map((item) => ({
    itemName: item.description || item.itemCode || "",
    productId: item.productId || "",
    itemCode: item.itemCode || "",
    hsnCode: item.hsnCode || "",
    description: item.description || "",
    quantity: String(item.quantity ?? ""),
    unit: formatUnit(item.unit),
    stockCategory: "sales",
    location: "",
    listPrice: String(item.listPrice ?? item.unitPrice ?? ""),
    discountType: item.discountType || "percent",
    discountValue: String(item.discountValue ?? ""),
    unitPrice: String(item.unitPrice ?? ""),
    totalAmount: String(item.totalAmount ?? "")
  }))
});

const mapSaleEntryToSalesInvoicePrefill = (entry = {}) => ({
  source: "performa",
  entryType: "sale",
  customerId: entry.customerId || "",
  counterpartyName: entry.counterpartyName || "",
  counterpartyPhone: entry.counterpartyPhone || "",
  counterpartyEmail: entry.counterpartyEmail || "",
  counterpartyGst: entry.counterpartyGst || "",
  counterpartyAddress: entry.counterpartyAddress || "",
  deliveryAddressType: entry.deliveryAddressType || "work_address",
  otherDeliveryAddress: entry.otherDeliveryAddress || "",
  transactionDate: new Date().toISOString().slice(0, 10),
  payDate: entry.payDate ? new Date(entry.payDate).toISOString().slice(0, 10) : "",
  reference: entry.reference || entry.documentNumber || "",
  referenceDate: entry.referenceDate ? new Date(entry.referenceDate).toISOString().slice(0, 10) : "",
  poNumber: entry.poNumber || "",
  poDate: entry.poDate ? new Date(entry.poDate).toISOString().slice(0, 10) : "",
  sourceQuotationId: entry.sourceQuotationId || "",
  sourceQuotationNo: entry.sourceQuotationNo || "",
  sourceDeliveryChallanId: entry.sourceDeliveryChallanId || "",
  sourceDeliveryChallanNo: entry.sourceDeliveryChallanNo || "",
  sourceDeliveryChallanType: entry.sourceDeliveryChallanType || "",
  skipInventoryDeduction: Boolean(entry.skipInventoryDeduction),
  notes: entry.notes || "",
  paymentTerms: entry.paymentTerms || "",
  warranty: entry.warranty || "",
  terms: entry.terms || "",
  paymentMode: entry.paymentMode || "",
  dispatchThrough: entry.dispatchThrough || "",
  deliveryNote: entry.deliveryNote || "",
  ewayBillNo: entry.ewayBillNo || "",
  otherReferences: entry.otherReferences || "",
  dispatchDocNo: entry.dispatchDocNo || "",
  deliveryNoteDate: entry.deliveryNoteDate ? new Date(entry.deliveryNoteDate).toISOString().slice(0, 10) : "",
  destination: entry.destination || "",
  billOfLading: entry.billOfLading || "",
  motorVehicleNo: entry.motorVehicleNo || "",
  deliveryAt: entry.deliveryAt || "",
  deliveryTime: entry.deliveryTime || "",
  gstType: entry.gstType || "cgst_sgst",
  sgst: String(entry.sgst ?? 9),
  cgst: String(entry.cgst ?? 9),
  igst: String(entry.igst ?? getDefaultIntegratedGst(entry.gstType)),
  roundOffEnabled: entry.roundOffEnabled || "no",
  roundOffAmount: entry.roundOffAmount || "",
  finalTotal: entry.finalTotal || "",
  advanceAmount: entry.advanceAmount || "",
  balanceAmount: entry.balanceAmount || "",
  entries: (entry.entries || []).map((item) => ({
    itemName: item.itemName || item.description || item.itemCode || "",
    productId: item.productId || "",
    itemCode: item.itemCode || "",
    hsnCode: item.hsnCode || "",
    description: item.description || item.itemName || "",
    quantity: String(item.quantity ?? ""),
    unit: formatUnit(item.unit),
    stockCategory: item.stockCategory || "sales",
    location: item.location || "",
    listPrice: String(item.listPrice ?? item.unitPrice ?? ""),
    discountType: item.discountType || "percent",
    discountValue: String(item.discountValue ?? ""),
    unitPrice: String(item.unitPrice ?? ""),
    totalAmount: String(item.totalAmount ?? "")
  }))
});

const mapPurchaseOrderToPurchaseInvoicePrefill = (purchaseOrder = {}) => ({
  source: "purchase-order",
  entryType: "purchase",
  dealerId: purchaseOrder.dealerId || "",
  counterpartyName: purchaseOrder.counterpartyName || "",
  counterpartyPhone: purchaseOrder.counterpartyPhone || "",
  counterpartyEmail: purchaseOrder.counterpartyEmail || "",
  counterpartyGst: purchaseOrder.counterpartyGst || purchaseOrder.supplierGst || "",
  supplierGst: purchaseOrder.supplierGst || purchaseOrder.counterpartyGst || "",
  counterpartyAddress: purchaseOrder.counterpartyAddress || "",
  transactionDate: new Date().toISOString().slice(0, 10),
  payDate: purchaseOrder.payDate ? new Date(purchaseOrder.payDate).toISOString().slice(0, 10) : "",
  reference: purchaseOrder.poNumber || purchaseOrder.documentNumber || "",
  referenceDate: purchaseOrder.transactionDate ? new Date(purchaseOrder.transactionDate).toISOString().slice(0, 10) : "",
  poNumber: purchaseOrder.poNumber || purchaseOrder.documentNumber || "",
  poDate: purchaseOrder.poDate ? new Date(purchaseOrder.poDate).toISOString().slice(0, 10) : (purchaseOrder.transactionDate ? new Date(purchaseOrder.transactionDate).toISOString().slice(0, 10) : ""),
  sourceDeliveryChallanId: "",
  sourceDeliveryChallanNo: "",
  sourceDeliveryChallanType: "",
  skipInventoryDeduction: false,
  paymentMode: purchaseOrder.paymentMode || "",
  paymentTerms: purchaseOrder.paymentTerms || purchaseOrder.payment || "",
  deliveryTime: purchaseOrder.deliveryTime || "",
  terms: purchaseOrder.terms || "",
  notes: purchaseOrder.notes || "",
  gstType: purchaseOrder.gstType || "cgst_sgst",
  sgst: String(purchaseOrder.sgst ?? 9),
  cgst: String(purchaseOrder.cgst ?? 9),
  igst: String(purchaseOrder.igst ?? getDefaultIntegratedGst(purchaseOrder.gstType)),
  entries: (purchaseOrder.entries || []).map((item) => ({
    itemName: item.itemName || item.description || item.itemCode || "",
    productId: item.productId || "",
    itemCode: item.itemCode || "",
    hsnCode: item.hsnCode || "",
    description: item.description || item.itemName || "",
    quantity: String(item.quantity ?? ""),
    unit: formatUnit(item.unit),
    stockCategory: item.stockCategory || "sales",
    location: item.location || "",
    listPrice: String(item.listPrice ?? item.unitPrice ?? ""),
    discountType: item.discountType || "percent",
    discountValue: String(item.discountValue ?? ""),
    unitPrice: String(item.unitPrice ?? ""),
    totalAmount: String(item.totalAmount ?? "")
  }))
});

const unitOptions = [
  ["-", "-"],
  ["Pkt", "Pkt"],
  ["No", "No"],
  ["Set", "Set"],
  ["Meter", "Meter"],
  ["Kg", "Kg"],
  ["Litre", "Litre"],
  ["Length", "Length"],
  ["Job", "Job"],
  ["Days", "Days"],
  ["Month", "Month"]
];

const formatUnit = (value = "") => {
  const unit = String(value || "").trim();
  if (!unit || unit.toLowerCase() === "pcs" || unit.toLowerCase() === "unit") return "No";
  return unit.charAt(0).toUpperCase() + unit.slice(1).toLowerCase();
};

const paymentModeOptions = [
  ["", "Select account"],
  ["od_account", "OD Account"],
  ["ca_account", "Current Account"],
  ["cash", "Cash"],
  ["other", "Other"]
];

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatPaymentMode = (value = "") => {
  if (value === "od_account") return "OD Account";
  if (value === "ca_account" || value === "current_account") return "Current Account";
  if (value === "cash") return "Cash";
  if (value === "other") return "Other";
  return value || "-";
};

const normalizeStockCategory = (value = "") => String(value || "").trim().toLowerCase() === "consumable" ? "consumable" : "sales";
const formatStockCategory = (value = "") => normalizeStockCategory(value) === "consumable" ? "Consumable" : "For Sales";

const entryLabelMap = {
  purchase: "Purchase",
  sale: "Sale",
  order: "Order"
};

const normalizeGstType = (value = "") => String(value || "").trim().toLowerCase();
const isIntegratedGstType = (value = "") => ["igst", "sez_supply"].includes(normalizeGstType(value));
const getDefaultIntegratedGst = (gstType = "") => normalizeGstType(gstType) === "sez_supply" ? "0" : "18";

const entryPageConfig = {
  purchase: {
    overline: "Purchase Invoice",
    title: "Purchase Invoice",
    description: "Add supplier invoice details, product rows, GST, payment terms, billing status, and payable balance in one document.",
    addLabel: "Add Purchase Invoice",
    formTitle: "Purchase Invoice Details & Products",
    formDescription: "Save one supplier invoice with commercial terms, GST, payment details, and product rows together.",
    historyTitle: "Purchase Invoice History",
    historyDescription: "Purchase invoice register with supplier details, invoice numbers, totals, and line items.",
    documentHeader: "Invoice",
    partyHeader: "Supplier",
    accent: "sky"
  },
  sale: {
    overline: "Sales Invoice",
    title: "Sales Invoice",
    description: "Create customer sales invoices with product rows, GST, payment terms, dispatch details, and receivable balance.",
    addLabel: "Add Sales Invoice",
    formTitle: "Sales Invoice Details & Products",
    formDescription: "Save one customer invoice with commercial terms, GST, payment details, and product rows together.",
    historyTitle: "Sales Invoice History",
    historyDescription: "Sales invoice register with customer details, invoice numbers, totals, and line items.",
    documentHeader: "Invoice",
    partyHeader: "Customer",
    accent: "rose"
  },
  salePerforma: {
    overline: "Proforma Invoice",
    title: "Proforma Invoice",
    description: "Create customer proforma invoices with the exact same sales invoice layout, product rows, GST, payment terms, dispatch details, and receivable balance.",
    addLabel: "Add Proforma Invoice",
    formTitle: "Proforma Invoice Details & Products",
    formDescription: "Save one customer proforma invoice with commercial terms, GST, payment details, and product rows together.",
    historyTitle: "Proforma Invoice History",
    historyDescription: "Proforma invoice register with customer details, invoice numbers, totals, and line items.",
    documentHeader: "Invoice",
    partyHeader: "Customer",
    accent: "fuchsia"
  },
  order: {
    overline: "Purchase Order",
    title: "Purchase Order",
    description: "Create supplier purchase orders with dispatch information, delivery address, GST, payment terms, and product rows.",
    addLabel: "Add Purchase Order",
    formTitle: "Purchase Order Details & Products",
    formDescription: "Save one supplier purchase order with dispatch, address, commercial terms, and product details together.",
    historyTitle: "Purchase Order History",
    historyDescription: "Purchase order register with supplier details, PO numbers, totals, and line items.",
    documentHeader: "PO No",
    partyHeader: "Supplier",
    accent: "amber"
  }
};

const formatAddressObject = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address;

  return [
    address.street,
    address.area,
    address.city,
    address.state,
    address.pincode
  ].filter(Boolean).join(", ");
};

const formatCounterpartyAddress = (party) => {
  return formatAddressObject(party?.address);
};

const resolveCustomerAddressByType = (customer, addressType, otherAddress = "") => {
  if (addressType === "billing_address") {
    return formatAddressObject(customer?.billingAddress || customer?.address);
  }
  if (addressType === "other") return otherAddress;
  return formatAddressObject(customer?.address || customer?.billingAddress);
};

const resolveSupplierAddressByType = (supplier, addressType, otherAddress = "") => {
  if (addressType === "genmech_service") return GENMECH_DELIVERY_ADDRESS;
  if (addressType === "other") return otherAddress;
  return formatCounterpartyAddress(supplier);
};

const recalculateDiscountedEntry = (entry) => {
  const quantity = Number(entry.quantity) || 0;
  const listPrice = Number(entry.listPrice) || 0;
  const discountValue = Number(entry.discountValue) || 0;
  const discountAmount = entry.discountType === "amount"
    ? discountValue
    : (listPrice * Math.min(discountValue, 100)) / 100;
  
  // If listPrice is provided, calculate unitPrice based on list price and discount.
  // Otherwise, preserve/fallback to any manually entered unitPrice.
  const unitPrice = listPrice > 0 ? Math.max(listPrice - discountAmount, 0) : Number(entry.unitPrice) || 0;
  const totalAmount = quantity > 0 && unitPrice > 0 ? quantity * unitPrice : 0;

  return {
    ...entry,
    unitPrice: listPrice > 0 || discountValue > 0 || unitPrice > 0 ? unitPrice.toFixed(2) : "",
    totalAmount: totalAmount ? totalAmount.toFixed(2) : ""
  };
};

const normalizeMaterialValue = (value) => String(value || "").trim().toLowerCase();
const getMaterialKey = (value) => normalizeMaterialValue(value);
const normalizeHistoryToken = (value) => String(value || "").trim().toLowerCase();
const toMinuteKey = (value) => {
  const parsed = new Date(value || 0);
  if (Number.isNaN(parsed.getTime())) return "";

  parsed.setSeconds(0, 0);
  return parsed.toISOString();
};
const getInventoryMaterialKey = (item = {}) => [
  getMaterialKey(item.productName || item.itemName || item.itemCode || item.productId),
  normalizeStockCategory(item.stockCategory)
].join("::");
const getEntryHistoryGroupKey = (entry) => {
  const documentNumber = String(entry.documentNumber || "").trim();
  if (documentNumber) {
    return `${entry.entryType || "entry"}::doc::${documentNumber.toLowerCase()}`;
  }

  const poNumber = String(entry.poNumber || "").trim();
  const counterpartyName = String(entry.counterpartyName || "").trim().toLowerCase();
  if (poNumber) {
    return `${entry.entryType || "entry"}::po::${poNumber.toLowerCase()}::${counterpartyName}`;
  }

  return [
    entry.entryType || "entry",
    entry.orderType || "",
    normalizeHistoryToken(entry.counterpartyName),
    normalizeHistoryToken(entry.reference),
    normalizeHistoryToken(entry.paymentMode),
    normalizeHistoryToken(entry.deliveryAt),
    normalizeHistoryToken(entry.billingStatus),
    normalizeHistoryToken(entry.paymentStatus),
    String(entry.transactionDate || "").slice(0, 10),
    toMinuteKey(entry.createdAt || entry.updatedAt || entry.transactionDate)
  ].join("::");
};


export default function MaterialStock({
  forcedEntryType = "",
  salesDocumentVariant = "",
  showPerformaPdfButton = false
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const role = localStorage.getItem("role") || "";
  const handleCounterpartyClick = (entry) => {
    if (entry.customerId) {
      navigate(role === "accounts" ? `/accounts/customer/${entry.customerId}` : `/customer/${entry.customerId}`);
    } else if (entry.dealerId) {
      navigate(role === "accounts" ? `/accounts/dealers/${entry.dealerId}` : `/admin/dealers/${entry.dealerId}`);
    }
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const lastAppliedPrefillKeyRef = useRef("");
  const normalizedForcedEntryType = String(forcedEntryType || "").trim().toLowerCase();
  const entryTypeFilter = normalizedForcedEntryType || String(searchParams.get("entryType") || "").trim().toLowerCase();
  const isDedicatedEntryPage = ["purchase", "sale", "order"].includes(normalizedForcedEntryType);
  const isEntryMode = ["purchase", "sale", "order"].includes(entryTypeFilter);
  const initialPrefill = location.state?.accountDocumentPrefill;
  const initialPrefillEntryType = String(initialPrefill?.entryType || "").trim().toLowerCase();
  const [filters, setFilters] = useState({
    entryType: "",
    transactionDateFrom: "",
    transactionDateTo: "",
    availability: "",
    stockCategory: ""
  });
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(() => {
    if (isEntryMode && initialPrefill && initialPrefillEntryType === entryTypeFilter) {
      return buildPrefilledDocumentForm(initialPrefill, entryTypeFilter, []);
    }
    return isEntryMode ? buildEmptyFormForEntryType(entryTypeFilter) : buildEmptyForm();
  });
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [entries, setEntries] = useState([]);
  const [productCatalog, setProductCatalog] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [summary, setSummary] = useState({
    totals: {
      totalIn: 0,
      totalOut: 0,
      transactionCount: 0,
      netStock: 0,
      uniqueItems: 0,
      lowStockItems: 0
    },
    stockByItem: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedMaterialKey, setSelectedMaterialKey] = useState("");
  const [historyTab, setHistoryTab] = useState("invoices");
  const [invoicesLimit, setInvoicesLimit] = useState(20);
  const [productsLimit, setProductsLimit] = useState(20);
  const [editingEntryId, setEditingEntryId] = useState("");
  const [showEntryForm, setShowEntryForm] = useState(Boolean(isEntryMode && initialPrefill && initialPrefillEntryType === entryTypeFilter));

  const supplierOptions = useMemo(() => {
    return suppliers.map((s) => ({
      value: s._id,
      label: s.companyName || s.name || s.supplierCode || "",
      data: s
    }));
  }, [suppliers]);

  const customerOptions = useMemo(() => {
    return customers.map((c) => ({
      value: c._id,
      label: c.name || c.customerId || "",
      data: c
    }));
  }, [customers]);

  const selectedSupplierOption = useMemo(() => {
    if (!form.dealerId) {
      const matched = suppliers.find(
        (s) => (s.companyName || s.name || s.supplierCode || "") === form.counterpartyName
      );
      if (matched) return { value: matched._id, label: matched.companyName || matched.name || matched.supplierCode || "", data: matched };
      return form.counterpartyName ? { value: "", label: form.counterpartyName } : null;
    }
    const matched = suppliers.find((s) => s._id === form.dealerId);
    if (matched) {
      return { value: matched._id, label: matched.companyName || matched.name || matched.supplierCode || "", data: matched };
    }
    return form.counterpartyName ? { value: form.dealerId, label: form.counterpartyName } : null;
  }, [suppliers, form.dealerId, form.counterpartyName]);

  const selectedCustomerOption = useMemo(() => {
    if (!form.customerId) {
      const matched = customers.find(
        (c) => (c.name || c.customerId || "") === form.counterpartyName
      );
      if (matched) return { value: matched._id, label: matched.name || matched.customerId || "", data: matched };
      return form.counterpartyName ? { value: "", label: form.counterpartyName } : null;
    }
    const matched = customers.find((c) => c._id === form.customerId);
    if (matched) {
      return { value: matched._id, label: matched.name || matched.customerId || "", data: matched };
    }
    return form.counterpartyName ? { value: form.customerId, label: form.counterpartyName } : null;
  }, [customers, form.customerId, form.counterpartyName]);

  const reactSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "36px",
      borderRadius: "0.25rem",
      borderColor: state.isFocused ? "#357edd" : "#cbd5e1",
      boxShadow: state.isFocused ? "0 0 0 1px #357edd" : "none",
      fontSize: "0.875rem",
      backgroundColor: "#ffffff",
      "&:hover": {
        borderColor: "#357edd"
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8"
    }),
    menu: (base) => ({
      ...base,
      zIndex: 60,
      borderRadius: "0.375rem",
      overflow: "hidden",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 60
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#dbeafe"
        : state.isFocused
          ? "#eff6ff"
          : "#ffffff",
      color: "#0f172a",
      fontSize: "0.875rem"
    }),
    indicatorSeparator: () => ({
      display: "none"
    })
  };
  const [selectedHistoryEntry, setSelectedHistoryEntry] = useState(null);
  const [classificationItem, setClassificationItem] = useState(null);
  const [classificationDraft, setClassificationDraft] = useState({ stockCategory: "sales", location: "" });
  const [classificationSaving, setClassificationSaving] = useState(false);
  const [selectedQuotationId, setSelectedQuotationId] = useState("");
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState("");

  // Redesign - Inventory Dashboard states
  const [storageLocations, setStorageLocations] = useState([]);
  const [storageAreas, setStorageAreas] = useState([]);
  const [areaLoading, setAreaLoading] = useState(false);
  const [selectedAreaFilter, setSelectedAreaFilter] = useState("");
  const [rackSearchQuery, setRackSearchQuery] = useState("");
  const [isAddingArea, setIsAddingArea] = useState(false);
  const [newAreaName, setNewAreaName] = useState("");
  const [isAddingBatchRack, setIsAddingBatchRack] = useState(false);
  const [batchRackAreaId, setBatchRackAreaId] = useState("");
  const [batchRackStartNum, setBatchRackStartNum] = useState(1);
  const [batchRackEndNum, setBatchRackEndNum] = useState(10);
  const [locationLoading, setLocationLoading] = useState(false);
  const [selectedRack, setSelectedRack] = useState("");
  const [localSearch, setLocalSearch] = useState("");
  const [localStockCategory, setLocalStockCategory] = useState("");
  const [localVoltage, setLocalVoltage] = useState("");
  const [localRack, setLocalRack] = useState("");
  const [localStatus, setLocalStatus] = useState("");
  const [localDateFrom, setLocalDateFrom] = useState("");
  const [localDateTo, setLocalDateTo] = useState("");
  const [localSpecFilters, setLocalSpecFilters] = useState({});

  const [sortColumn, setSortColumn] = useState(""); // "status" | ""
  const [sortDirection, setSortDirection] = useState(""); // "asc" | "desc" | ""
  const [historySortField, setHistorySortField] = useState("date");
  const [historySortOrder, setHistorySortOrder] = useState("desc");

  const [localCategory, setLocalCategory] = useState("");
  const [localManufacturer, setLocalManufacturer] = useState("");
  const [dashboardStats, setDashboardStats] = useState({
    totalMaterials: 0,
    totalStockQuantity: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    todayStockIn: 0,
    todayStockOut: 0,
    recentlyAdded: []
  });

  const [activeFilters, setActiveFilters] = useState({
    search: "",
    stockCategory: "",
    voltage: "",
    rack: "",
    status: "",
    dateFrom: "",
    dateTo: "",
    category: "",
    manufacturer: ""
  });
  const [activeSpecFilters, setActiveSpecFilters] = useState({});

  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [materialFormMode, setMaterialFormMode] = useState("add"); // "add" | "edit" | "view"
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [materialFormData, setMaterialFormData] = useState({
    productName: "",
    partNumber: "",
    modelNumber: "",
    voltage: "",
    stockCategory: "sales",
    brand: "",
    manufacturer: "",
    category: "",
    subCategory: "",
    rackNumber: "",
    shelfBin: "",
    unit: "No",
    openingStock: "",
    currentStock: "",
    minimumStock: "",
    hsnCode: "",
    purchasePrice: "",
    sellingPrice: "",
    supplier: "",
    purchaseDate: "",
    remarks: "",
    specifications: [],
    imageUrl: "",
    attachments: [],
    imageFile: null,
    datasheetFile: null
  });

  const [showMovementModal, setShowMovementModal] = useState(false);
  const [movementType, setMovementType] = useState("IN"); // "IN" | "OUT"
  const [movementFormData, setMovementFormData] = useState({
    productId: "",
    quantity: "",
    transactionDate: new Date().toISOString().slice(0, 10),
    reference: "",
    remarks: ""
  });

  const [showImportModal, setShowImportModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [importText, setImportText] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [importLoading, setImportLoading] = useState(false);

  const effectiveEntryType = isEntryMode ? entryTypeFilter : filters.entryType;
  const documentApiBase = isEntryMode ? "/api/account-documents" : "/api/material-stock";
  const scopedEntries = useMemo(() => {
    if (!isEntryMode || !entryTypeFilter) return entries;

    return entries.filter((entry) =>
      String(entry.entryType || "").trim().toLowerCase() === entryTypeFilter
    );
  }, [entries, entryTypeFilter, isEntryMode]);

  const loadStorageAreas = useCallback(async () => {
    try {
      setAreaLoading(true);
      const res = await axios.get(API_BASE_URL + "/api/material-stock/storage-areas");
      setStorageAreas(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load storage areas", err);
      setStorageAreas([]);
    } finally {
      setAreaLoading(false);
    }
  }, []);

  const loadStorageLocations = useCallback(async () => {
    try {
      setLocationLoading(true);
      const res = await axios.get(API_BASE_URL + "/api/material-stock/storage-locations");
      setStorageLocations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load storage locations", err);
      setStorageLocations([]);
    } finally {
      setLocationLoading(false);
    }
  }, []);

  const handleAddArea = async () => {
    if (!newAreaName || !newAreaName.trim()) return;
    try {
      await axios.post(API_BASE_URL + "/api/material-stock/storage-areas", {
        name: newAreaName.trim().toUpperCase()
      });
      setNewAreaName("");
      setIsAddingArea(false);
      await loadStorageAreas();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add storage area");
    }
  };

  const handleDeleteArea = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete Area "${name}"? This will delete all its racks and unlink associated inventory items.`)) return;
    try {
      await axios.delete(API_BASE_URL + `/api/material-stock/storage-areas/${id}`);
      await loadStorageAreas();
      await loadStorageLocations();
      setSelectedRack("");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete storage area");
    }
  };

  const handleBatchAddLocations = async () => {
    if (!batchRackAreaId) {
       alert("Please select a Storage Area");
       return;
    }
    try {
      const res = await axios.post(API_BASE_URL + "/api/material-stock/storage-locations/batch", {
        areaId: batchRackAreaId,
        startNum: batchRackStartNum,
        endNum: batchRackEndNum
      });
      setStorageLocations(Array.isArray(res.data) ? res.data : []);
      setIsAddingBatchRack(false);
      setBatchRackAreaId("");
      setBatchRackStartNum(1);
      setBatchRackEndNum(10);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to generate racks");
    }
  };

  const handleDeleteLocation = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the rack "${name}"? Associated items will be unlinked.`)) return;
    try {
      await axios.delete(API_BASE_URL + `/api/material-stock/storage-locations/${id}`);
      if (selectedRack === name) {
        setSelectedRack("");
      }
      await loadStorageLocations();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete storage location");
    }
  };

  const loadDashboardStats = useCallback(async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/api/material-stock/dashboard-stats");
      setDashboardStats(res.data);
    } catch (error) {
      console.error("Failed to load dashboard stats:", error);
    }
  }, []);

  const loadData = useCallback(async (query = "") => {
    try {
      setLoading(true);
      const params = {};

      if (query.trim()) {
        params.search = query.trim();
      }

      if (effectiveEntryType) {
        params.entryType = effectiveEntryType;
      }

      if (filters.transactionDateFrom) {
        params.transactionDateFrom = filters.transactionDateFrom;
      }

      if (filters.transactionDateTo) {
        params.transactionDateTo = filters.transactionDateTo;
      }

      if (filters.stockCategory) {
        params.stockCategory = filters.stockCategory;
      }

      const entriesRes = await axios.get(API_BASE_URL + documentApiBase, {
        params: Object.keys(params).length ? params : undefined
      });
      const loadedEntries = Array.isArray(entriesRes.data) ? entriesRes.data : [];
      setEntries(
        isEntryMode && entryTypeFilter
          ? loadedEntries.filter((entry) => String(entry.entryType || "").trim().toLowerCase() === entryTypeFilter)
          : loadedEntries
      );
      setInvoicesLimit(20);
      setProductsLimit(20);

      if (!isEntryMode) {
        loadDashboardStats();
        const inventoryRes = await axios.get(API_BASE_URL + "/api/material-stock/inventory", {
          params: query.trim() ? { search: query.trim() } : undefined
        });
        setInventoryItems(Array.isArray(inventoryRes.data) ? inventoryRes.data : []);

        const summaryRes = await axios.get(API_BASE_URL + "/api/material-stock/summary", {
          params: Object.keys(params).length ? params : undefined
        });
        setSummary(summaryRes.data || {
          totals: {
            totalIn: 0,
            totalOut: 0,
            transactionCount: 0,
            netStock: 0,
            uniqueItems: 0,
            lowStockItems: 0
          },
          stockByItem: []
        });
      }
    } catch (error) {
      console.error(isEntryMode ? "Failed to load account documents" : "Failed to load material stock", error);
      setEntries([]);
      setInventoryItems([]);
    } finally {
      setLoading(false);
    }
  }, [documentApiBase, effectiveEntryType, entryTypeFilter, filters.stockCategory, filters.transactionDateFrom, filters.transactionDateTo, isEntryMode, loadDashboardStats]);

  useEffect(() => {
    loadData();
    loadStorageLocations();
    loadStorageAreas();
  }, [loadData, loadStorageLocations, loadStorageAreas]);

  useEffect(() => {
    const loadReferences = async () => {
      try {
        const [suppliersRes, customersRes, productCatalogRes, quotationsRes, purchaseOrdersRes] = await Promise.all([
          axios.get(API_BASE_URL + "/api/dealers").catch(err => { console.error("Failed to load dealers", err); return { data: [] }; }),
          axios.get(API_BASE_URL + "/api/customers").catch(err => { console.error("Failed to load customers", err); return { data: [] }; }),
          axios.get(API_BASE_URL + "/api/products", { params: { limit: 200 } }).catch(err => { console.error("Failed to load products", err); return { data: { products: [] } }; }),
          axios.get(API_BASE_URL + "/api/quotations").catch(err => { console.error("Failed to load quotations", err); return { data: [] }; }),
          axios.get(API_BASE_URL + "/api/account-documents", { params: { entryType: "order" } }).catch(err => { console.error("Failed to load account documents", err); return { data: [] }; })
        ]);

        const getProductsArray = (resData) => {
          if (!resData) return [];
          if (Array.isArray(resData)) return resData;
          if (Array.isArray(resData.products)) return resData.products;
          return [];
        };

        setSuppliers(Array.isArray(suppliersRes.data) ? suppliersRes.data : []);
        setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
        setProductCatalog(getProductsArray(productCatalogRes.data));
        setQuotations(Array.isArray(quotationsRes.data) ? quotationsRes.data : []);
        setPurchaseOrders(Array.isArray(purchaseOrdersRes.data) ? purchaseOrdersRes.data : []);
      } catch (error) {
        console.error("Reference data load failed", error);
        setSuppliers([]);
        setCustomers([]);
        setProductCatalog([]);
        setQuotations([]);
        setPurchaseOrders([]);
      }
    };

    loadReferences();
  }, []);

  useEffect(() => {
    if (!isEntryMode) return;

    setForm((current) => {
      if (current.entryType === entryTypeFilter) {
        return current;
      }

      return {
        ...current,
        entryType: entryTypeFilter
      };
    });
  }, [entryTypeFilter, isEntryMode]);

  useEffect(() => {
    if (!isEntryMode) {
      setShowEntryForm(false);
      return;
    }

    const prefill = location.state?.accountDocumentPrefill;
    const prefillEntryType = String(prefill?.entryType || "").trim().toLowerCase();
    if (prefill && prefillEntryType === entryTypeFilter) {
      setShowEntryForm(true);
      if (prefill.sourceQuotationId) {
        setSelectedQuotationId(prefill.sourceQuotationId);
      }
      if (prefill.sourcePurchaseOrderId) {
        setSelectedPurchaseOrderId(prefill.sourcePurchaseOrderId);
      }
      return;
    }

    if (!prefill && lastAppliedPrefillKeyRef.current) {
      lastAppliedPrefillKeyRef.current = "";
      return;
    }

    setForm(buildEmptyFormForEntryType(entryTypeFilter));
    setShowEntryForm(false);
    setEditingEntryId("");
    setSelectedHistoryEntry(null);
    setSelectedQuotationId("");
    setSelectedPurchaseOrderId("");
  }, [entryTypeFilter, isEntryMode, location.state]);

  useEffect(() => {
    if (isEntryMode) {
      setFilters((current) => ({
        ...current,
        entryType: entryTypeFilter
      }));
      return;
    }

    setFilters((current) => ({
      ...current,
      entryType: ""
    }));
  }, [entryTypeFilter, isEntryMode]);

  useEffect(() => {
    const prefill = location.state?.accountDocumentPrefill;
    const prefillKey = location.state?.prefillKey || "";

    if (!isEntryMode || !prefill || lastAppliedPrefillKeyRef.current === prefillKey) {
      return;
    }

    const prefillEntryType = String(prefill.entryType || entryTypeFilter || "").trim().toLowerCase();
    if (prefillEntryType !== entryTypeFilter) {
      return;
    }

    lastAppliedPrefillKeyRef.current = prefillKey;
    setEditingEntryId("");
    setSelectedHistoryEntry(null);
    setShowEntryForm(true);
    if (prefill.sourceQuotationId) {
      setSelectedQuotationId(prefill.sourceQuotationId);
    }
    if (prefill.sourcePurchaseOrderId) {
      setSelectedPurchaseOrderId(prefill.sourcePurchaseOrderId);
    }
    setForm(buildPrefilledDocumentForm(prefill, entryTypeFilter, customers));
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(location.pathname, { replace: true, state: {} });
  }, [customers, entryTypeFilter, isEntryMode, location.pathname, location.state, navigate]);

  useEffect(() => {
    const entryToEdit = location.state?.accountDocumentEditEntry;
    const prefillKey = location.state?.prefillKey || "";

    if (!isEntryMode || !entryToEdit || lastAppliedPrefillKeyRef.current === prefillKey) {
      return;
    }

    const entryType = String(entryToEdit.entryType || "").trim().toLowerCase();
    if (entryType !== entryTypeFilter) {
      return;
    }

    const hasLineItems = Array.isArray(entryToEdit.lineItems) && entryToEdit.lineItems.length > 0;
    if (!hasLineItems && scopedEntries.length === 0) {
      // Still load the single entry as a fallback so the form isn't empty, but don't mark as permanently applied yet
    } else {
      lastAppliedPrefillKeyRef.current = prefillKey;
    }

    const activeEntries = hasLineItems
      ? entryToEdit.lineItems
      : (scopedEntries.length > 0
          ? scopedEntries.filter((item) => getEntryHistoryGroupKey(item) === getEntryHistoryGroupKey(entryToEdit))
              .sort((a, b) => new Date(a.createdAt || a.transactionDate || 0) - new Date(b.createdAt || b.transactionDate || 0))
          : [entryToEdit]);

    const isOrderEntry = entryType === "order";
    const deliveryAddressType = isOrderEntry
      ? (entryToEdit.deliveryAddressType === "other" ? "other" : "genmech_service")
      : entryType === "sale"
      ? entryToEdit.deliveryAddressType || "work_address"
      : entryToEdit.deliveryAddressType || "billing_address";

    setEditingEntryId(entryToEdit._id || "");
    setSelectedHistoryEntry(null);
    setShowEntryForm(true);
    setForm({
      entryType,
      orderType: entryToEdit.orderType || "supplier_order",
      documentNumber: isOrderEntry ? "" : entryToEdit.documentNumber || "",
      poNumber: entryToEdit.poNumber || "",
      poDate: entryToEdit.poDate ? new Date(entryToEdit.poDate).toISOString().slice(0, 10) : "",
      reference: entryToEdit.reference || "",
      referenceDate: entryToEdit.referenceDate ? new Date(entryToEdit.referenceDate).toISOString().slice(0, 10) : "",
      transactionDate: entryToEdit.transactionDate ? new Date(entryToEdit.transactionDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      payDate: entryToEdit.payDate ? new Date(entryToEdit.payDate).toISOString().slice(0, 10) : "",
      dealerId: entryToEdit.dealerId || "",
      customerId: entryToEdit.customerId || "",
      counterpartyName: entryToEdit.counterpartyName || "",
      counterpartyPhone: entryToEdit.counterpartyPhone || "",
      counterpartyEmail: entryToEdit.counterpartyEmail || "",
      counterpartyGst: entryToEdit.counterpartyGst || "",
      supplierGst: entryToEdit.supplierGst || entryToEdit.counterpartyGst || "",
      endUserGst: entryToEdit.endUserGst || "",
      counterpartyAddress: isOrderEntry
        ? (deliveryAddressType === "other" ? entryToEdit.otherDeliveryAddress || entryToEdit.counterpartyAddress || "" : GENMECH_DELIVERY_ADDRESS)
        : entryToEdit.counterpartyAddress || "",
      deliveryAddressType,
      otherDeliveryAddress: entryToEdit.otherDeliveryAddress || (deliveryAddressType === "other" ? entryToEdit.counterpartyAddress || "" : ""),
      sourceQuotationId: entryToEdit.sourceQuotationId || "",
      sourceQuotationNo: entryToEdit.sourceQuotationNo || "",
      sourceDeliveryChallanId: entryToEdit.sourceDeliveryChallanId || "",
      sourceDeliveryChallanNo: entryToEdit.sourceDeliveryChallanNo || "",
      sourceDeliveryChallanType: entryToEdit.sourceDeliveryChallanType || "",
      skipInventoryDeduction: Boolean(entryToEdit.skipInventoryDeduction),
      location: entryToEdit.location || "",
      payment: entryToEdit.payment || "",
      paymentMode: entryToEdit.paymentMode || "",
      roundOffEnabled: isOrderEntry ? "no" : entryToEdit.roundOffEnabled ? "yes" : "no",
      roundOffAmount: String(entryToEdit.roundOffAmount ?? ""),
      finalTotal: String(entryToEdit.finalTotal ?? ""),
      freightType: entryToEdit.freightType || "paid",
      dispatchMode: entryToEdit.dispatchMode || "by_road",
      expectedDeliveryDate: entryToEdit.expectedDeliveryDate ? new Date(entryToEdit.expectedDeliveryDate).toISOString().slice(0, 10) : "",
      deliveryAt: entryToEdit.deliveryAt || "",
      deliveryNote: entryToEdit.deliveryNote || "",
      dispatchThrough: entryToEdit.dispatchThrough || "",
      ewayBillNo: entryToEdit.ewayBillNo || "",
      otherReferences: entryToEdit.otherReferences || "",
      dispatchDocNo: entryToEdit.dispatchDocNo || "",
      deliveryNoteDate: entryToEdit.deliveryNoteDate ? new Date(entryToEdit.deliveryNoteDate).toISOString().slice(0, 10) : "",
      destination: entryToEdit.destination || "",
      billOfLading: entryToEdit.billOfLading || "",
      motorVehicleNo: entryToEdit.motorVehicleNo || "",
      deliveryTime: entryToEdit.deliveryTime || "Within 2-3 weeks after confirmed purchase order",
      paymentTerms: entryToEdit.paymentTerms || "50% advance along with PO, balance before dispatch",
      warranty: entryToEdit.warranty || "12 months against manufacturing defects",
      terms: entryToEdit.terms || "Prices are valid for the agreed document date. Taxes are extra as applicable. Freight and installation are extra unless mentioned.",
      showDiscountInPdf: Boolean(entryToEdit.showDiscountInPdf),
      commonDiscountType: entryToEdit.discountType || "percent",
      billingStatus: entryToEdit.billingStatus || "not_billed",
      paymentStatus: entryToEdit.paymentStatus || "not_done",
      gstType: entryToEdit.gstType || "cgst_sgst",
      sgst: String(entryToEdit.sgst ?? 9),
      cgst: String(entryToEdit.cgst ?? 9),
      igst: String(entryToEdit.igst ?? getDefaultIntegratedGst(entryToEdit.gstType)),
      gst: String(entryToEdit.gst ?? 18),
      advanceAmount: String(entryToEdit.advanceAmount ?? ""),
      balanceAmount: String(entryToEdit.balanceAmount ?? ""),
      notes: entryToEdit.notes || "",
      entries: activeEntries.map((item) => ({
        productId: item.productId || "",
        itemName: item.itemName || "",
        itemCode: item.itemCode || "",
        hsnCode: item.hsnCode || "",
        description: item.description || "",
        quantity: String(item.quantity ?? ""),
        unit: formatUnit(item.unit),
        stockCategory: normalizeStockCategory(item.stockCategory),
        location: item.location || "",
        listPrice: String(item.listPrice ?? item.unitPrice ?? ""),
        discountType: item.discountType || "percent",
        discountValue: String(item.discountValue ?? ""),
        unitPrice: String(item.unitPrice ?? ""),
        totalAmount: String(item.totalAmount ?? "")
      }))
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(location.pathname, { replace: true, state: {} });
  }, [entryTypeFilter, isEntryMode, location.pathname, location.state, navigate, scopedEntries]);

  const applyQuotationToSalesInvoice = (quotation) => {
    const prefill = mapQuotationToSalesInvoicePrefill(quotation);
    setEditingEntryId("");
    setSelectedHistoryEntry(null);
    setSelectedQuotationId(quotation?._id || "");
    setShowEntryForm(true);
    setForm(buildPrefilledDocumentForm(prefill, "sale", customers));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleObtainQuotationDetails = () => {
    const quotation = quotations.find((entry) => String(entry._id || "") === String(selectedQuotationId || ""));
    if (!quotation) {
      alert("Select a quotation first");
      return;
    }

    applyQuotationToSalesInvoice(quotation);
  };

  const applyPurchaseOrderToPurchaseInvoice = (purchaseOrder) => {
    const prefill = mapPurchaseOrderToPurchaseInvoicePrefill(purchaseOrder);
    setEditingEntryId("");
    setSelectedHistoryEntry(null);
    setSelectedPurchaseOrderId(purchaseOrder?._id || "");
    setShowEntryForm(true);
    setForm(buildPrefilledDocumentForm(prefill, "purchase", customers));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleObtainPurchaseOrderDetails = () => {
    const purchaseOrder = purchaseOrders.find((entry) => String(entry._id || "") === String(selectedPurchaseOrderId || ""));
    if (!purchaseOrder) {
      alert("Select a purchase order first");
      return;
    }

    applyPurchaseOrderToPurchaseInvoice(purchaseOrder);
  };

  const openPurchaseInvoiceFromOrder = (group) => {
    if (!group?.primaryEntry || group.primaryEntry.entryType !== "order") {
      return;
    }

    const prefill = mapPurchaseOrderToPurchaseInvoicePrefill({
      ...group.primaryEntry,
      entries: group.entries
    });

    navigate("/accounts/purchase-invoices", {
      state: {
        accountDocumentPrefill: {
          ...prefill,
          sourcePurchaseOrderId: group.primaryEntry._id || "",
          sourcePurchaseOrderNo: group.primaryEntry.poNumber || group.primaryEntry.documentNumber || ""
        },
        prefillKey: `purchase-order-${group.primaryEntry._id || Date.now()}`
      }
    });
  };

  const convertPerformaToSalesInvoice = (group) => {
    const primaryEntry = group?.primaryEntry || group;
    if (!primaryEntry || primaryEntry.entryType !== "sale") {
      return;
    }

    const groupEntries = Array.isArray(group?.entries) && group.entries.length > 0 ? group.entries : [primaryEntry];

    navigate("/accounts/sales-invoices", {
      state: {
        accountDocumentPrefill: {
          ...mapSaleEntryToSalesInvoicePrefill({
            ...primaryEntry,
            entries: groupEntries
          }),
          sourcePerformaId: primaryEntry._id || "",
          sourcePerformaNo: primaryEntry.documentNumber || ""
        },
        prefillKey: `sales-invoice-from-performa-${primaryEntry._id || Date.now()}`
      }
    });
  };

  const handleEntryTypeChange = (value) => {
    if (isDedicatedEntryPage) {
      return;
    }

    setSearchParams(value ? { entryType: value } : {});
    setForm((current) => ({
      ...current,
      entryType: value,
      orderType: value === "order" ? "supplier_order" : "",
      documentNumber: value === "order" ? "" : current.documentNumber,
      dealerId: value === "sale" ? "" : current.dealerId,
      customerId: value === "sale" ? current.customerId : "",
      counterpartyName: "",
      counterpartyPhone: "",
      counterpartyEmail: "",
      counterpartyGst: "",
      supplierGst: "",
      counterpartyAddress: value === "order" ? GENMECH_DELIVERY_ADDRESS : "",
      deliveryAddressType: value === "order" ? "genmech_service" : value === "sale" ? "work_address" : current.deliveryAddressType,
      roundOffEnabled: value === "order" ? "no" : current.roundOffEnabled
    }));
  };

  // Redesign - Inventory Dashboard Handlers
  const handleSearchChange = (value) => {
    setSearch(value);
    setActiveFilters(prev => ({ ...prev, search: value }));
  };

  const applyFilters = () => {
    setActiveFilters({
      search: localSearch,
      stockCategory: localStockCategory,
      voltage: localVoltage,
      rack: localRack,
      status: localStatus,
      dateFrom: localDateFrom,
      dateTo: localDateTo,
      category: localCategory,
      manufacturer: localManufacturer
    });
    setActiveSpecFilters(localSpecFilters);
  };

  const clearAllFilters = () => {
    setLocalSearch("");
    setLocalStockCategory("");
    setLocalVoltage("");
    setLocalRack("");
    setLocalStatus("");
    setLocalDateFrom("");
    setLocalDateTo("");
    setLocalCategory("");
    setLocalManufacturer("");
    setSelectedRack("");
    setLocalSpecFilters({});
    setActiveFilters({
      search: "",
      stockCategory: "",
      voltage: "",
      rack: "",
      status: "",
      dateFrom: "",
      dateTo: "",
      category: "",
      manufacturer: ""
    });
    setActiveSpecFilters({});
  };

  const openAddMaterialModal = () => {
    setMaterialFormMode("add");
    setSelectedInventoryItem(null);
    setMaterialFormData({
      productName: "",
      partNumber: "",
      modelNumber: "",
      voltage: "",
      stockCategory: "sales",
      brand: "",
      manufacturer: "",
      category: "",
      subCategory: "",
      rackNumber: "",
      shelfBin: "",
      unit: "No",
      openingStock: "",
      currentStock: "",
      minimumStock: "",
      hsnCode: "",
      purchasePrice: "",
      sellingPrice: "",
      supplier: "",
      purchaseDate: "",
      remarks: "",
      specifications: []
    });
    setShowMaterialModal(true);
  };

  const openEditMaterialModal = (item) => {
    setMaterialFormMode("edit");
    setSelectedInventoryItem(item);
    setMaterialFormData({
      productName: item.productName || "",
      partNumber: item.partNumber || item.itemCode || "",
      modelNumber: item.modelNumber || "",
      voltage: item.voltage || "",
      stockCategory: item.stockCategory || "sales",
      brand: item.brand || "",
      manufacturer: item.manufacturer || "",
      category: item.category || "",
      subCategory: item.subCategory || "",
      rackNumber: item.rackNumber || "",
      shelfBin: item.shelfBin || "",
      unit: item.unit || "No",
      openingStock: String(item.openingStock ?? ""),
      currentStock: String(item.currentStock ?? ""),
      minimumStock: String(item.minimumStock ?? ""),
      hsnCode: item.hsnCode || "",
      purchasePrice: String(item.purchasePrice ?? ""),
      sellingPrice: String(item.sellingPrice ?? ""),
      supplier: item.supplier || "",
      purchaseDate: item.purchaseDate ? new Date(item.purchaseDate).toISOString().slice(0, 10) : "",
      remarks: item.remarks || "",
      specifications: Array.isArray(item.specifications) ? item.specifications : []
    });
    setShowMaterialModal(true);
  };

  const openViewMaterialModal = (item) => {
    setMaterialFormMode("view");
    setSelectedInventoryItem(item);
    setMaterialFormData({
      productName: item.productName || "",
      partNumber: item.partNumber || item.itemCode || "",
      modelNumber: item.modelNumber || "",
      voltage: item.voltage || "",
      stockCategory: item.stockCategory || "sales",
      brand: item.brand || "",
      manufacturer: item.manufacturer || "",
      category: item.category || "",
      subCategory: item.subCategory || "",
      rackNumber: item.rackNumber || "",
      shelfBin: item.shelfBin || "",
      unit: item.unit || "No",
      openingStock: String(item.openingStock ?? ""),
      currentStock: String(item.currentStock ?? ""),
      minimumStock: String(item.minimumStock ?? ""),
      hsnCode: item.hsnCode || "",
      purchasePrice: String(item.purchasePrice ?? ""),
      sellingPrice: String(item.sellingPrice ?? ""),
      supplier: item.supplier || "",
      purchaseDate: item.purchaseDate ? new Date(item.purchaseDate).toISOString().slice(0, 10) : "",
      remarks: item.remarks || "",
      specifications: Array.isArray(item.specifications) ? item.specifications : []
    });
    setShowMaterialModal(true);
  };

  const openStockInModal = (item = null) => {
    setMovementType("IN");
    setMovementFormData({
      productId: item ? (item.productId || item._id) : "",
      quantity: "",
      transactionDate: new Date().toISOString().slice(0, 10),
      purchaseDate: new Date().toISOString().slice(0, 10),
      supplier: "",
      invoiceNumber: "",
      purchasePrice: item ? String(item.purchasePrice || "") : "",
      remarks: ""
    });
    setShowMovementModal(true);
  };

  const openStockOutModal = (item = null) => {
    setMovementType("OUT");
    setMovementFormData({
      productId: item ? (item.productId || item._id) : "",
      quantity: "",
      transactionDate: new Date().toISOString().slice(0, 10),
      issuedTo: "",
      department: "",
      projectName: "",
      engineerName: "",
      remarks: ""
    });
    setShowMovementModal(true);
  };

  const openImportModal = () => {
    setImportText("");
    setShowImportModal(true);
  };

  const handleSaveMaterial = async (addAnother = false) => {
    try {
      const data = new FormData();
      Object.keys(materialFormData).forEach(key => {
        if (key === "specifications") {
          data.append(key, JSON.stringify(materialFormData[key]));
        } else if (key === "imageFile") {
          if (materialFormData.imageFile) data.append("image", materialFormData.imageFile);
        } else if (key === "datasheetFile") {
          if (materialFormData.datasheetFile) data.append("datasheet", materialFormData.datasheetFile);
        } else {
          data.append(key, materialFormData[key] || "");
        }
      });

      const opStock = Number(materialFormData.openingStock) || 0;
      const curStock = Number(materialFormData.currentStock) || opStock || 0;
      data.set("openingStock", String(opStock));
      data.set("currentStock", String(curStock));
      data.set("minimumStock", String(Number(materialFormData.minimumStock) || 0));
      data.set("purchasePrice", String(Number(materialFormData.purchasePrice) || 0));
      data.set("sellingPrice", String(Number(materialFormData.sellingPrice) || 0));

      if (materialFormMode === "edit" && selectedInventoryItem?._id) {
        await axios.put(API_BASE_URL + `/api/material-stock/inventory/${selectedInventoryItem._id}`, data);
      } else {
        await axios.post(API_BASE_URL + "/api/material-stock/inventory", data);
      }

      await loadData();
      
      if (addAnother) {
        setMaterialFormData({
          productName: "",
          partNumber: "",
          modelNumber: "",
          voltage: "",
          stockCategory: "sales",
          brand: "",
          manufacturer: "",
          category: "",
          subCategory: "",
          rackNumber: "",
          shelfBin: "",
          unit: "No",
          openingStock: "",
          currentStock: "",
          minimumStock: "",
          hsnCode: "",
          purchasePrice: "",
          sellingPrice: "",
          supplier: "",
          purchaseDate: "",
          remarks: "",
          specifications: [],
          imageUrl: "",
          attachments: [],
          imageFile: null,
          datasheetFile: null
        });
      } else {
        setShowMaterialModal(false);
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save material");
    }
  };

  const getCurrentUserName = () => {
    const role = localStorage.getItem("role") || "";
    if (role === "admin") return localStorage.getItem("adminName") || "Admin";
    if (role === "accounts") return localStorage.getItem("accountsName") || "Accounts";
    if (role === "engineer") return localStorage.getItem("engineerName") || "Engineer";
    return "System User";
  };

  const handleSaveMovement = async () => {
    try {
      const payload = {
        ...movementFormData,
        transactionType: movementType,
        quantity: Number(movementFormData.quantity),
        performedBy: getCurrentUserName()
      };

      await axios.post(API_BASE_URL + "/api/material-stock/movement", payload);
      await loadData();
      setShowMovementModal(false);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to record stock movement");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleImportJson = async () => {
    try {
      setImportLoading(true);
      const parsed = JSON.parse(importText);
      const res = await axios.post(API_BASE_URL + "/api/material-stock/inventory/import", parsed);
      alert(`Import completed! Inserted: ${res.data.inserted}, Updated: ${res.data.updated}`);
      await loadData();
      setShowImportModal(false);
      setImportText("");
    } catch (err) {
      alert(err.message || "Invalid JSON or import failed");
    } finally {
      setImportLoading(false);
    }
  };

  const handleDeleteItem = async (item) => {
    if (window.confirm(`Are you sure you want to delete "${item.productName}"?`)) {
      try {
        const res = await axios.delete(API_BASE_URL + `/api/material-stock/inventory/${item._id}`);
        if (res.data.archived) {
          alert("This material has stock history. It has been archived instead of permanently deleted.");
        } else {
          alert("Material deleted successfully.");
        }
        await loadData();
      } catch (err) {
        alert(err.response?.data?.message || "Failed to delete item");
      }
    }
  };

  const handleArchiveItem = async (item) => {
    if (window.confirm(`Are you sure you want to archive "${item.productName}"?`)) {
      try {
        await axios.put(API_BASE_URL + `/api/material-stock/inventory/${item._id}`, {
          isArchived: true
        });
        await loadData();
      } catch (err) {
        alert(err.response?.data?.message || "Failed to archive item");
      }
    }
  };

  const handleUnarchiveItem = async (item) => {
    if (window.confirm(`Are you sure you want to restore "${item.productName}" from archive?`)) {
      try {
        await axios.put(API_BASE_URL + `/api/material-stock/inventory/${item._id}`, {
          isArchived: false
        });
        await loadData();
      } catch (err) {
        alert(err.response?.data?.message || "Failed to unarchive item");
      }
    }
  };

  const exportToExcel = () => {
    const dataToExport = filteredStockItems.map(item => ({
      "Material Name": item.productName || "",
      "Part Code": item.partNumber || item.itemCode || "",
      "Model Number": item.modelNumber || "",
      "Manufacturer": item.manufacturer || "",
      "Category": item.category || "",
      "Voltage": item.voltage || "",
      "Unit": item.unit || "No",
      "Stock Category": item.stockCategory || "sales",
      "Current Stock": item.currentStock ?? 0,
      "Min Stock Level": item.minimumStock ?? 0,
      "Purchase Price": item.purchasePrice || 0,
      "Selling Price": item.sellingPrice || 0,
      "Location": item.location || "",
      "Remarks": item.remarks || ""
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory");
    XLSX.writeFile(workbook, "Electrical_Inventory.xlsx");
  };

  const exportToCSV = () => {
    const headers = [
      "Material Name", "Part Code", "Model Number", "Manufacturer", "Category", 
      "Voltage", "Unit", "Stock Category", "Current Stock", "Min Stock Level", 
      "Purchase Price", "Selling Price", "Location", "Remarks"
    ];
    
    const rows = filteredStockItems.map(item => [
      `"${(item.productName || "").replace(/"/g, '""')}"`,
      `"${(item.partNumber || item.itemCode || "").replace(/"/g, '""')}"`,
      `"${(item.modelNumber || "").replace(/"/g, '""')}"`,
      `"${(item.manufacturer || "").replace(/"/g, '""')}"`,
      `"${(item.category || "").replace(/"/g, '""')}"`,
      `"${(item.voltage || "").replace(/"/g, '""')}"`,
      `"${item.unit || "No"}"`,
      `"${item.stockCategory || "sales"}"`,
      item.currentStock ?? 0,
      item.minimumStock ?? 0,
      item.purchasePrice || 0,
      item.sellingPrice || 0,
      `"${(item.location || "").replace(/"/g, '""')}"`,
      `"${(item.remarks || "").replace(/"/g, '""')}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "Electrical_Inventory.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const applyCounterparty = (party, type, typedValue) => {
    setForm((current) => ({
      ...current,
      counterpartyName: typedValue,
      dealerId: type === "supplier" ? party?._id || "" : "",
      customerId: type === "supplier" ? "" : party?._id || "",
      counterpartyPhone: party?.phone || "",
      counterpartyEmail: party?.email || "",
      counterpartyGst: party?.gstNumber || "",
      supplierGst: party?.gstNumber || "",
      counterpartyAddress: current.entryType === "order"
        ? (current.deliveryAddressType === "other"
          ? current.otherDeliveryAddress
          : resolveSupplierAddressByType(party, current.deliveryAddressType, current.otherDeliveryAddress))
        : current.entryType === "sale"
        ? resolveCustomerAddressByType(party, current.deliveryAddressType || "work_address", current.otherDeliveryAddress)
        : formatCounterpartyAddress(party)
    }));
  };

  const handleCounterpartyInput = (value) => {
    const needsSupplier = form.entryType === "purchase" || form.entryType === "order";

    if (needsSupplier) {
      const matchedSupplier = suppliers.find((supplier) =>
        [supplier.companyName, supplier.name, supplier.dealerId, supplier.supplierCode]
          .filter(Boolean)
          .some((name) => name.toLowerCase() === value.trim().toLowerCase())
      );

      applyCounterparty(matchedSupplier, "supplier", value);
      return;
    }

    const matchedCustomer = customers.find((customer) =>
      [customer.name, customer.customerId]
        .filter(Boolean)
        .some((name) => name.toLowerCase() === value.trim().toLowerCase())
    );

    applyCounterparty(matchedCustomer, "customer", value);
  };

  const handleHeaderChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === "entryType") {
      handleEntryTypeChange(value);
      return;
    }

    if (name === "counterpartyName") {
      handleCounterpartyInput(value);
      return;
    }

    if (name === "gstType") {
      setForm((current) => ({
        ...current,
        gstType: value,
        igst: normalizeGstType(value) === "sez_supply"
          ? "0"
          : current.igst || getDefaultIntegratedGst(value),
        gst: isIntegratedGstType(value)
          ? String(Number(normalizeGstType(value) === "sez_supply" ? 0 : (current.igst || getDefaultIntegratedGst(value))) || 0)
          : String((Number(current.sgst) || 0) + (Number(current.cgst) || 0) || 18)
      }));
      return;
    }

    if (["sgst", "cgst", "igst"].includes(name)) {
      setForm((current) => {
        const next = {
          ...current,
          [name]: value
        };
        const sgst = Number(name === "sgst" ? value : next.sgst) || 0;
        const cgst = Number(name === "cgst" ? value : next.cgst) || 0;
        const igst = Number(name === "igst" ? value : next.igst) || 0;

        next.gst = isIntegratedGstType(next.gstType) ? String(igst) : String(sgst + cgst);
        return next;
      });
      return;
    }

    if (name === "deliveryAddressType") {
      if (form.entryType === "sale") {
        const selectedCustomer = customers.find((customer) => customer._id === form.customerId)
          || customers.find((customer) =>
            [customer.name, customer.customerId]
              .filter(Boolean)
              .some((entry) => entry.toLowerCase() === form.counterpartyName.trim().toLowerCase())
          );

        setForm((current) => ({
          ...current,
          deliveryAddressType: value,
          counterpartyAddress: value === "other"
            ? current.otherDeliveryAddress
            : resolveCustomerAddressByType(selectedCustomer, value, current.otherDeliveryAddress)
        }));
        return;
      }

      const selectedSupplier = suppliers.find((supplier) => supplier._id === form.dealerId)
        || suppliers.find((supplier) =>
          [supplier.companyName, supplier.name, supplier.dealerId, supplier.supplierCode]
            .filter(Boolean)
            .some((entry) => entry.toLowerCase() === form.counterpartyName.trim().toLowerCase())
        );

      setForm((current) => ({
        ...current,
        deliveryAddressType: value,
        counterpartyAddress: value === "other"
          ? current.otherDeliveryAddress
          : resolveSupplierAddressByType(selectedSupplier, value, current.otherDeliveryAddress)
      }));
      return;
    }

    if (name === "otherDeliveryAddress") {
      setForm((current) => ({
        ...current,
        otherDeliveryAddress: value,
        counterpartyAddress: current.deliveryAddressType === "other"
          ? value
          : current.counterpartyAddress
      }));
      return;
    }

    if (name === "dispatchMode") {
      setForm((current) => ({
        ...current,
        dispatchMode: value,
        dispatchThrough: value === "other" ? current.dispatchThrough : ""
      }));
      return;
    }

    if (name === "commonDiscountType") {
      setForm((current) => ({
        ...current,
        commonDiscountType: value,
        entries: current.entries.map((item) => recalculateDiscountedEntry({
          ...item,
          discountType: value
        }))
      }));
      return;
    }

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // eslint-disable-next-line no-unused-vars
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({
      ...current,
      [name]: value
    }));
  };

  const updateEntryRow = (index, name, value) => {
    setForm((current) => ({
      ...current,
      entries: current.entries.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        let nextItem = {
          ...item,
          [name]: value
        };

        if (name === "discountValue") {
          nextItem = recalculateDiscountedEntry({
            ...nextItem,
            discountType: current.commonDiscountType
          });
          return nextItem;
        }

        if (["quantity", "listPrice"].includes(name)) {
          nextItem = recalculateDiscountedEntry(nextItem);
          return nextItem;
        }

        if (name === "quantity" || name === "unitPrice") {
          const quantity = Number(name === "quantity" ? value : nextItem.quantity);
          const unitPrice = Number(name === "unitPrice" ? value : nextItem.unitPrice);

          nextItem.totalAmount = quantity > 0 && unitPrice > 0
            ? String((quantity * unitPrice).toFixed(2))
            : "";
        }

        return nextItem;
      })
    }));
  };

  const applySavedMaterial = (index, value, field) => {
    const query = normalizeMaterialValue(value);
    if (!query) return;

    const matchedMaterial = materialCatalog.find((item) =>
      field === "itemCode"
        ? normalizeMaterialValue(item.itemCode) === query
          || normalizeMaterialValue(item.itemName) === query
          || normalizeMaterialValue(item.hsnCode) === query
        : normalizeMaterialValue(item.itemName) === query
          || normalizeMaterialValue(item.itemCode) === query
          || normalizeMaterialValue(item.hsnCode) === query
    );

    if (!matchedMaterial) {
      return;
    }

    setForm((current) => ({
      ...current,
      entries: current.entries.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        const nextItem = {
          ...item,
          productId: matchedMaterial.productId || item.productId,
          itemName: matchedMaterial.itemName || item.itemName,
          itemCode: matchedMaterial.itemCode || item.itemCode,
          hsnCode: matchedMaterial.hsnCode || item.hsnCode,
          description: matchedMaterial.description || item.description,
          unit: formatUnit(matchedMaterial.unit || item.unit),
          listPrice: matchedMaterial.unitPrice || item.listPrice,
          discountType: current.commonDiscountType,
          unitPrice: matchedMaterial.unitPrice || item.unitPrice
        };

        return recalculateDiscountedEntry(nextItem);
      })
    }));
  };

  const updateEntryRowWithAutofill = (index, name, value) => {
    updateEntryRow(index, name, value);

    if (name === "itemName" || name === "itemCode") {
      applySavedMaterial(index, value, name);
    }
  };

  const addEntryRow = () => {
    setForm((current) => ({
      ...current,
      entries: [...current.entries, {
        ...buildEmptyItem(),
        discountType: current.commonDiscountType
      }]
    }));
  };

  const removeEntryRow = (index) => {
    setForm((current) => ({
      ...current,
      entries: current.entries.length === 1
        ? [buildEmptyItem()]
        : current.entries.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const moveEntryRow = (index, direction) => {
    setForm((current) => {
      const entries = [...(current.entries || [])];
      if (direction === "up" && index > 0) {
        const temp = entries[index];
        entries[index] = entries[index - 1];
        entries[index - 1] = temp;
      } else if (direction === "down" && index < entries.length - 1) {
        const temp = entries[index];
        entries[index] = entries[index + 1];
        entries[index + 1] = temp;
      }
      return { ...current, entries };
    });
  };

  const handleSubmit = async (event) => {
    console.log("MaterialStock handleSubmit event type:", event?.type, "target:", event?.target);
    console.trace("MaterialStock handleSubmit trace");
    event.preventDefault();

    const filteredEntries = form.entries.filter((item) =>
      item.itemName.trim() || item.itemCode.trim() || item.quantity
    );

    if (filteredEntries.length === 0) {
      alert("Add at least one product row before saving");
      return;
    }

    const invalidRowIndex = filteredEntries.findIndex((item) =>
      form.entryType === "order"
        ? !String(item.itemCode || "").trim()
        : !String(item.itemName || "").trim()
    );
    if (invalidRowIndex >= 0) {
      alert(form.entryType === "order"
        ? `Enter item code in product row ${invalidRowIndex + 1}`
        : `Enter material name in product row ${invalidRowIndex + 1}`);
      return;
    }

    const invalidQuantityIndex = filteredEntries.findIndex((item) => Number(item.quantity) <= 0);
    if (invalidQuantityIndex >= 0) {
      alert(`Enter quantity greater than 0 in product row ${invalidQuantityIndex + 1}`);
      return;
    }

    const needsSupplier = form.entryType === "purchase" || form.entryType === "order";
    const needsCustomer = form.entryType === "sale";

    if (needsSupplier && !form.dealerId) {
      alert("Select a saved supplier so details can be auto-filled");
      return;
    }

    if (needsCustomer && !form.customerId) {
      alert("Select a saved customer so details can be auto-filled");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        entryType: form.entryType,
        orderType: form.orderType,
        documentNumber: form.documentNumber,
        poNumber: form.poNumber,
        poDate: form.poDate || null,
        reference: form.reference,
        referenceDate: form.referenceDate,
        transactionDate: form.transactionDate || new Date().toISOString(),
        payDate: form.payDate || null,
        dealerId: form.dealerId,
        customerId: form.customerId,
        counterpartyName: form.counterpartyName,
        counterpartyPhone: form.counterpartyPhone,
        counterpartyEmail: form.counterpartyEmail,
        counterpartyGst: form.counterpartyGst,
        supplierGst: form.supplierGst,
        endUserGst: form.endUserGst,
        counterpartyAddress: form.counterpartyAddress,
        deliveryAddressType: form.deliveryAddressType,
        otherDeliveryAddress: form.otherDeliveryAddress,
        sourceQuotationId: form.sourceQuotationId,
        sourceQuotationNo: form.sourceQuotationNo,
        sourceDeliveryChallanId: form.sourceDeliveryChallanId,
        sourceDeliveryChallanNo: form.sourceDeliveryChallanNo,
        sourceDeliveryChallanType: form.sourceDeliveryChallanType,
        skipInventoryDeduction: Boolean(form.skipInventoryDeduction),
        location: form.location,
        payment: form.payment,
        paymentMode: form.paymentMode,
        roundOffEnabled: form.entryType !== "order" && form.roundOffEnabled === "yes",
        roundOffAmount: form.entryType === "order" ? 0 : roundOffAmount,
        finalTotal: form.entryType === "order" ? grandTotal : finalTotal,
        freightType: form.freightType,
        dispatchMode: form.dispatchMode,
        expectedDeliveryDate: form.expectedDeliveryDate,
        deliveryAt: form.deliveryAt,
        deliveryNote: form.deliveryNote,
        dispatchThrough: form.dispatchThrough,
        ewayBillNo: form.ewayBillNo || "",
        otherReferences: form.otherReferences || "",
        dispatchDocNo: form.dispatchDocNo || "",
        deliveryNoteDate: form.deliveryNoteDate || null,
        destination: form.destination || "",
        billOfLading: form.billOfLading || "",
        motorVehicleNo: form.motorVehicleNo || "",
        deliveryTime: form.deliveryTime,
        paymentTerms: form.paymentTerms,
        warranty: form.warranty,
        terms: form.terms,
        billingStatus: form.billingStatus,
        paymentStatus: form.paymentStatus,
        notes: form.notes,
        showDiscountInPdf: Boolean(form.showDiscountInPdf),
        entries: filteredEntries.map((item) => ({
          ...item,
          productId: item.productId || "",
          itemName: item.itemName || item.description || item.itemCode,
          quantity: Number(item.quantity),
          unit: formatUnit(item.unit),
          stockCategory: normalizeStockCategory(item.stockCategory),
          location: item.location || "",
          listPrice: Number(item.listPrice) || 0,
          discountType: item.discountType === "amount" ? "amount" : "percent",
          discountValue: Number(item.discountValue) || 0,
          unitPrice: Number(item.unitPrice) || 0,
          totalAmount: Number(item.totalAmount) || 0
        })),
        gstType: form.gstType,
        sgst: Number(form.sgst) || 0,
        cgst: Number(form.cgst) || 0,
        igst: Number(form.igst) || 0,
        gst: Number(form.gst) || 0,
        advanceAmount,
        balanceAmount
      };

      if (editingEntryId) {
        const firstEntry = payload.entries[0];
        await axios.put(API_BASE_URL + `${documentApiBase}/${editingEntryId}`, {
          ...payload,
          ...firstEntry
        });
      } else {
        await axios.post(API_BASE_URL + documentApiBase, payload);
      }

      setForm(isEntryMode ? buildEmptyFormForEntryType(entryTypeFilter) : buildEmptyForm());
      setEditingEntryId("");
      setShowEntryForm(false);
      await refreshMaterialCatalog();
      await loadData(search);
    } catch (error) {
      alert(error.response?.data?.message || (isEntryMode ? "Failed to save account document" : "Failed to save stock entry"));
    } finally {
      setSaving(false);
    }
  };

  const handleEditEntry = (entry) => {
    const entryType = entry.entryType || (isEntryMode ? entryTypeFilter : "purchase");
    const isOrderEntry = entryType === "order";
    const deliveryAddressType = isOrderEntry
      ? (entry.deliveryAddressType === "other" ? "other" : "genmech_service")
      : entryType === "sale"
      ? entry.deliveryAddressType || "work_address"
      : entry.deliveryAddressType || "billing_address";

    const targetKey = getEntryHistoryGroupKey(entry);
    const relatedEntries = scopedEntries
      .filter((item) => getEntryHistoryGroupKey(item) === targetKey)
      .sort((a, b) => new Date(a.createdAt || a.transactionDate || 0) - new Date(b.createdAt || b.transactionDate || 0));

    const activeEntries = relatedEntries.length > 0 ? relatedEntries : [entry];

    setEditingEntryId(entry._id);
    setShowEntryForm(true);
    setForm({
      entryType,
      orderType: entry.orderType || "supplier_order",
      documentNumber: isOrderEntry ? "" : entry.documentNumber || "",
      poNumber: entry.poNumber || "",
      poDate: entry.poDate ? new Date(entry.poDate).toISOString().slice(0, 10) : "",
      reference: entry.reference || "",
      referenceDate: entry.referenceDate ? new Date(entry.referenceDate).toISOString().slice(0, 10) : "",
      transactionDate: entry.transactionDate ? new Date(entry.transactionDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      payDate: entry.payDate ? new Date(entry.payDate).toISOString().slice(0, 10) : "",
      dealerId: entry.dealerId || "",
      customerId: entry.customerId || "",
      counterpartyName: entry.counterpartyName || "",
      counterpartyPhone: entry.counterpartyPhone || "",
      counterpartyEmail: entry.counterpartyEmail || "",
      counterpartyGst: entry.counterpartyGst || "",
      supplierGst: entry.supplierGst || entry.counterpartyGst || "",
      endUserGst: entry.endUserGst || "",
      counterpartyAddress: isOrderEntry
        ? (deliveryAddressType === "other" ? entry.otherDeliveryAddress || entry.counterpartyAddress || "" : GENMECH_DELIVERY_ADDRESS)
        : entry.counterpartyAddress || "",
      deliveryAddressType,
      otherDeliveryAddress: entry.otherDeliveryAddress || (deliveryAddressType === "other" ? entry.counterpartyAddress || "" : ""),
      sourceQuotationId: entry.sourceQuotationId || "",
      sourceQuotationNo: entry.sourceQuotationNo || "",
      sourceDeliveryChallanId: entry.sourceDeliveryChallanId || "",
      sourceDeliveryChallanNo: entry.sourceDeliveryChallanNo || "",
      sourceDeliveryChallanType: entry.sourceDeliveryChallanType || "",
      skipInventoryDeduction: Boolean(entry.skipInventoryDeduction),
      location: entry.location || "",
      payment: entry.payment || "",
      paymentMode: entry.paymentMode || "",
      roundOffEnabled: isOrderEntry ? "no" : entry.roundOffEnabled ? "yes" : "no",
      roundOffAmount: String(entry.roundOffAmount ?? ""),
      finalTotal: String(entry.finalTotal ?? ""),
      freightType: entry.freightType || "paid",
      dispatchMode: entry.dispatchMode || "by_road",
      expectedDeliveryDate: entry.expectedDeliveryDate ? new Date(entry.expectedDeliveryDate).toISOString().slice(0, 10) : "",
      deliveryAt: entry.deliveryAt || "",
      deliveryNote: entry.deliveryNote || "",
      dispatchThrough: entry.dispatchThrough || "",
      ewayBillNo: entry.ewayBillNo || "",
      otherReferences: entry.otherReferences || "",
      dispatchDocNo: entry.dispatchDocNo || "",
      deliveryNoteDate: entry.deliveryNoteDate ? new Date(entry.deliveryNoteDate).toISOString().slice(0, 10) : "",
      destination: entry.destination || "",
      billOfLading: entry.billOfLading || "",
      motorVehicleNo: entry.motorVehicleNo || "",
      deliveryTime: entry.deliveryTime || "Within 2-3 weeks after confirmed purchase order",
      paymentTerms: entry.paymentTerms || "50% advance along with PO, balance before dispatch",
      warranty: entry.warranty || "12 months against manufacturing defects",
      terms: entry.terms || "Prices are valid for the agreed document date. Taxes are extra as applicable. Freight and installation are extra unless mentioned.",
      showDiscountInPdf: Boolean(entry.showDiscountInPdf),
      commonDiscountType: entry.discountType || "percent",
      billingStatus: entry.billingStatus || "not_billed",
      paymentStatus: entry.paymentStatus || "not_done",
      gstType: entry.gstType || "cgst_sgst",
      sgst: String(entry.sgst ?? 9),
      cgst: String(entry.cgst ?? 9),
      igst: String(entry.igst ?? getDefaultIntegratedGst(entry.gstType)),
      gst: String(entry.gst ?? 18),
      advanceAmount: String(entry.advanceAmount ?? ""),
      balanceAmount: String(entry.balanceAmount ?? ""),
      notes: entry.notes || "",
      entries: activeEntries.map((item) => ({
        productId: item.productId || "",
        itemName: item.itemName || "",
        itemCode: item.itemCode || "",
        hsnCode: item.hsnCode || "",
        description: item.description || "",
        quantity: String(item.quantity ?? ""),
        unit: formatUnit(item.unit),
        stockCategory: normalizeStockCategory(item.stockCategory),
        location: item.location || "",
        listPrice: String(item.listPrice ?? item.unitPrice ?? ""),
        discountType: item.discountType || "percent",
        discountValue: String(item.discountValue ?? ""),
        unitPrice: String(item.unitPrice ?? ""),
        totalAmount: String(item.totalAmount ?? "")
      }))
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    const nextEmptyForm = buildEmptyForm();
    setEditingEntryId("");
    setShowEntryForm(false);
    setForm({
      ...nextEmptyForm,
      entryType: isEntryMode ? entryTypeFilter : nextEmptyForm.entryType
    });
  };

  const refreshMaterialCatalog = useCallback(async () => {
    try {
      const catalogRes = await axios.get(API_BASE_URL + "/api/products", { params: { limit: 200 } });
      const resData = catalogRes.data;
      const products = Array.isArray(resData) ? resData : (Array.isArray(resData?.products) ? resData.products : []);
      setProductCatalog(products);
    } catch (catalogError) {
      setProductCatalog([]);
    }
  }, []);

  const openEntryPdf = (entryId, pdfType = salesDocumentVariant === "performa" ? "performa" : "") => {
    const params = new URLSearchParams();
    if (pdfType) params.set("pdfType", pdfType);
    const query = params.toString();
    window.open(
      appendAuthToken(API_BASE_URL + `${documentApiBase}/${entryId}/pdf${query ? `?${query}` : ""}`),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const downloadEntryPdf = (entryId, pdfType = salesDocumentVariant === "performa" ? "performa" : "") => {
    const params = new URLSearchParams();
    if (pdfType) params.set("pdfType", pdfType);
    params.set("download", "1");
    const query = params.toString();
    window.open(
      appendAuthToken(API_BASE_URL + `${documentApiBase}/${entryId}/pdf${query ? `?${query}` : ""}`),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openAvailableStockPdf = () => {
    const params = new URLSearchParams();
    if (activeFilters.search.trim()) params.set("search", activeFilters.search.trim());
    if (activeFilters.voltage) params.set("voltage", activeFilters.voltage);
    if (activeFilters.stockCategory) params.set("stockCategory", activeFilters.stockCategory);

    const activeRack = selectedRack || activeFilters.rack;
    if (activeRack) params.set("rack", activeRack);

    if (activeFilters.status) params.set("status", activeFilters.status);
    if (activeFilters.dateFrom) params.set("dateFrom", activeFilters.dateFrom);
    if (activeFilters.dateTo) params.set("dateTo", activeFilters.dateTo);

    // Forward active specifications filters
    Object.entries(activeSpecFilters).forEach(([specName, specValue]) => {
      if (specValue) {
        params.set("spec_" + specName, specValue);
      }
    });

    const query = params.toString();
    window.open(appendAuthToken(API_BASE_URL + `/api/material-stock/summary/pdf${query ? `?${query}` : ""}`), "_blank", "noopener,noreferrer");
  };

  // eslint-disable-next-line no-unused-vars
  const openClassificationModal = (item) => {
    setClassificationItem(item);
    setClassificationDraft({
      stockCategory: normalizeStockCategory(item.stockCategory),
      location: item.location || ""
    });
  };

  const handleSaveClassification = async () => {
    if (!classificationItem?._id) return;

    try {
      setClassificationSaving(true);
      await axios.patch(API_BASE_URL + `/api/material-stock/inventory/${classificationItem._id}/classification`, {
        stockCategory: normalizeStockCategory(classificationDraft.stockCategory),
        location: classificationDraft.location || ""
      });
      setClassificationItem(null);
      await loadData(search);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update stock type");
    } finally {
      setClassificationSaving(false);
    }
  };

  const counts = useMemo(() => ({
    purchaseCount: scopedEntries.filter((entry) => entry.entryType === "purchase").length,
    saleCount: scopedEntries.filter((entry) => entry.entryType === "sale").length,
    orderCount: scopedEntries.filter((entry) => entry.entryType === "order").length
  }), [scopedEntries]);

  const groupedHistoryEntries = useMemo(() => {
    const groups = new Map();

    scopedEntries.forEach((entry) => {
      const key = getEntryHistoryGroupKey(entry);
      const existingGroup = groups.get(key);

      if (!existingGroup) {
        groups.set(key, {
          key,
          primaryEntry: entry,
          entries: [entry],
          productCount: 1,
          totalQuantity: Number(entry.quantity) || 0,
          totalAmount: Number(entry.totalAmount) || 0
        });
        return;
      }

      existingGroup.entries.push(entry);
      existingGroup.productCount += 1;
      existingGroup.totalQuantity += Number(entry.quantity) || 0;
      existingGroup.totalAmount += Number(entry.totalAmount) || 0;
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        entries: [...group.entries].sort((a, b) => new Date(a.createdAt || a.transactionDate || 0) - new Date(b.createdAt || b.transactionDate || 0))
      }))
      .sort((a, b) => new Date(b.primaryEntry.transactionDate || 0) - new Date(a.primaryEntry.transactionDate || 0));
  }, [scopedEntries]);

  const sortedGroupedHistoryEntries = useMemo(() => {
    return [...groupedHistoryEntries].sort((a, b) => {
      if (historySortField === "invoiceNo") {
        const valA = a.primaryEntry.documentNumber || a.primaryEntry.poNumber || "";
        const valB = b.primaryEntry.documentNumber || b.primaryEntry.poNumber || "";
        return historySortOrder === "asc"
          ? valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' })
          : valB.localeCompare(valA, undefined, { numeric: true, sensitivity: 'base' });
      } else {
        // default: sort by date
        const dateA = new Date(a.primaryEntry.transactionDate || 0);
        const dateB = new Date(b.primaryEntry.transactionDate || 0);
        return historySortOrder === "asc" ? dateA - dateB : dateB - dateA;
      }
    });
  }, [groupedHistoryEntries, historySortField, historySortOrder]);

  const allProductsHistory = useMemo(() => {
    return [...scopedEntries].sort((a, b) => new Date(b.transactionDate || 0) - new Date(a.transactionDate || 0));
  }, [scopedEntries]);

  const sortedAllProductsHistory = useMemo(() => {
    return [...allProductsHistory].sort((a, b) => {
      if (historySortField === "invoiceNo") {
        const valA = a.documentNumber || a.poNumber || "";
        const valB = b.documentNumber || b.poNumber || "";
        return historySortOrder === "asc"
          ? valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' })
          : valB.localeCompare(valA, undefined, { numeric: true, sensitivity: 'base' });
      } else {
        const dateA = new Date(a.transactionDate || 0);
        const dateB = new Date(b.transactionDate || 0);
        return historySortOrder === "asc" ? dateA - dateB : dateB - dateA;
      }
    });
  }, [allProductsHistory, historySortField, historySortOrder]);

  const filteredGroupedHistory = useMemo(() => {
    if (!search.trim()) return sortedGroupedHistoryEntries;
    const s = search.toLowerCase();
    return sortedGroupedHistoryEntries.filter((group) => {
      const entry = group.primaryEntry;
      const matchesHeader =
        String(entry.documentNumber || "").toLowerCase().includes(s) ||
        String(entry.poNumber || "").toLowerCase().includes(s) ||
        String(entry.counterpartyName || "").toLowerCase().includes(s) ||
        String(entry.reference || "").toLowerCase().includes(s) ||
        String(entry.notes || "").toLowerCase().includes(s);
      
      const matchesItems = group.entries.some((item) => 
        String(item.itemName || "").toLowerCase().includes(s) ||
        String(item.itemCode || "").toLowerCase().includes(s) ||
        String(item.hsnCode || "").toLowerCase().includes(s) ||
        String(item.description || "").toLowerCase().includes(s)
      );

      return matchesHeader || matchesItems;
    });
  }, [sortedGroupedHistoryEntries, search]);

  const filteredProductsHistory = useMemo(() => {
    if (!search.trim()) return sortedAllProductsHistory;
    const s = search.toLowerCase();
    return sortedAllProductsHistory.filter((entry) => 
      String(entry.documentNumber || "").toLowerCase().includes(s) ||
      String(entry.poNumber || "").toLowerCase().includes(s) ||
      String(entry.counterpartyName || "").toLowerCase().includes(s) ||
      String(entry.itemName || "").toLowerCase().includes(s) ||
      String(entry.itemCode || "").toLowerCase().includes(s) ||
      String(entry.hsnCode || "").toLowerCase().includes(s) ||
      String(entry.description || "").toLowerCase().includes(s)
    );
  }, [sortedAllProductsHistory, search]);

  const displayedGroupedHistory = useMemo(() => {
    return filteredGroupedHistory.slice(0, invoicesLimit);
  }, [filteredGroupedHistory, invoicesLimit]);

  const displayedProductsHistory = useMemo(() => {
    return filteredProductsHistory.slice(0, productsLimit);
  }, [filteredProductsHistory, productsLimit]);

  const materialCatalog = useMemo(() => {
    return productCatalog
      .map((product) => ({
        productId: String(product._id || ""),
        itemName: String(product.description || "").trim(),
        itemCode: String(product.itemCode || "").trim(),
        sku: "",
        description: String(product.description || "").trim(),
        hsnCode: String(product.hsnCode || "").trim(),
        unit: formatUnit(product.unit),
        unitPrice: product.listPrice ? String(product.listPrice) : "",
        listPrice: product.listPrice ? String(product.listPrice) : ""
      }))
      .sort((a, b) => `${a.itemName} ${a.itemCode}`.localeCompare(`${b.itemName} ${b.itemCode}`));
  }, [productCatalog]);

  const materialSuggestions = useMemo(
    () => materialCatalog.flatMap((item) => [item.itemName, item.itemCode, item.hsnCode].filter(Boolean)),
    [materialCatalog]
  );

  const materialDetails = useMemo(() => {
    const map = new Map();

    inventoryItems.forEach((item) => {
      const key = getInventoryMaterialKey(item);
      map.set(key, {
        ...item,
        itemName: item.productName || item.itemName || "",
        availableStock: Number(item.currentStock) || 0,
        totalIn: 0,
        totalOut: 0,
        lastTransactionDate: item.lastUpdated,
        recentEntries: []
      });
    });

    return map;
  }, [inventoryItems]);

  const selectedMaterial = selectedMaterialKey ? materialDetails.get(selectedMaterialKey) : null;
  const voltagesList = useMemo(() => {
    const list = new Set();
    inventoryItems.forEach(item => {
      if (item.voltage) list.add(item.voltage.trim());
    });
    return Array.from(list).sort();
  }, [inventoryItems]);

  const racksList = useMemo(() => {
    return storageLocations.map(loc => loc.name).sort();
  }, [storageLocations]);

  const lowStockCount = useMemo(() => {
    return inventoryItems.filter(item => {
      const minStock = item.minimumStock !== undefined ? Number(item.minimumStock) : 5;
      const stock = Number(item.currentStock) || 0;
      return stock <= minStock && stock > 0;
    }).length;
  }, [inventoryItems]);

  const rackStats = useMemo(() => {
    const stats = {};
    storageLocations.forEach(loc => {
      stats[loc.name] = {
        id: loc._id,
        total: 0,
        lowStock: 0
      };
    });

    inventoryItems.forEach(item => {
      const itemLoc = String(item.rackNumber || item.location || "").trim();
      storageLocations.forEach(loc => {
        const matchExact = itemLoc.toLowerCase() === loc.name.toLowerCase();
        const matchPrefix = item.location && String(item.location).toLowerCase().startsWith(loc.name.toLowerCase() + " -");
        const matchWhole = item.location && String(item.location).toLowerCase() === loc.name.toLowerCase();
        if (matchExact || matchPrefix || matchWhole) {
          if (stats[loc.name]) {
            stats[loc.name].total += 1;
            const minStock = item.minimumStock !== undefined ? Number(item.minimumStock) : 5;
            if ((Number(item.currentStock) || 0) <= minStock) {
              stats[loc.name].lowStock += 1;
            }
          }
        }
      });
    });

    return stats;
  }, [inventoryItems, storageLocations]);


  const filteredStockItems = useMemo(() => {
    let items = Array.isArray(inventoryItems) ? inventoryItems : [];

    // Filter by rack selection (from rack overview panel)
    if (selectedRack) {
      items = items.filter(item => {
        const itemLoc = String(item.rackNumber || item.location || "").trim().toLowerCase();
        const matchExact = itemLoc === selectedRack.toLowerCase();
        const matchPrefix = item.location && String(item.location).toLowerCase().startsWith(selectedRack.toLowerCase() + " -");
        const matchWhole = item.location && String(item.location).toLowerCase() === selectedRack.toLowerCase();
        return matchExact || matchPrefix || matchWhole;
      });
    }

    // Filter by active search (includes specifications name and value)
    if (activeFilters.search) {
      const q = activeFilters.search.toLowerCase().trim();
      items = items.filter(item => 
        (item.productName || "").toLowerCase().includes(q) ||
        (item.itemCode || "").toLowerCase().includes(q) ||
        (item.voltage || "").toLowerCase().includes(q) ||
        (item.brand || "").toLowerCase().includes(q) ||
        (item.modelNumber || "").toLowerCase().includes(q) ||
        (item.partNumber || "").toLowerCase().includes(q) ||
        (item.category || "").toLowerCase().includes(q) ||
        (item.subCategory || "").toLowerCase().includes(q) ||
        (item.location || "").toLowerCase().includes(q) ||
        (item.supplier || "").toLowerCase().includes(q) ||
        (item.hsnCode || "").toLowerCase().includes(q) ||
        (Array.isArray(item.specifications) && item.specifications.some(spec => 
          (spec.name || "").toLowerCase().includes(q) ||
          (spec.value || "").toLowerCase().includes(q)
        ))
      );
    }

    // Filter by active voltage
    if (activeFilters.voltage) {
      items = items.filter(item => item.voltage === activeFilters.voltage);
    }

    // Filter by active stockCategory
    if (activeFilters.stockCategory) {
      items = items.filter(item => item.stockCategory === activeFilters.stockCategory);
    }

    // Filter by active rack number
    if (activeFilters.rack) {
      items = items.filter(item => {
        const itemLoc = String(item.rackNumber || item.location || "").trim().toLowerCase();
        const matchExact = itemLoc === activeFilters.rack.toLowerCase();
        const matchPrefix = item.location && String(item.location).toLowerCase().startsWith(activeFilters.rack.toLowerCase() + " -");
        const matchWhole = item.location && String(item.location).toLowerCase() === activeFilters.rack.toLowerCase();
        return matchExact || matchPrefix || matchWhole;
      });
    }

    // Filter by active category
    if (activeFilters.category) {
      items = items.filter(item => (item.category || "").toLowerCase() === activeFilters.category.toLowerCase());
    }

    // Filter by active manufacturer
    if (activeFilters.manufacturer) {
      items = items.filter(item => (item.manufacturer || "").toLowerCase() === activeFilters.manufacturer.toLowerCase());
    }

    // Filter by active stock status
    if (activeFilters.status === "archived") {
      items = items.filter(item => item.isArchived === true);
    } else {
      // By default hide archived items
      items = items.filter(item => item.isArchived !== true);

      if (activeFilters.status) {
        items = items.filter(item => {
          const stock = Number(item.currentStock) || 0;
          const minStock = item.minimumStock !== undefined ? Number(item.minimumStock) : 5;
          if (activeFilters.status === "in_stock") {
            return stock > minStock;
          } else if (activeFilters.status === "low_stock") {
            return stock <= minStock && stock > 0;
          } else if (activeFilters.status === "out_of_stock") {
            return stock <= 0;
          }
          return true;
        });
      }
    }

    // Filter by date range (lastUpdated)
    if (activeFilters.dateFrom) {
      const from = new Date(activeFilters.dateFrom).getTime();
      items = items.filter(item => {
        const itemDate = new Date(item.lastUpdated || item.updatedAt || Date.now()).getTime();
        return itemDate >= from;
      });
    }
    if (activeFilters.dateTo) {
      const to = new Date(activeFilters.dateTo + "T23:59:59").getTime();
      items = items.filter(item => {
        const itemDate = new Date(item.lastUpdated || item.updatedAt || Date.now()).getTime();
        return itemDate <= to;
      });
    }

    // Filter by active spec filters
    if (Object.keys(activeSpecFilters).length > 0) {
      items = items.filter(item => {
        return Object.entries(activeSpecFilters).every(([specName, specValue]) => {
          if (!specValue) return true;
          if (!Array.isArray(item.specifications)) return false;
          return item.specifications.some(s => s.name === specName && s.value === specValue);
        });
      });
    }

    // Apply sorting by status if active
    if (sortColumn === "status" && sortDirection) {
      items = [...items].sort((a, b) => {
        const getStatusScore = (item) => {
          const stock = Number(item.currentStock) || 0;
          const minStock = item.minimumStock !== undefined ? Number(item.minimumStock) : 5;
          if (stock <= 0) return 0; // Out of Stock
          if (stock <= minStock) return 1; // Low Stock
          return 2; // In Stock
        };
        const scoreA = getStatusScore(a);
        const scoreB = getStatusScore(b);
        return sortDirection === "asc" ? scoreA - scoreB : scoreB - scoreA;
      });
    }

    return items;
  }, [inventoryItems, selectedRack, activeFilters, activeSpecFilters, sortColumn, sortDirection]);

  const categoriesList = useMemo(() => {
    const set = new Set();
    if (Array.isArray(inventoryItems)) {
      inventoryItems.forEach(item => {
        if (item.category) set.add(item.category);
      });
    }
    return Array.from(set).sort();
  }, [inventoryItems]);

  const manufacturersList = useMemo(() => {
    const set = new Set();
    if (Array.isArray(inventoryItems)) {
      inventoryItems.forEach(item => {
        if (item.manufacturer) set.add(item.manufacturer);
      });
    }
    return Array.from(set).sort();
  }, [inventoryItems]);

  // eslint-disable-next-line no-unused-vars
  const statCards = useMemo(() => ([
    { label: "Available Stock", value: summary.totals?.netStock || 0, tone: "emerald" },
    { label: "Purchases", value: counts.purchaseCount, tone: "sky" },
    { label: "Sales", value: counts.saleCount, tone: "rose" },
    { label: "Orders", value: counts.orderCount, tone: "amber" }
  ]), [counts, summary]);

  const isSupplierEntry = form.entryType === "purchase" || form.entryType === "order";
  const hasLineDiscount = true;
  const counterpartyPlaceholder = isSupplierEntry
    ? "Type supplier name"
    : "Type customer name";

  const subTotal = useMemo(
    () => form.entries.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0),
    [form.entries]
  );
  const sgstPercent = Number(form.sgst) || 0;
  const cgstPercent = Number(form.cgst) || 0;
  const igstPercent = Number(form.igst) || 0;
  const gstPercent = isIntegratedGstType(form.gstType) ? igstPercent : sgstPercent + cgstPercent;
  const gstAmount = useMemo(
    () => Number((subTotal * gstPercent / 100).toFixed(2)),
    [subTotal, gstPercent]
  );
  const grandTotal = useMemo(
    () => Number((subTotal + gstAmount).toFixed(2)),
    [subTotal, gstAmount]
  );
  const roundOffEnabled = form.roundOffEnabled === "yes";
  const finalTotal = useMemo(
    () => roundOffEnabled ? Math.round(grandTotal) : grandTotal,
    [grandTotal, roundOffEnabled]
  );
  const roundOffAmount = useMemo(
    () => Number((finalTotal - grandTotal).toFixed(2)),
    [finalTotal, grandTotal]
  );
  const advanceAmount = Number(form.advanceAmount) || 0;
  const balanceAmount = useMemo(
    () => Number(Math.max(finalTotal - advanceAmount, 0).toFixed(2)),
    [advanceAmount, finalTotal]
  );
  const entryPage = isEntryMode
    ? (entryTypeFilter === "sale" && salesDocumentVariant === "performa"
      ? entryPageConfig.salePerforma
      : entryPageConfig[entryTypeFilter])
    : null;
  // eslint-disable-next-line no-unused-vars
  const selectedSupplierAddress = useMemo(() => {
    if (form.entryType !== "order") return "";

    const selectedSupplier = suppliers.find((supplier) => supplier._id === form.dealerId)
      || suppliers.find((supplier) =>
        [supplier.companyName, supplier.name, supplier.dealerId, supplier.supplierCode]
          .filter(Boolean)
          .some((entry) => entry.toLowerCase() === form.counterpartyName.trim().toLowerCase())
      );

    return formatCounterpartyAddress(selectedSupplier);
  }, [form.counterpartyName, form.dealerId, form.entryType, suppliers]);
  // eslint-disable-next-line no-unused-vars
  const selectedCustomerBillingAddress = useMemo(() => {
    if (form.entryType !== "sale") return "";

    const selectedCustomer = customers.find((customer) => customer._id === form.customerId)
      || customers.find((customer) =>
        [customer.name, customer.customerId]
          .filter(Boolean)
          .some((entry) => entry.toLowerCase() === form.counterpartyName.trim().toLowerCase())
      );

    return formatAddressObject(selectedCustomer?.billingAddress || selectedCustomer?.address);
  }, [customers, form.counterpartyName, form.customerId, form.entryType]);
  const pageOverline = entryPage?.overline || "Material Stock";
  const pageTitle = entryPage?.title || "Material Stocks";
  const primaryPdfLabel = salesDocumentVariant === "performa" ? "Performa PDF" : "PDF";
  // eslint-disable-next-line no-unused-vars
  const heroClassName = entryPage?.accent === "sky"
    ? "rounded-[2rem] border border-sky-200 bg-[linear-gradient(135deg,_#eff6ff,_#ffffff,_#f0f9ff)] p-4 shadow-sm sm:p-6 lg:p-8"
    : entryPage?.accent === "rose"
    ? "rounded-[2rem] border border-rose-200 bg-[linear-gradient(135deg,_#fff1f2,_#ffffff,_#fff7ed)] p-4 shadow-sm sm:p-6 lg:p-8"
    : entryPage?.accent === "fuchsia"
    ? "rounded-[2rem] border border-fuchsia-200 bg-[linear-gradient(135deg,_#fdf4ff,_#ffffff,_#faf5ff)] p-4 shadow-sm sm:p-6 lg:p-8"
    : entryPage?.accent === "amber"
    ? "rounded-[2rem] border border-amber-200 bg-[linear-gradient(135deg,_#fffbeb,_#ffffff,_#fff7ed)] p-4 shadow-sm sm:p-6 lg:p-8"
    : "rounded-[2rem] border border-amber-200 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.18),_transparent_34%),linear-gradient(135deg,_#fff7ed,_#ffffff,_#fffbeb)] p-4 shadow-sm sm:p-6 lg:p-8";
  // eslint-disable-next-line no-unused-vars
  const overlineClassName = entryPage?.accent === "sky"
    ? "text-sm font-semibold uppercase tracking-[0.28em] text-sky-700"
    : entryPage?.accent === "rose"
    ? "text-sm font-semibold uppercase tracking-[0.28em] text-rose-700"
    : entryPage?.accent === "fuchsia"
    ? "text-sm font-semibold uppercase tracking-[0.28em] text-fuchsia-700"
    : entryPage?.accent === "amber"
    ? "text-sm font-semibold uppercase tracking-[0.28em] text-amber-700"
    : "text-sm font-semibold uppercase tracking-[0.28em] text-amber-700";
  // eslint-disable-next-line no-unused-vars
  const entryBadgeClassName = entryPage?.accent === "sky"
    ? "mt-4 inline-flex rounded-full bg-sky-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-800"
    : entryPage?.accent === "rose"
    ? "mt-4 inline-flex rounded-full bg-rose-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-rose-800"
    : entryPage?.accent === "fuchsia"
    ? "mt-4 inline-flex rounded-full bg-fuchsia-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-fuchsia-800"
    : "mt-4 inline-flex rounded-full bg-amber-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-800";

  return (
    <div className="mobile-dense-page space-y-3 overflow-x-hidden p-3 bg-slate-50 min-h-screen text-slate-800">
      {isEntryMode ? (
        <section className="rounded-[4px] border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <nav className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
                ACCOUNTS / {
                  salesDocumentVariant === "performa"
                    ? "PROFORMA INVOICES"
                    : entryTypeFilter === "sale"
                    ? "SALES INVOICES"
                    : entryTypeFilter === "order"
                    ? "PURCHASE ORDERS"
                    : "PURCHASE INVOICES"
                }
              </nav>
              <h1 className="text-lg font-bold text-slate-900 mt-0.5 flex items-center gap-1.5">
                <span className="text-blue-600">📄</span> {
                  showEntryForm
                    ? (editingEntryId ? `Edit ${pageOverline}` : `Create ${pageOverline}`)
                    : pageTitle
                }
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {showEntryForm ? (
                <>
                  <button
                    type="submit"
                    form="material-stock-form"
                    disabled={saving}
                    className="rounded-[4px] bg-blue-600 hover:bg-blue-700 px-4 py-1.5 text-sm font-semibold text-white transition disabled:opacity-60 h-9 flex items-center justify-center gap-1.5 min-w-[130px]"
                  >
                    <span>💾</span> {saving ? "Saving..." : editingEntryId ? `Update ${pageOverline}` : `Save ${pageOverline}`}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 px-4 py-1.5 text-sm font-semibold text-slate-700 transition h-9 flex items-center justify-center gap-1.5"
                  >
                    <span>✕</span> Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditingEntryId("");
                    setForm(buildEmptyFormForEntryType(entryTypeFilter));
                    setShowEntryForm(true);
                  }}
                  className="rounded-[4px] bg-blue-600 hover:bg-blue-700 px-4 py-1.5 text-sm font-semibold text-white transition h-9 flex items-center justify-center"
                >
                  + Add {pageOverline}
                </button>
              )}
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-white border border-stone-200 rounded-3xl p-6 md:p-8 shadow-sm">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-widest block">Industrial ERP</span>
              <h1 className="mt-1 text-3xl font-bold text-[#1E3A8A] tracking-tight">Inventory Management</h1>
              <p className="mt-2 text-stone-600 text-sm max-w-xl">
                Manage stock materials, rack locations, stock movements, and low stock alerts.
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={openAddMaterialModal}
                className="inline-flex items-center gap-2 rounded-xl bg-[#1E3A8A] hover:bg-[#152e72] px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition duration-200 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add Material
              </button>
              <button
                type="button"
                onClick={openStockInModal}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition duration-200 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 13l-7 7-7-7m14-6l-7 7-7-7" />
                </svg>
                Stock In
              </button>
              <button
                type="button"
                onClick={openStockOutModal}
                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 hover:bg-rose-700 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition duration-200 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 11l7-7 7 7m-14 6l7-7 7 7" />
                </svg>
                Stock Out
              </button>
              <button
                type="button"
                onClick={openImportModal}
                className="inline-flex items-center gap-2 rounded-xl border border-stone-300 bg-white hover:bg-stone-50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-stone-700 transition duration-200"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import Excel
              </button>
              <button
                type="button"
                onClick={exportToExcel}
                className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-white hover:bg-emerald-50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-emerald-700 transition duration-200"
              >
                📊 Export Excel
              </button>
              <button
                type="button"
                onClick={exportToCSV}
                className="inline-flex items-center gap-2 rounded-xl border border-teal-300 bg-white hover:bg-teal-50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-teal-700 transition duration-200"
              >
                📝 Export CSV
              </button>
              <button
                type="button"
                onClick={openAvailableStockPdf}
                className="inline-flex items-center gap-2 rounded-xl border border-stone-300 bg-white hover:bg-stone-50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-stone-700 transition duration-200"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export PDF
              </button>
            </div>
          </div>
          
        </section>
      )}

      {!isEntryMode ? (
        <>
          {/* KPI Dashboard Statistics */}
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {/* Total Materials */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm flex items-center justify-between hover:shadow-md transition duration-200">
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Total Materials</p>
                <h3 className="text-2xl font-bold text-stone-900 mt-2">{dashboardStats.totalMaterials || inventoryItems.length}</h3>
                <p className="text-[10px] text-emerald-600 font-semibold mt-2">
                  Active items in catalog
                </p>
              </div>
              <div className="p-3 bg-blue-50 text-[#1E3A8A] rounded-xl">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            </div>

            {/* Total Stock Quantity */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm flex items-center justify-between hover:shadow-md transition duration-200">
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Total Stock Qty</p>
                <h3 className="text-2xl font-bold text-stone-900 mt-2">{dashboardStats.totalStockQuantity}</h3>
                <p className="text-[10px] text-stone-500 mt-2">
                  Across all materials
                </p>
              </div>
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>

            {/* Low Stock Items */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm flex items-center justify-between hover:shadow-md transition duration-200">
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Low Stock Items</p>
                <h3 className={`text-2xl font-bold mt-2 ${dashboardStats.lowStockItems > 0 ? "text-amber-600" : "text-stone-900"}`}>{dashboardStats.lowStockItems}</h3>
                <p className={`text-[10px] font-semibold mt-2 ${dashboardStats.lowStockItems > 0 ? "text-amber-600 animate-pulse" : "text-emerald-600"}`}>
                  {dashboardStats.lowStockItems > 0 ? "⚠️ Reorder suggested" : "✓ Stock levels healthy"}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-amber-50 text-amber-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>

            {/* Out of Stock Items */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm flex items-center justify-between hover:shadow-md transition duration-200">
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Out of Stock</p>
                <h3 className={`text-2xl font-bold mt-2 ${dashboardStats.outOfStockItems > 0 ? "text-rose-600" : "text-stone-900"}`}>{dashboardStats.outOfStockItems}</h3>
                <p className={`text-[10px] font-semibold mt-2 ${dashboardStats.outOfStockItems > 0 ? "text-rose-600 animate-pulse" : "text-emerald-600"}`}>
                  {dashboardStats.outOfStockItems > 0 ? "🚫 Critical stockout" : "✓ No stockouts"}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${dashboardStats.outOfStockItems > 0 ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
            </div>

            {/* Today's Stock In */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm flex items-center justify-between hover:shadow-md transition duration-200">
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Today's Stock In</p>
                <h3 className="text-2xl font-bold text-stone-900 mt-2">+{dashboardStats.todayStockIn}</h3>
                <p className="text-[10px] text-stone-500 mt-2">
                  Items received today
                </p>
              </div>
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 17l-4 4m0 0l-4-4m4 4V3" />
                </svg>
              </div>
            </div>

            {/* Today's Stock Out */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm flex items-center justify-between hover:shadow-md transition duration-200">
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Today's Stock Out</p>
                <h3 className="text-2xl font-bold text-stone-900 mt-2">-{dashboardStats.todayStockOut}</h3>
                <p className="text-[10px] text-stone-500 mt-2">
                  Items issued today
                </p>
              </div>
              <div className="p-3 bg-rose-50 text-rose-600 rounded-xl">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7l4-4m0 0l4 4m-4-4v18" />
                </svg>
              </div>
            </div>
          </section>

          {/* Advanced Compact Filters */}
          <section className="bg-white border border-stone-200 rounded-3xl p-5 shadow-sm">
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-10 items-end">
              <div>
                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">Search Material</label>
                <input
                  type="text"
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  placeholder="Name, part no..."
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">Stock Type</label>
                <select
                  value={localStockCategory}
                  onChange={(e) => setLocalStockCategory(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A]"
                >
                  <option value="">All Stock Types</option>
                  <option value="sales">Sales</option>
                  <option value="consumable">Consumable</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">Category</label>
                <select
                  value={localCategory}
                  onChange={(e) => setLocalCategory(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A]"
                >
                  <option value="">All Categories</option>
                  {categoriesList.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">Manufacturer</label>
                <select
                  value={localManufacturer}
                  onChange={(e) => setLocalManufacturer(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A]"
                >
                  <option value="">All Manufacturers</option>
                  {manufacturersList.map(mfg => <option key={mfg} value={mfg}>{mfg}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">Voltage</label>
                <select
                  value={localVoltage}
                  onChange={(e) => setLocalVoltage(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A]"
                >
                  <option value="">All Voltages</option>
                  {voltagesList.map(voltage => <option key={voltage} value={voltage}>{voltage}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">Location / Area</label>
                <select
                  value={localRack}
                  onChange={(e) => setLocalRack(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A]"
                >
                  <option value="">All Locations</option>
                  {racksList.map(rack => <option key={rack} value={rack}>{rack}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">Stock Status</label>
                <select
                  value={localStatus}
                  onChange={(e) => setLocalStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A]"
                >
                  <option value="">All Statuses</option>
                  <option value="in_stock">In Stock</option>
                  <option value="low_stock">Low Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                  <option value="archived">Archived Items</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">From Date</label>
                <input
                  type="date"
                  value={localDateFrom}
                  onChange={(e) => setLocalDateFrom(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">To Date</label>
                <input
                  type="date"
                  value={localDateTo}
                  onChange={(e) => setLocalDateTo(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                />
              </div>
            </div>

            
            <div className="flex justify-end gap-2 mt-4 border-t border-stone-100 pt-4">
              <button
                type="button"
                onClick={clearAllFilters}
                className="px-4 py-2 rounded-xl border border-stone-300 hover:bg-stone-50 text-xs font-semibold text-stone-600 transition"
              >
                Clear Filters
              </button>
              <button
                type="button"
                onClick={applyFilters}
                className="px-4 py-2 rounded-xl bg-[#1E3A8A] hover:bg-[#152e72] text-xs font-semibold text-white transition shadow-sm"
              >
                Apply Filters
              </button>
            </div>
          </section>

          {/* Location / Area Panel */}
          <section className="bg-white border border-stone-200 rounded-3xl p-6 shadow-sm">
            <div className="mb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-md font-bold text-stone-900">Storage Locations</h2>
                <p className="text-xs text-stone-500">View and manage items by storage area and rack group.</p>
              </div>
            </div>

            {/* Search, Filter, and Action Controls */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 border-b border-stone-100 pb-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedAreaFilter("")}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition ${selectedAreaFilter === "" ? "bg-[#1E3A8A] text-white border-[#1E3A8A] shadow-sm" : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"}`}
                >
                  All Areas
                </button>
                {storageAreas.map(area => (
                  <button
                    key={area._id}
                    type="button"
                    onClick={() => setSelectedAreaFilter(area._id)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition ${selectedAreaFilter === area._id ? "bg-[#1E3A8A] text-white border-[#1E3A8A] shadow-sm" : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"}`}
                  >
                    Area {area.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <div className="relative w-full md:w-60">
                  <svg className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search racks (e.g. A1)..."
                    value={rackSearchQuery}
                    onChange={(e) => setRackSearchQuery(e.target.value)}
                    className="w-full text-xs pl-9 pr-4 py-2 border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A]"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setIsAddingArea(true)}
                  className="px-3.5 py-2 rounded-xl bg-stone-100 hover:bg-stone-200 text-xs font-bold text-stone-700 flex items-center gap-1.5 transition whitespace-nowrap"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Area
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIsAddingBatchRack(true);
                    if (storageAreas.length > 0) {
                      setBatchRackAreaId(storageAreas[0]._id);
                    }
                  }}
                  className="px-3.5 py-2 rounded-xl bg-[#1E3A8A] hover:bg-[#152e72] text-xs font-bold text-white flex items-center gap-1.5 transition whitespace-nowrap"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Racks
                </button>
              </div>
            </div>

            {/* Hierarchical Display of Areas and Racks */}
            <div className="space-y-6">
              {areaLoading || locationLoading ? (
                <div className="flex items-center justify-center py-8 text-stone-500 text-xs gap-2">
                  <svg className="animate-spin h-5 w-5 text-[#1E3A8A]" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Loading storage locations...</span>
                </div>
              ) : storageAreas.length === 0 ? (
                <div className="text-center py-8 text-stone-500 text-xs border border-dashed border-stone-200 rounded-2xl bg-stone-50/50">
                  No storage areas defined. Click "Add Area" above to get started.
                </div>
              ) : (
                (() => {
                  const filteredAreas = storageAreas.filter(area => selectedAreaFilter === "" || area._id === selectedAreaFilter);
                  let hasVisibleRacks = false;

                  const content = filteredAreas.map(area => {
                    const areaRacks = storageLocations.filter(loc => {
                      const belongsToArea = loc.area && (loc.area._id === area._id || loc.area === area._id);
                      const matchesSearch = rackSearchQuery.trim() === "" || loc.name.toLowerCase().includes(rackSearchQuery.toLowerCase());
                      return belongsToArea && matchesSearch;
                    });

                    if (rackSearchQuery.trim() !== "" && areaRacks.length === 0) {
                      return null;
                    }

                    hasVisibleRacks = true;

                    return (
                      <div key={area._id} className="border border-stone-100 rounded-2xl p-4 bg-stone-50/30">
                        <div className="flex items-center justify-between mb-3 px-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-stone-700 uppercase tracking-wider">Area {area.name}</span>
                            <span className="text-[10px] text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full font-medium">
                              {areaRacks.length} Racks
                            </span>
                          </div>
                          {areaRacks.length === 0 && (
                            <button
                              type="button"
                              onClick={() => handleDeleteArea(area._id, area.name)}
                              className="text-stone-400 hover:text-rose-600 text-[10px] font-semibold transition"
                            >
                              Delete Area
                            </button>
                          )}
                        </div>

                        {areaRacks.length === 0 ? (
                          <div className="text-stone-400 text-[11px] italic py-4 px-2">
                            No racks in this area. Generate some using "Add Racks" above.
                          </div>
                        ) : (
                          <div className="overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-stone-200">
                            <div className="flex gap-4">
                              {areaRacks.map(loc => {
                                const name = loc.name;
                                const stats = rackStats[name] || { total: 0, lowStock: 0 };
                                const isSelected = selectedRack === name;

                                return (
                                  <div
                                    key={loc._id}
                                    className={`flex-shrink-0 w-44 rounded-2xl p-4 border transition duration-200 relative flex flex-col justify-between ${isSelected ? "border-[#1E3A8A] bg-blue-50/50 shadow-sm" : "border-stone-200 bg-white hover:border-stone-300"}`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <button
                                        type="button"
                                        onClick={() => setSelectedRack(isSelected ? "" : name)}
                                        className="text-left flex-grow focus:outline-none"
                                      >
                                        <span className="text-xs font-bold text-[#1E3A8A] block truncate pr-6">{name}</span>
                                        {stats.lowStock > 0 && (
                                          <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold bg-rose-50 text-rose-800 animate-pulse">
                                            {stats.lowStock} Low
                                          </span>
                                        )}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteLocation(loc._id, name);
                                        }}
                                        title="Delete rack"
                                        className="text-stone-400 hover:text-rose-600 p-1 rounded-lg hover:bg-stone-100 transition absolute top-2 right-2"
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>

                                    <div className="mt-4 flex items-end justify-between font-semibold">
                                      <button
                                        type="button"
                                        onClick={() => setSelectedRack(isSelected ? "" : name)}
                                        className="text-left focus:outline-none"
                                      >
                                        <p className="text-lg font-bold text-stone-900">{stats.total}</p>
                                        <p className="text-[9px] text-stone-500 uppercase tracking-wider font-semibold">Items</p>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setSelectedRack(isSelected ? "" : name)}
                                        className="text-[10px] text-blue-600 font-bold focus:outline-none"
                                      >
                                        {isSelected ? "Clear" : "View"}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  });

                  if (!hasVisibleRacks && rackSearchQuery.trim() !== "") {
                    return (
                      <div className="text-center py-8 text-stone-500 text-xs border border-dashed border-stone-200 rounded-2xl bg-stone-50/50">
                        No racks match your search query "{rackSearchQuery}".
                      </div>
                    );
                  }

                  return content;
                })()
              )}
            </div>

            {/* Add Area Dialog Modal */}
            {isAddingArea && (
              <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
                <div className="bg-white rounded-3xl p-6 w-full max-w-sm border border-stone-200 shadow-xl">
                  <h3 className="text-md font-bold text-stone-900 mb-2">Create New Storage Area</h3>
                  <p className="text-xs text-stone-500 mb-4">Specify a letter or name for the new storage area (e.g. D, E, Warehouse).</p>
                  <input
                    type="text"
                    placeholder="e.g. D"
                    value={newAreaName}
                    onChange={(e) => setNewAreaName(e.target.value)}
                    className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] mb-4"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddArea();
                      if (e.key === "Escape") {
                        setIsAddingArea(false);
                        setNewAreaName("");
                      }
                    }}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingArea(false);
                        setNewAreaName("");
                      }}
                      className="px-4 py-2 text-xs font-semibold text-stone-600 border border-stone-200 rounded-xl hover:bg-stone-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddArea}
                      className="px-4 py-2 text-xs font-bold text-white bg-[#1E3A8A] hover:bg-[#152e72] rounded-xl transition"
                    >
                      Save Area
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Add Racks Dialog Modal */}
            {isAddingBatchRack && (
              <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
                <div className="bg-white rounded-3xl p-6 w-full max-w-md border border-stone-200 shadow-xl">
                  <h3 className="text-md font-bold text-stone-900 mb-2">Generate Multiple Racks</h3>
                  <p className="text-xs text-stone-500 mb-4">Automatically generate sequential racks (e.g., A1–A20) under a storage area.</p>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-xs font-semibold text-stone-700 mb-1">Select Storage Area</label>
                      {storageAreas.length === 0 ? (
                        <p className="text-xs text-rose-600">Please create an area first.</p>
                      ) : (
                        <select
                          value={batchRackAreaId}
                          onChange={(e) => setBatchRackAreaId(e.target.value)}
                          className="w-full text-sm px-4 py-2.5 border border-stone-200 bg-white rounded-xl outline-none focus:border-[#1E3A8A]"
                        >
                          <option value="">-- Select Area --</option>
                          {storageAreas.map(area => (
                            <option key={area._id} value={area._id}>Area {area.name}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-stone-700 mb-1">Start Number</label>
                        <input
                          type="number"
                          min="1"
                          value={batchRackStartNum}
                          onChange={(e) => setBatchRackStartNum(parseInt(e.target.value, 10) || 1)}
                          className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-stone-700 mb-1">End Number</label>
                        <input
                          type="number"
                          min="1"
                          value={batchRackEndNum}
                          onChange={(e) => setBatchRackEndNum(parseInt(e.target.value, 10) || 1)}
                          className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A]"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingBatchRack(false);
                        setBatchRackAreaId("");
                        setBatchRackStartNum(1);
                        setBatchRackEndNum(10);
                      }}
                      className="px-4 py-2 text-xs font-semibold text-stone-600 border border-stone-200 rounded-xl hover:bg-stone-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={storageAreas.length === 0}
                      onClick={handleBatchAddLocations}
                      className="px-4 py-2 text-xs font-bold text-white bg-[#1E3A8A] hover:bg-[#152e72] rounded-xl disabled:bg-stone-300 disabled:cursor-not-allowed transition"
                    >
                      Generate Racks
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        </>
      ) : null}

      {isEntryMode ? (
        <section className="space-y-4">

          {showEntryForm ? (
          <form id="material-stock-form" onSubmit={handleSubmit} className="space-y-4">
            
            {/* Top Cards Grid */}
            <div className="grid gap-4 md:grid-cols-2">
              
              {/* Card A: Partner & Contact */}
              <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                  <span className="text-blue-600 text-sm">👥</span> {form.entryType === "sale" ? "Customer & Contact" : "Supplier & Contact"}
                </h2>
                
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-semibold text-slate-500">
                      {form.entryType === "sale" ? "Customer" : "Supplier"} <span className="text-red-500">*</span>
                    </label>
                    <ReactSelect
                      options={isSupplierEntry ? supplierOptions : customerOptions}
                      value={isSupplierEntry ? selectedSupplierOption : selectedCustomerOption}
                      styles={reactSelectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      onChange={(option) => {
                        if (option) {
                          applyCounterparty(option.data, isSupplierEntry ? "supplier" : "customer", option.label);
                        } else {
                          setForm((current) => ({
                            ...current,
                            counterpartyName: "",
                            dealerId: "",
                            customerId: "",
                            counterpartyPhone: "",
                            counterpartyEmail: "",
                            counterpartyGst: "",
                            supplierGst: "",
                            counterpartyAddress: ""
                          }));
                        }
                      }}
                      placeholder={counterpartyPlaceholder}
                      isClearable
                    />
                  </div>
                  <div>
                    <InputField
                      label="Phone"
                      name="counterpartyPhone"
                      value={form.counterpartyPhone}
                      onChange={handleHeaderChange}
                    />
                  </div>
                  <div>
                    <InputField
                      label="Email"
                      name="counterpartyEmail"
                      value={form.counterpartyEmail}
                      onChange={handleHeaderChange}
                    />
                  </div>
                  <div>
                    <InputField
                      label="GST No"
                      name={form.entryType === "order" ? "supplierGst" : "counterpartyGst"}
                      value={form.entryType === "order" ? form.supplierGst : form.counterpartyGst}
                      onChange={handleHeaderChange}
                    />
                  </div>
                  
                  {form.entryType === "sale" ? (
                    <>
                      <div>
                        <InputField
                          label="Buyer's Order No"
                          name="poNumber"
                          value={form.poNumber}
                          onChange={handleHeaderChange}
                        />
                      </div>
                      <div>
                        <InputField
                          label="Buyer's Order Date"
                          name="poDate"
                          value={form.poDate}
                          onChange={handleHeaderChange}
                          type="date"
                        />
                      </div>
                      <div>
                        <InputField
                          label="Reference No"
                          name="reference"
                          value={form.reference}
                          onChange={handleHeaderChange}
                        />
                      </div>
                      <div>
                        <InputField
                          label="Reference Date"
                          name="referenceDate"
                          value={form.referenceDate}
                          onChange={handleHeaderChange}
                          type="date"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <InputField
                          label="Reference No"
                          name="reference"
                          value={form.reference}
                          onChange={handleHeaderChange}
                        />
                      </div>
                      {form.entryType === "order" && (
                        <>
                          <div>
                            <InputField
                              label="Reference Date"
                              name="referenceDate"
                              value={form.referenceDate}
                              onChange={handleHeaderChange}
                              type="date"
                            />
                          </div>
                          <div>
                            <InputField
                              label="End User GST"
                              name="endUserGst"
                              value={form.endUserGst}
                              onChange={handleHeaderChange}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Card B: Document Details */}
              <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                  <span className="text-blue-600 text-sm">📄</span> {pageOverline} Details
                </h2>
                
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                  {form.entryType === "order" ? (
                    <>
                      <div>
                        <InputField
                          label="PO No"
                          name="poNumber"
                          value={form.poNumber}
                          onChange={handleHeaderChange}
                          placeholder="Auto-generated"
                        />
                      </div>
                      <div>
                        <InputField
                          label="PO Date"
                          name="transactionDate"
                          value={form.transactionDate}
                          onChange={handleHeaderChange}
                          type="date"
                        />
                      </div>
                      <div>
                        <InputField
                          label="Expected Delivery Date"
                          name="expectedDeliveryDate"
                          value={form.expectedDeliveryDate}
                          onChange={handleHeaderChange}
                          type="date"
                        />
                      </div>
                      <div>
                        <InputField
                          label="Pay Date"
                          name="payDate"
                          value={form.payDate}
                          onChange={handleHeaderChange}
                          type="date"
                          required
                        />
                      </div>
                      <div>
                        <InputField
                          label="Payment Terms"
                          name="payment"
                          value={form.payment}
                          onChange={handleHeaderChange}
                        />
                      </div>
                      <div>
                        <SelectField
                          label="Freight"
                          name="freightType"
                          value={form.freightType}
                          onChange={handleHeaderChange}
                          options={[
                            ["paid", "Paid"],
                            ["to_pay", "To Pay"]
                          ]}
                        />
                      </div>
                      <div>
                        <SelectField
                          label="Mode of Dispatch"
                          name="dispatchMode"
                          value={form.dispatchMode}
                          onChange={handleHeaderChange}
                          options={[
                            ["by_road", "By Road"],
                            ["by_air", "By Air"],
                            ["by_surface", "By Surface"],
                            ["other", "Other"]
                          ]}
                        />
                      </div>
                      {form.dispatchMode === "other" && (
                        <div>
                          <InputField
                            label="Other Dispatch Mode"
                            name="dispatchThrough"
                            value={form.dispatchThrough}
                            onChange={handleHeaderChange}
                            placeholder="Enter dispatch mode"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <InputField
                          label="Invoice No"
                          name="documentNumber"
                          value={form.documentNumber}
                          placeholder="Auto-generated"
                          onChange={handleHeaderChange}
                        />
                      </div>
                      <div>
                        <InputField
                          label="Invoice Date"
                          name="transactionDate"
                          value={form.transactionDate}
                          onChange={handleHeaderChange}
                          type="date"
                        />
                      </div>
                      <div>
                        <SelectField
                          label="Payment Account"
                          name="paymentMode"
                          value={form.paymentMode}
                          onChange={handleHeaderChange}
                          options={paymentModeOptions}
                        />
                      </div>
                      {form.entryType === "sale" && (
                        <>
                          <div>
                            <InputField
                              label="Delivery Note"
                              name="deliveryNote"
                              value={form.deliveryNote}
                              onChange={handleHeaderChange}
                            />
                          </div>
                          <div>
                            <InputField
                              label="Dispatch Through"
                              name="dispatchThrough"
                              value={form.dispatchThrough}
                              onChange={handleHeaderChange}
                            />
                          </div>
                          <div>
                            <InputField
                              label="E-Way Bill No"
                              name="ewayBillNo"
                              value={form.ewayBillNo}
                              onChange={handleHeaderChange}
                            />
                          </div>
                          <div>
                            <InputField
                              label="Motor Vehicle No"
                              name="motorVehicleNo"
                              value={form.motorVehicleNo}
                              onChange={handleHeaderChange}
                            />
                          </div>
                          <div>
                            <InputField
                              label="Other References"
                              name="otherReferences"
                              value={form.otherReferences}
                              onChange={handleHeaderChange}
                            />
                          </div>
                          <div>
                            <InputField
                              label="Dispatch Doc No"
                              name="dispatchDocNo"
                              value={form.dispatchDocNo}
                              onChange={handleHeaderChange}
                            />
                          </div>
                          <div>
                            <InputField
                              label="Delivery Note Date"
                              name="deliveryNoteDate"
                              value={form.deliveryNoteDate}
                              onChange={handleHeaderChange}
                              type="date"
                            />
                          </div>
                          <div>
                            <InputField
                              label="Destination"
                              name="destination"
                              value={form.destination}
                              onChange={handleHeaderChange}
                            />
                          </div>
                          <div>
                            <InputField
                              label="Bill of Lading/LR-RR No"
                              name="billOfLading"
                              value={form.billOfLading}
                              onChange={handleHeaderChange}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                  
                  {/* Quotation / Purchase Order selectors inside Card B */}
                  {form.entryType === "sale" && (
                    <div className="sm:col-span-2 rounded-[4px] border border-slate-200 bg-slate-50/50 p-2.5 space-y-1.5">
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Reference Quotation</div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <select
                          value={selectedQuotationId}
                          onChange={(event) => setSelectedQuotationId(event.target.value)}
                          className="w-full rounded-[4px] border border-slate-300 bg-white px-2 py-1 h-9 text-xs outline-none focus:border-blue-500 font-medium text-slate-700"
                        >
                          <option value="">Select quotation</option>
                          {quotations.map((quotation) => (
                            <option key={quotation._id} value={quotation._id}>
                              {(quotation.quotationNo || "Quotation")} - {quotation.customerName || "-"}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleObtainQuotationDetails}
                          className="rounded-[4px] border border-blue-300 bg-blue-50 hover:bg-blue-100/50 text-blue-700 px-3 py-1 text-xs font-semibold h-9 transition shrink-0"
                        >
                          Obtain Details
                        </button>
                      </div>
                    </div>
                  )}

                  {form.entryType === "purchase" && (
                    <div className="sm:col-span-2 rounded-[4px] border border-slate-200 bg-slate-50/50 p-2.5 space-y-1.5">
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Reference Purchase Order</div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <select
                          value={selectedPurchaseOrderId}
                          onChange={(event) => setSelectedPurchaseOrderId(event.target.value)}
                          className="w-full rounded-[4px] border border-slate-300 bg-white px-2 py-1 h-9 text-xs outline-none focus:border-blue-500 font-medium text-slate-700"
                        >
                          <option value="">Select purchase order</option>
                          {purchaseOrders.map((purchaseOrder) => (
                            <option key={purchaseOrder._id} value={purchaseOrder._id}>
                              {(purchaseOrder.poNumber || purchaseOrder.documentNumber || "Purchase Order")} - {purchaseOrder.counterpartyName || "-"}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleObtainPurchaseOrderDetails}
                          className="rounded-[4px] border border-blue-300 bg-blue-50 hover:bg-blue-100/50 text-blue-700 px-3 py-1 text-xs font-semibold h-9 transition shrink-0"
                        >
                          Obtain Details
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* SECTION 3: LINE ITEMS */}
            <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-3">
                <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <span className="text-blue-600 text-sm">🛒</span> {pageOverline} Items
                </h2>
                
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={addEntryRow}
                    className="rounded-[4px] border border-blue-300 bg-blue-50/50 hover:bg-blue-50 text-blue-700 px-3 py-1 text-xs font-semibold h-8 flex items-center gap-1 transition"
                  >
                    <span>+</span> Add Item
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm("Clear all item rows?")) {
                        setForm((current) => ({ ...current, entries: [buildEmptyItem()] }));
                      }
                    }}
                    className="rounded-[4px] border border-red-200 bg-red-50/30 hover:bg-red-50 text-red-600 px-3 py-1 text-xs font-semibold h-8 flex items-center gap-1 transition"
                  >
                    <span>🗑️</span> Clear All
                  </button>
                </div>
              </div>
              
              {/* Spreadsheet settings toolbar */}
              <div className="mb-2 flex flex-wrap items-center gap-4 bg-slate-50 p-2 border border-slate-200 rounded-[4px] text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-slate-500">Discount Type:</span>
                  <select
                    name="commonDiscountType"
                    value={form.commonDiscountType}
                    onChange={handleHeaderChange}
                    className="rounded-[4px] border border-slate-300 bg-white px-2 py-1 text-xs outline-none h-8 text-slate-700"
                  >
                    <option value="percent">Percent (%)</option>
                    <option value="amount">Amount</option>
                  </select>
                </div>
                <div className="flex items-center gap-1 text-[11px] text-slate-500 italic">
                  <span className="text-blue-500 font-bold">ⓘ</span>
                  <span>Unit Price auto-calculates from List Price.</span>
                </div>
              </div>

              {/* Grid-based table wrapper */}
              <div className="max-w-full overflow-x-auto pb-1">
                <div className="overflow-auto max-h-[600px] border border-slate-200 rounded-[4px] bg-white">
                  <table className="w-full min-w-[1200px] border-collapse text-left text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                        <th className="px-3 py-2 font-semibold border-r border-slate-100">S.No</th>
                        <th className="px-3 py-2 font-semibold border-r border-slate-100">Item Code</th>
                        <th className="px-3 py-2 font-semibold border-r border-slate-100">HSN Code</th>
                        <th className="px-3 py-2 font-semibold border-r border-slate-100">Material Description</th>
                        <th className="px-3 py-2 font-semibold border-r border-slate-100 text-right">Qty</th>
                        <th className="px-3 py-2 font-semibold border-r border-slate-100">Unit</th>
                        {form.entryType === "purchase" ? <th className="px-3 py-2 font-semibold border-r border-slate-100">Stock Type</th> : null}
                        {form.entryType === "purchase" ? <th className="px-3 py-2 font-semibold border-r border-slate-100">Location</th> : null}
                        {hasLineDiscount ? <th className="px-3 py-2 font-semibold border-r border-slate-100 text-right">List Price</th> : <th className="px-3 py-2 font-semibold border-r border-slate-100 text-right">Price</th>}
                        {hasLineDiscount ? <th className="px-3 py-2 font-semibold border-r border-slate-100 text-right">Discount</th> : null}
                        {hasLineDiscount ? <th className="px-3 py-2 font-semibold border-r border-slate-100 text-right">Unit Price</th> : null}
                        <th className="px-3 py-2 font-semibold border-r border-slate-100 text-right">Total</th>
                        <th className="px-3 py-2 font-semibold text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.entries.map((item, index) => (
                        <tr key={`entry-row-${index}`} className="border-b border-slate-200 hover:bg-slate-50/50 align-top">
                          <td className="px-3 py-2 text-center text-xs font-semibold text-slate-500 border-r border-slate-100">
                            {index + 1}
                          </td>
                          <td className="px-2 py-1.5 border-r border-slate-100">
                            <input
                              value={item.itemCode}
                              onChange={(value) => updateEntryRowWithAutofill(index, "itemCode", value.target.value)}
                              onBlur={(event) => applySavedMaterial(index, event.target.value, "itemCode")}
                              placeholder="Item code"
                              list="material-suggestions"
                              className="w-full rounded-[4px] border border-slate-200 px-2 py-1 text-xs outline-none focus:border-blue-500 focus:bg-blue-50/10 bg-white h-8 text-slate-800 font-mono uppercase"
                            />
                          </td>
                          <td className="px-2 py-1.5 border-r border-slate-100">
                            <input
                              value={item.hsnCode}
                              onChange={(value) => updateEntryRow(index, "hsnCode", value.target.value)}
                              placeholder="HSN"
                              className="w-full rounded-[4px] border border-slate-200 px-2 py-1 text-xs outline-none focus:border-blue-500 focus:bg-blue-50/10 bg-white h-8 text-slate-800"
                            />
                          </td>
                          <td className="px-2 py-1.5 border-r border-slate-100">
                            <div className="rounded-[4px] border border-slate-200 overflow-hidden bg-white">
                              <input
                                value={item.itemName}
                                onChange={(e) => updateEntryRowWithAutofill(index, "itemName", e.target.value)}
                                onBlur={(event) => applySavedMaterial(index, event.target.value, "itemName")}
                                placeholder="Material"
                                list="material-suggestions"
                                className="w-full border-0 border-b border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:bg-slate-50 text-slate-800 h-8"
                              />
                              <input
                                value={item.description}
                                onChange={(e) => updateEntryRow(index, "description", e.target.value)}
                                placeholder="Description"
                                className="w-full border-0 bg-white px-2 py-1 text-xs outline-none focus:bg-slate-50 text-slate-800 h-8"
                              />
                            </div>
                          </td>
                          <td className="px-2 py-1.5 border-r border-slate-100">
                            <input
                              value={item.quantity}
                              onChange={(value) => updateEntryRow(index, "quantity", value.target.value)}
                              type="number"
                              min="0.01"
                              step="0.01"
                              placeholder="0"
                              className="w-full rounded-[4px] border border-slate-200 px-2 py-1 text-xs outline-none focus:border-blue-500 focus:bg-blue-50/10 bg-white h-8 text-right font-medium text-slate-800"
                            />
                          </td>
                          <td className="px-2 py-1.5 border-r border-slate-100">
                            <select
                              value={formatUnit(item.unit)}
                              onChange={(event) => updateEntryRow(index, "unit", event.target.value)}
                              className="w-full rounded-[4px] border border-slate-200 bg-white px-1 h-8 text-xs outline-none focus:border-blue-500 font-medium text-slate-800"
                            >
                              {unitOptions.map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </td>
                          {form.entryType === "purchase" ? (
                            <td className="px-2 py-1.5 border-r border-slate-100">
                              <select
                                value={normalizeStockCategory(item.stockCategory)}
                                onChange={(event) => updateEntryRow(index, "stockCategory", event.target.value)}
                                className="w-full rounded-[4px] border border-slate-200 bg-white px-1 h-8 text-xs outline-none focus:border-blue-500 font-medium text-slate-800"
                              >
                                <option value="sales">For Sales</option>
                                <option value="consumable">Consumable</option>
                              </select>
                            </td>
                          ) : null}
                          {form.entryType === "purchase" ? (
                            <td className="px-2 py-1.5 border-r border-slate-100">
                              <input
                                value={item.location}
                                onChange={(value) => updateEntryRow(index, "location", value.target.value)}
                                placeholder="Location"
                                className="w-full rounded-[4px] border border-slate-200 px-2 py-1 text-xs outline-none focus:border-blue-500 focus:bg-blue-50/10 bg-white h-8 text-slate-800"
                              />
                            </td>
                          ) : null}
                          <td className="px-2 py-1.5 border-r border-slate-100">
                            <div className="flex items-center bg-white px-2 border border-slate-200 rounded-[4px] h-8 focus-within:border-blue-500 w-full">
                              <span className="text-slate-400 mr-0.5 text-xs">₹</span>
                              <input
                                value={hasLineDiscount ? item.listPrice : item.unitPrice}
                                onChange={(e) => updateEntryRow(index, hasLineDiscount ? "listPrice" : "unitPrice", e.target.value)}
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                className="w-full border-0 p-0 text-right outline-none text-xs text-slate-800 font-medium bg-white"
                              />
                            </div>
                          </td>
                          {hasLineDiscount ? (
                            <td className="px-2 py-1.5 border-r border-slate-100">
                              <div className="flex items-center bg-white px-2 border border-slate-200 rounded-[4px] h-8 focus-within:border-blue-500 w-full">
                                <input
                                  value={item.discountValue}
                                  onChange={(event) => updateEntryRow(index, "discountValue", event.target.value)}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="0.00"
                                  className="w-full border-0 p-0 text-right outline-none text-xs text-slate-800 font-medium bg-white"
                                />
                                <span className="text-slate-400 ml-1 text-xs">
                                  {form.commonDiscountType === "amount" ? "" : "%"}
                                </span>
                              </div>
                            </td>
                          ) : null}
                          {hasLineDiscount ? (
                            <td className="px-2 py-1.5 border-r border-slate-100">
                              <div className="flex items-center bg-slate-50 px-2 border border-slate-200 rounded-[4px] h-8 w-full">
                                <span className="text-slate-400 mr-0.5 text-xs">₹</span>
                                <input
                                  value={item.unitPrice}
                                  onChange={() => {}}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="0.00"
                                  readOnly
                                  className="w-full border-0 p-0 text-right outline-none text-xs text-slate-800 font-medium bg-slate-50"
                                />
                              </div>
                            </td>
                          ) : null}
                          <td className="px-2 py-1.5 border-r border-slate-100">
                            <div className="flex items-center bg-slate-50 px-2 border border-slate-200 rounded-[4px] h-8 w-full">
                              <span className="text-slate-400 mr-0.5 text-xs font-bold">₹</span>
                              <input
                                value={item.totalAmount}
                                onChange={hasLineDiscount ? () => {} : (e) => updateEntryRow(index, "totalAmount", e.target.value)}
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                readOnly={hasLineDiscount}
                                className="w-full border-0 p-0 text-right outline-none text-xs font-bold text-slate-900 bg-slate-50"
                              />
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                type="button"
                                disabled={index === 0}
                                onClick={() => moveEntryRow(index, "up")}
                                className="text-slate-400 hover:text-slate-700 disabled:opacity-30 transition"
                                title="Move row up"
                              >
                                <span className="text-xs">▲</span>
                              </button>
                              <button
                                type="button"
                                disabled={index === form.entries.length - 1}
                                onClick={() => moveEntryRow(index, "down")}
                                className="text-slate-400 hover:text-slate-700 disabled:opacity-30 transition"
                                title="Move row down"
                              >
                                <span className="text-xs">▼</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => removeEntryRow(index)}
                                className="text-red-400 hover:text-red-600 transition"
                                title="Remove row"
                              >
                                <span className="text-xs">🗑️</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  <datalist id="material-suggestions">
                    {materialSuggestions.map((value, index) => (
                      <option key={`${value}-${index}`} value={value} />
                    ))}
                  </datalist>
                </div>

                {/* Add New Row button centered inside container */}
                <div className="p-2 border-t border-slate-200 bg-white flex justify-center">
                  <button
                    type="button"
                    onClick={addEntryRow}
                    className="w-full max-w-xs border border-dashed border-blue-300 rounded-[4px] py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50/50 transition flex items-center justify-center gap-1"
                  >
                    <span>+</span> Add New Row
                  </button>
                </div>

                {/* Bottom Spreadsheet Info Bar */}
                <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold text-slate-600">
                  <div>Total Items: {form.entries.length}</div>
                  <div className="flex items-center gap-8">
                    <span>Sub Total (Before Tax)</span>
                    <span className="font-bold text-slate-800">₹{formatCurrency(subTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Area: Grid for Address & Notes (left) + Summary & Settings (right) */}
            <div className="grid gap-4 lg:grid-cols-12 items-start">
              
              {/* Left Column: Billing/Shipping Address & Notes / Terms */}
              <div className="lg:col-span-8 space-y-4">
                <div className="grid gap-4 md:grid-cols-5 items-stretch">
                  
                  {/* SECTION 5A: BILLING & SHIPPING ADDRESS */}
                  <div className="md:col-span-2 rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                    <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                      <span className="text-blue-600 text-sm">📍</span> {form.entryType === "order" ? "Dispatch Destination" : "Billing & Shipping Address"}
                    </h2>
                    <div className="space-y-3">
                      {form.entryType === "sale" ? (
                        <SelectField
                          label="Consignee (Ship to)"
                          name="deliveryAddressType"
                          value={form.deliveryAddressType}
                          onChange={handleHeaderChange}
                          options={[
                            ["work_address", "Customer Work / Delivery Address"],
                            ["billing_address", "Billing Address"],
                            ["other", "Other"]
                          ]}
                        />
                      ) : form.entryType === "order" ? (
                        <SelectField
                          label="Delivery Address Type"
                          name="deliveryAddressType"
                          value={form.deliveryAddressType}
                          onChange={handleHeaderChange}
                          options={[
                            ["genmech_service", "Genmech Service"],
                            ["other", "Other"]
                          ]}
                        />
                      ) : null}

                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-500">Address Details</label>
                        <textarea 
                          name={form.deliveryAddressType === "other" ? "otherDeliveryAddress" : "counterpartyAddress"}
                          value={form.deliveryAddressType === "other" ? form.otherDeliveryAddress : form.counterpartyAddress}
                          onChange={handleHeaderChange}
                          rows={4} 
                          readOnly={form.deliveryAddressType !== "other"}
                          placeholder={form.deliveryAddressType === "other" ? "Enter address..." : "Address auto-fills from contact profile..."}
                          className="w-full rounded-[4px] border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white resize-y min-h-[100px] text-slate-700 read-only:bg-slate-50" 
                        />
                      </div>
                    </div>
                  </div>

                  {/* SECTION 5B: NOTES / TERMS */}
                  <div className="md:col-span-3 rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                    <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                      <span className="text-blue-600 text-sm">📝</span> Notes / Terms
                    </h2>
                    <div className="space-y-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        {form.entryType !== "sale" && (
                          <InputField label="Delivery Time" name="deliveryTime" value={form.deliveryTime} onChange={handleHeaderChange} />
                        )}
                        <InputField label="Payment Terms" name="paymentTerms" value={form.paymentTerms} onChange={handleHeaderChange} />
                        <InputField label="Warranty" name="warranty" value={form.warranty} onChange={handleHeaderChange} />
                        <SelectField
                          label="GST Type"
                          name="gstType"
                          value={form.gstType}
                          onChange={handleHeaderChange}
                          options={[
                            ["cgst_sgst", "GST"],
                            ["igst", "IGST"],
                            ["sez_supply", "SEZ SUPPLY"]
                          ]}
                        />
                        {isIntegratedGstType(form.gstType) ? (
                          <InputField label="IGST %" name="igst" value={form.igst} onChange={handleHeaderChange} type="number" min="0" step="0.01" />
                        ) : (
                          <>
                            <InputField label="SGST %" name="sgst" value={form.sgst} onChange={handleHeaderChange} type="number" min="0" step="0.01" />
                            <InputField label="CGST %" name="cgst" value={form.cgst} onChange={handleHeaderChange} type="number" min="0" step="0.01" />
                          </>
                        )}
                        {form.entryType !== "order" && (
                          <SelectField
                            label="Round Off"
                            name="roundOffEnabled"
                            value={form.roundOffEnabled}
                            onChange={handleHeaderChange}
                            options={[
                              ["no", "No"],
                              ["yes", "Yes"]
                            ]}
                          />
                        )}
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-slate-500">Terms & Conditions</label>
                          <textarea name="terms" value={form.terms} onChange={handleHeaderChange} rows={2} className="w-full rounded-[4px] border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white resize-y h-[70px] text-slate-700" />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-slate-500">Notes / Remarks</label>
                          <textarea name="notes" value={form.notes} onChange={handleHeaderChange} rows={2} className="w-full rounded-[4px] border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white resize-y h-[70px] text-slate-700" />
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Right Column: Summary & Settings */}
              <div className="lg:col-span-4 space-y-4">
                
                {/* SECTION 4: FINANCIAL SUMMARY */}
                <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                  <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                    <span className="text-blue-600 text-sm">📊</span> {entryLabelMap[form.entryType] || "Document"} Summary
                  </h2>
                  <div className="space-y-2">
                    <SummaryRow label="Sub Total" value={`₹${formatCurrency(subTotal)}`} strong={true} />
                    
                    {/* Discount row in red if dynamic discounts are applied */}
                    <div className="flex items-center justify-between rounded-[4px] bg-white px-3 py-2 border border-slate-200 text-sm text-red-600 font-semibold">
                      <span>Discount</span>
                      <span>-₹0.00</span>
                    </div>

                    <SummaryRow label="After Discount" value={`₹${formatCurrency(subTotal)}`} />
                    <SummaryRow 
                      label={isIntegratedGstType(form.gstType) ? `${form.gstType === "sez_supply" ? "SEZ SUPPLY" : "IGST"} (${igstPercent}%)` : `GST (${gstPercent}%)`} 
                      value={`₹${formatCurrency(gstAmount)}`} 
                    />
                    <SummaryRow label="Others / OH" value="₹0.00" />

                    {form.entryType !== "order" && roundOffEnabled ? (
                      <>
                        <SummaryRow label="Round Off" value={`${roundOffAmount >= 0 ? "+" : ""}₹${formatCurrency(roundOffAmount)}`} />
                        <SummaryRow label="Rounded Total" value={`₹${formatCurrency(finalTotal)}`} />
                      </>
                    ) : null}
                    
                    <div className="flex items-center justify-between rounded-[4px] bg-blue-50/40 px-3 py-2.5 border border-blue-200 font-bold text-blue-700 text-base">
                      <span>Grand Total</span>
                      <span>₹{formatCurrency(finalTotal)}</span>
                    </div>

                    {form.entryType !== "order" ? (
                      <>
                        <div className="pt-2 border-t border-slate-100 space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-1/2">
                              <InputField
                                label={form.entryType === "sale" ? "Amount Received" : "Amount Paid"}
                                name="advanceAmount"
                                value={form.advanceAmount}
                                onChange={handleHeaderChange}
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </div>
                            <div className="w-1/2">
                              <label className="mb-1 block text-xs font-semibold text-slate-500">Balance Due</label>
                              <div className="flex items-center bg-slate-50 px-3 border border-slate-200 rounded-[4px] h-9 w-full text-slate-700 font-bold text-sm">
                                ₹{formatCurrency(balanceAmount)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="pt-2 border-t border-slate-100 space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-1/2">
                              <InputField
                                label="Amount Paying"
                                name="advanceAmount"
                                value={form.advanceAmount}
                                onChange={handleHeaderChange}
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </div>
                            <div className="w-1/2">
                              <label className="mb-1 block text-xs font-semibold text-slate-500">Balance Due</label>
                              <div className="flex items-center bg-slate-50 px-3 border border-slate-200 rounded-[4px] h-9 w-full text-slate-700 font-bold text-sm">
                                ₹{formatCurrency(balanceAmount)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Checkbox Settings Area */}
                <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                  <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                    <span className="text-blue-600 text-sm">⚙️</span> Settings
                  </h2>
                  <div className="flex flex-col gap-2">
                    <CheckboxInput
                      label="Show Discount In PDF"
                      name="showDiscountInPdf"
                      checked={Boolean(form.showDiscountInPdf)}
                      onChange={handleHeaderChange}
                    />
                  </div>
                </div>

              </div>

            </div>

          </form>
          ) : null}

        </section>
      ) : (
        <section className="bg-white border border-stone-200 rounded-3xl p-6 shadow-sm">
          <div className="mb-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-stone-100 pb-5">
            <div>
              <h2 className="text-xl font-bold text-[#1E3A8A]">Available Spares & Materials</h2>
              <p className="mt-1 text-xs text-stone-500">
                Current warehouse stock quantities, locations, and status alerts.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 md:justify-end">
              <div className="relative w-full max-w-xl">
                <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-stone-400">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Quick search by material code, name, specifications..."
                  className="w-full pl-12 pr-4 py-3 rounded-2xl border border-stone-300 focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] bg-stone-50 focus:bg-white outline-none transition text-base shadow-sm font-semibold text-stone-900 placeholder-stone-400"
                />
              </div>
              <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-xs font-bold shrink-0 text-center sm:text-left shadow-sm">
                Low Stock: {lowStockCount}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="rounded-2xl bg-stone-50 px-4 py-12 text-center text-stone-500 flex flex-col items-center justify-center gap-3">
              <svg className="animate-spin h-8 w-8 text-[#1E3A8A]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm font-medium">Loading inventory records...</span>
            </div>
          ) : filteredStockItems.length === 0 ? (
            <div className="rounded-2xl bg-stone-50 px-4 py-12 text-center text-stone-500">
              <p className="text-sm font-medium">No inventory materials match your active filters.</p>
              <button
                type="button"
                onClick={clearAllFilters}
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#1E3A8A] hover:underline"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              {/* Desktop View Table */}
              <div className="hidden md:block overflow-auto border border-stone-200 rounded-2xl max-h-[600px] scrollbar-thin scrollbar-thumb-stone-200">
                <table className="w-full min-w-[1240px] text-left border-collapse">
                  <thead className="bg-stone-50 text-stone-700 border-b border-stone-200 sticky top-0 z-10 text-xs font-bold uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-4">Material Name</th>
                      <th className="px-4 py-4">Voltage</th>
                      <th className="px-4 py-4">Stock Type</th>
                      <th className="px-4 py-4">Model No.</th>
                      <th className="px-4 py-4">Location / Area</th>
                      <th className="px-4 py-4 text-center">Unit</th>
                      <th className="px-4 py-4 text-right">Current Stock</th>
                      <th className="px-4 py-4 text-right">Min. Stock</th>
                      <th 
                        className="px-6 py-4 text-center cursor-pointer hover:bg-stone-100 select-none transition duration-150"
                        onClick={() => {
                          if (sortColumn !== "status") {
                            setSortColumn("status");
                            setSortDirection("asc");
                          } else if (sortDirection === "asc") {
                            setSortDirection("desc");
                          } else {
                            setSortColumn("");
                            setSortDirection("");
                          }
                        }}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span>Status</span>
                          <span className="text-[10px] text-stone-400 font-mono">
                            {sortColumn === "status" ? (sortDirection === "asc" ? " ▲" : " ▼") : " ↕"}
                          </span>
                        </div>
                      </th>
                      <th className="px-4 py-4">Last Updated</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 text-sm">
                    {filteredStockItems.map((item) => {
                      const materialKey = getInventoryMaterialKey(item);
                      return (
                        <tr key={`${item.productId}-${item.itemCode}`} className="hover:bg-stone-50/50 transition">
                          <td className="px-6 py-4 font-semibold text-stone-900">
                            <div>
                              <span>{item.productName || "-"}</span>
                              {item.partNumber && <span className="block text-[11px] font-normal text-stone-500 mt-0.5">Part No: {item.partNumber}</span>}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-stone-600">{item.voltage || "-"}</td>
                          <td className="px-4 py-4 text-stone-600 font-semibold">{formatStockCategory(item.stockCategory)}</td>
                          <td className="px-4 py-4 text-stone-600 font-mono text-xs">{item.modelNumber || "-"}</td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-1 text-stone-700">
                              <span className="text-stone-400">📍</span>
                              <span>{item.location || "-"}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center text-stone-500">{formatUnit(item.unit)}</td>
                          <td className="px-4 py-4 text-right font-bold text-stone-900">{item.currentStock ?? 0}</td>
                          <td className="px-4 py-4 text-right font-semibold text-stone-500">{item.minimumStock ?? 0}</td>
                          <td className="px-6 py-4 text-center">
                            {renderStockStatusBadge(item)}
                          </td>
                          <td className="px-4 py-4 text-xs text-stone-500">{formatDate(item.lastUpdated || item.updatedAt)}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center gap-1.5 justify-end">
                              <button
                                type="button"
                                onClick={() => openViewMaterialModal(item)}
                                title="View details"
                                className="p-1.5 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => openEditMaterialModal(item)}
                                title="Edit details"
                                className="p-1.5 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedMaterialKey(materialKey)}
                                title="Stock history log"
                                className="p-1.5 rounded-lg border border-[#1E3A8A]/20 text-[#1E3A8A] hover:bg-blue-50 transition"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => openStockInModal(item)}
                                title="Add Stock"
                                className="p-1.5 rounded-lg border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => openStockOutModal(item)}
                                title="Issue Stock"
                                className="p-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 transition"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                              {item.isArchived ? (
                                <button
                                  type="button"
                                  onClick={() => handleUnarchiveItem(item)}
                                  title="Restore material"
                                  className="p-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 15.89M9 11l3 3L22 4" />
                                  </svg>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleArchiveItem(item)}
                                  title="Archive material"
                                  className="p-1.5 rounded-lg border border-amber-200 text-amber-600 hover:bg-amber-50 transition"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                  </svg>
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteItem(item)}
                                title="Delete material"
                                className="p-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-900 transition"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card Grid View */}
              <div className="space-y-4 md:hidden">
                {filteredStockItems.map((item) => {
                  const materialKey = getInventoryMaterialKey(item);
                  const stock = Number(item.currentStock) || 0;
                  const minStock = item.minimumStock !== undefined ? Number(item.minimumStock) : 5;
                  return (
                    <div key={`${item.productId}-${item.itemCode}`} className="bg-white border border-stone-200 rounded-2xl p-4 shadow-sm space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-bold text-stone-900 text-sm leading-snug">{item.productName}</h3>
                          {item.partNumber && <p className="text-xs text-stone-500 mt-1">Part No: {item.partNumber}</p>}
                        </div>
                        <div>
                          {renderStockStatusBadge(item)}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-xs border-t border-b border-stone-100 py-2.5">
                        <div>
                          <span className="text-stone-500 block">Category</span>
                          <span className="font-semibold text-stone-700">{item.category || "-"}</span>
                        </div>
                        <div>
                          <span className="text-stone-500 block">Brand</span>
                          <span className="font-semibold text-stone-700">{item.brand || "-"}</span>
                        </div>
                        <div>
                          <span className="text-stone-500 block">Location / Area</span>
                          <span className="font-semibold text-stone-700">{item.location || "-"}</span>
                        </div>
                        <div>
                          <span className="text-stone-500 block">Stock Level</span>
                          <span className="font-semibold text-stone-700">{stock} / {minStock} {formatUnit(item.unit)}</span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => openViewMaterialModal(item)}
                          className="rounded-xl border border-stone-200 py-2 text-center text-xs font-semibold text-stone-700 hover:bg-stone-50"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditMaterialModal(item)}
                          className="rounded-xl border border-stone-200 py-2 text-center text-xs font-semibold text-stone-700 hover:bg-stone-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedMaterialKey(materialKey)}
                          className="rounded-xl border border-[#1E3A8A]/20 text-[#1E3A8A] py-2 text-center text-xs font-semibold hover:bg-blue-50"
                        >
                          Log
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(item)}
                          className="rounded-xl border border-rose-200 text-rose-700 py-2 text-center text-xs font-semibold hover:bg-rose-50"
                        >
                          Del
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>
      )}

      {isEntryMode && (
        <section className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-3">
            <div>
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                <span className="text-blue-600 text-sm">
                  {historyTab === "invoices" ? "📜" : "🛒"}
                </span>{" "}
                {historyTab === "invoices"
                  ? entryPage?.historyTitle || "Entry History"
                  : entryTypeFilter === "sale"
                  ? "Sales Quoted Products History"
                  : "Purchase Products History"}
              </h2>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setInvoicesLimit(20);
                  setProductsLimit(20);
                }}
                placeholder={
                  historyTab === "invoices"
                    ? `Search ${entryPage?.historyTitle?.toLowerCase() || "entries"}...`
                    : "Search products by name, code, party..."
                }
                className="rounded-[4px] border border-slate-300 px-3 py-1 text-sm outline-none focus:border-blue-500 h-9 bg-white w-full sm:w-64 text-slate-700"
              />

              <select
                value={`${historySortField}-${historySortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split("-");
                  setHistorySortField(field);
                  setHistorySortOrder(order);
                }}
                className="rounded-[4px] border border-slate-300 px-2.5 py-1 text-sm outline-none focus:border-blue-500 h-9 bg-white w-full sm:w-auto text-slate-755 font-semibold cursor-pointer text-slate-700"
              >
                <option value="date-desc">📅 Date: Newest</option>
                <option value="date-asc">📅 Date: Oldest</option>
                <option value="invoiceNo-asc">📄 Invoice No: A-Z</option>
                <option value="invoiceNo-desc">📄 Invoice No: Z-A</option>
              </select>

              <button
                type="button"
                onClick={() => loadData(search)}
                className="rounded-[4px] bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold px-4 h-9 transition shrink-0"
              >
                Search
              </button>
            </div>
          </div>

          {["sale", "purchase"].includes(entryTypeFilter) && (
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-4 mt-2">
              <button
                type="button"
                onClick={() => setHistoryTab("invoices")}
                className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
                  historyTab === "invoices"
                    ? "bg-sky-600 text-white shadow-sm border-sky-600"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {entryTypeFilter === "sale" ? "Sales Invoices" : "Purchase Invoices"}
              </button>
              <button
                type="button"
                onClick={() => setHistoryTab("products")}
                className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
                  historyTab === "products"
                    ? "bg-sky-600 text-white shadow-sm border-sky-600"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                Products
              </button>
            </div>
          )}

          {historyTab === "invoices" || !["sale", "purchase"].includes(entryTypeFilter) ? (
            <>
              {loading ? (
                <div className="rounded-[4px] border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-400">Loading entries...</div>
              ) : displayedGroupedHistory.length === 0 ? (
                <div className="rounded-[4px] border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-400">No entries found.</div>
              ) : (
                <>
                  {/* Mobile Grid Layout for History */}
                  <div className="space-y-3 md:hidden mt-3">
                    {displayedGroupedHistory.map((group) => {
                      const entry = group.primaryEntry;
                      const materialPreview = group.entries
                        .slice(0, 2)
                        .map((item) => item.itemName)
                        .filter(Boolean)
                        .join(", ");

                      return (
                        <article key={`mobile-${group.key}`} className="rounded-[4px] border border-slate-200 bg-white p-3 shadow-sm space-y-2">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{formatDate(entry.transactionDate)}</p>
                              <h3 className="mt-0.5 break-words text-sm font-bold text-slate-800">{entry.documentNumber || entry.poNumber || "-"}</h3>
                              {entry.customerId || entry.dealerId ? (
                                <button
                                  type="button"
                                  onClick={() => handleCounterpartyClick(entry)}
                                  className="mt-0.5 truncate text-xs text-blue-600 hover:text-blue-800 hover:underline font-semibold text-left block"
                                >
                                  {entry.counterpartyName || "-"}
                                </button>
                              ) : (
                                <p className="mt-0.5 truncate text-xs text-slate-600">{entry.counterpartyName || "-"}</p>
                              )}
                            </div>
                            <p className="shrink-0 text-right text-sm font-bold text-slate-800">{formatCurrency(entry.finalTotal || group.totalAmount)}</p>
                          </div>
                          <div className="rounded-[4px] bg-slate-50 p-2.5 border border-slate-100">
                            <p className="line-clamp-2 text-xs font-semibold text-slate-700">{materialPreview || entry.itemName || "-"}</p>
                            <p className="mt-0.5 text-[10px] text-slate-400">
                              {group.productCount > 2
                                ? `+${group.productCount - 2} more products`
                                : `${group.productCount} product${group.productCount > 1 ? "s" : ""}`}
                            </p>
                            {entry.entryType === "order" ? (
                              <p className="mt-1 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                                {entry.orderType === "supplier_order" ? "Supplier Order" : "Quotation / Proforma"}
                              </p>
                            ) : null}
                          </div>
                          <div className="grid grid-cols-2 gap-1.5 pt-1">
                            <button type="button" onClick={() => openEntryPdf(entry._id)} className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1.5 text-xs font-semibold text-slate-700 transition">
                              {primaryPdfLabel}
                            </button>
                            <button type="button" onClick={() => downloadEntryPdf(entry._id)} className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1.5 text-xs font-semibold text-slate-700 transition">
                              Download PDF
                            </button>
                            {showPerformaPdfButton && entry.entryType === "sale" ? (
                              <button type="button" onClick={() => convertPerformaToSalesInvoice(group)} className="rounded-[4px] border border-emerald-300 bg-white hover:bg-emerald-50 px-2 py-1.5 text-xs font-semibold text-emerald-800 transition">
                                Sales Invoice
                              </button>
                            ) : null}
                            <button type="button" onClick={() => setSelectedHistoryEntry(group)} className="rounded-[4px] border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1.5 text-xs font-semibold text-slate-700 transition">
                              View
                            </button>
                            {entry.entryType === "order" ? (
                              <button type="button" onClick={() => openPurchaseInvoiceFromOrder(group)} className="rounded-[4px] border border-emerald-300 bg-white hover:bg-emerald-50 px-2 py-1.5 text-xs font-semibold text-emerald-800 transition">
                                Invoice
                              </button>
                            ) : null}
                            <button type="button" onClick={() => handleEditEntry(entry)} className="rounded-[4px] border border-blue-300 bg-white hover:bg-blue-50 px-2 py-1.5 text-xs font-semibold text-blue-800 transition">
                              Edit
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  {/* Desktop Table View for Invoices */}
                  <div className="hidden overflow-auto md:block max-h-[600px] border border-slate-200 rounded-[4px] mt-3">
                    <table className="w-full min-w-[1200px] border-collapse text-left text-xs">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                          <th className="px-3 py-2 font-semibold border-r border-slate-100 w-28">Date</th>
                          <th className="px-3 py-2 font-semibold border-r border-slate-100 w-36">{entryPage?.documentHeader || "Doc No"}</th>
                          <th className="px-3 py-2 font-semibold border-r border-slate-100">Products</th>
                          <th className="px-3 py-2 font-semibold border-r border-slate-100 w-32">Total</th>
                          <th className="px-3 py-2 font-semibold border-r border-slate-100 w-64">{entryPage?.partyHeader || "Supplier / Customer"}</th>
                          <th className="px-3 py-2 font-semibold w-72">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {displayedGroupedHistory.map((group) => {
                          const entry = group.primaryEntry;
                          const materialPreview = group.entries
                            .slice(0, 2)
                            .map((item) => item.itemName)
                            .filter(Boolean)
                            .join(", ");

                          return (
                            <tr key={group.key} className="hover:bg-slate-50/50 transition align-middle">
                              <td className="px-3 py-2 text-slate-600 border-r border-slate-100">{formatDate(entry.transactionDate)}</td>
                              <td className="px-3 py-2 font-semibold text-slate-700 border-r border-slate-100">{entry.documentNumber || entry.poNumber || "-"}</td>
                              <td className="px-3 py-2 border-r border-slate-100">
                                <p className="font-semibold text-slate-800">{materialPreview || entry.itemName || "-"}</p>
                                <p className="text-[10px] text-slate-400">
                                  {group.productCount > 2
                                    ? `+${group.productCount - 2} more products`
                                    : `${group.productCount} product${group.productCount > 1 ? "s" : ""}`}
                                </p>
                              </td>
                              <td className="px-3 py-2 text-slate-800 font-bold border-r border-slate-100">{formatCurrency(entry.finalTotal || group.totalAmount)}</td>
                              <td className="px-3 py-2 text-slate-700 border-r border-slate-100">
                                {entry.customerId || entry.dealerId ? (
                                  <button
                                    type="button"
                                    onClick={() => handleCounterpartyClick(entry)}
                                    className="text-blue-600 hover:text-blue-850 hover:underline font-semibold text-left"
                                  >
                                    {entry.counterpartyName || "-"}
                                  </button>
                                ) : (
                                  entry.counterpartyName || "-"
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-1.5 items-center">
                                  {entry.entryType === "order" && (
                                    <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-[2px] mr-1">
                                      {entry.orderType === "supplier_order" ? "Supplier PO" : "Cust Order"}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => openEntryPdf(entry._id)}
                                    className="rounded-[4px] border border-slate-300 hover:bg-slate-50 text-slate-700 px-2 py-1 text-[11px] font-semibold transition shrink-0"
                                  >
                                    {primaryPdfLabel}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => downloadEntryPdf(entry._id)}
                                    className="rounded-[4px] border border-slate-300 hover:bg-slate-50 text-slate-700 px-2 py-1 text-[11px] font-semibold transition shrink-0"
                                  >
                                    Download PDF
                                  </button>
                                  {showPerformaPdfButton && entry.entryType === "sale" ? (
                                    <button
                                      type="button"
                                      onClick={() => convertPerformaToSalesInvoice(group)}
                                      className="rounded-[4px] border border-emerald-300 hover:bg-emerald-50 text-emerald-800 px-2 py-1 text-[11px] font-semibold transition shrink-0"
                                    >
                                      Sales Invoice
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={() => setSelectedHistoryEntry(group)}
                                    className="rounded-[4px] border border-slate-300 hover:bg-slate-50 text-slate-700 px-2 py-1 text-[11px] font-semibold transition shrink-0"
                                  >
                                    View
                                  </button>
                                  {entry.entryType === "order" ? (
                                    <button
                                      type="button"
                                      onClick={() => openPurchaseInvoiceFromOrder(group)}
                                      className="rounded-[4px] border border-emerald-300 hover:bg-emerald-50 text-emerald-800 px-2 py-1 text-[11px] font-semibold transition shrink-0"
                                    >
                                      Purchase Invoice
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={() => handleEditEntry(entry)}
                                    className="rounded-[4px] border border-blue-300 hover:bg-blue-50 text-blue-800 px-2 py-1 text-[11px] font-semibold transition shrink-0"
                                  >
                                    Edit
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination View More for Invoices */}
                  {filteredGroupedHistory.length > invoicesLimit && (
                    <div className="mt-4 flex justify-center pb-2">
                      <button
                        type="button"
                        onClick={() => setInvoicesLimit((prev) => prev + 20)}
                        className="rounded-[4px] bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-4 py-2 transition"
                      >
                        View More
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {/* Mobile Grid Layout for Quoted/Purchased Products */}
              <div className="space-y-3 md:hidden mt-3">
                {displayedProductsHistory.length === 0 ? (
                  <div className="rounded-[4px] border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-400">No products found.</div>
                ) : (
                  displayedProductsHistory.map((p, idx) => (
                    <article key={`mobile-prod-${p._id || idx}`} className="rounded-[4px] border border-slate-200 bg-white p-3 shadow-sm space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{formatDate(p.transactionDate)}</p>
                          <h3 className="mt-0.5 break-words text-sm font-bold text-slate-800">{p.documentNumber || p.poNumber || "-"}</h3>
                          {p.customerId || p.dealerId ? (
                            <button
                              type="button"
                              onClick={() => handleCounterpartyClick(p)}
                              className="mt-0.5 truncate text-xs text-blue-600 hover:text-blue-800 hover:underline font-semibold text-left block"
                            >
                              {p.counterpartyName || "-"}
                            </button>
                          ) : (
                            <p className="mt-0.5 truncate text-xs text-slate-600">{p.counterpartyName || "-"}</p>
                          )}
                        </div>
                      </div>
                      <div className="rounded-[4px] bg-slate-50 p-2.5 border border-slate-100 text-xs">
                        <p className="font-semibold text-slate-700">{p.itemName || p.description || "-"}</p>
                        <p className="mt-1 text-[10px] text-slate-500">Qty: {p.quantity} {p.unit || "No"}</p>
                        <p className="mt-0.5 font-bold text-slate-900">Total: ₹{(p.totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>
                    </article>
                  ))
                )}
              </div>

              {/* Desktop Table View for Products */}
              <div className="hidden overflow-auto md:block max-h-[600px] border border-slate-200 rounded-[4px] mt-3">
                <table className="w-full min-w-[1200px] border-collapse text-left text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                      <th className="px-3 py-2 font-semibold border-r border-slate-100 w-28">Date</th>
                      <th className="px-3 py-2 font-semibold border-r border-slate-100 w-36">Doc No</th>
                      <th className="px-3 py-2 font-semibold border-r border-slate-100 w-64">{entryTypeFilter === "sale" ? "Customer" : "Supplier"}</th>
                      <th className="px-3 py-2 font-semibold border-r border-slate-100">Product Name</th>
                      <th className="px-3 py-2 font-semibold border-r border-slate-100 w-28">Item Code</th>
                      <th className="px-3 py-2 font-semibold border-r border-slate-100 w-28">HSN Code</th>
                      <th className="px-3 py-2 font-semibold text-right border-r border-slate-100 w-24">Quantity</th>
                      <th className="px-3 py-2 border-r border-slate-100 w-20">Unit</th>
                      <th className="px-3 py-2 text-right border-r border-slate-100 w-28">Unit Price</th>
                      <th className="px-3 py-2 text-right w-32">Total Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {displayedProductsHistory.length === 0 ? (
                      <tr><td colSpan="10" className="px-3 py-8 text-center text-slate-400">No products found.</td></tr>
                    ) : (
                      displayedProductsHistory.map((p, idx) => (
                        <tr key={`prod-row-${p._id || idx}`} className="hover:bg-slate-50/50 transition align-middle">
                          <td className="px-3 py-2 text-slate-600 border-r border-slate-100">{formatDate(p.transactionDate)}</td>
                          <td className="px-3 py-2 font-semibold text-slate-700 border-r border-slate-100">{p.documentNumber || p.poNumber || "-"}</td>
                          <td className="px-3 py-2 text-slate-700 border-r border-slate-100 font-medium">
                            {p.customerId || p.dealerId ? (
                              <button
                                type="button"
                                onClick={() => handleCounterpartyClick(p)}
                                className="text-blue-600 hover:text-blue-850 hover:underline font-semibold text-left"
                              >
                                {p.counterpartyName || "-"}
                              </button>
                            ) : (
                              p.counterpartyName || "-"
                            )}
                          </td>
                          <td className="px-3 py-2 border-r border-slate-100 text-slate-800 font-semibold whitespace-pre-wrap">{p.itemName || p.description || "-"}</td>
                          <td className="px-3 py-2 font-mono text-[10px] text-slate-500 border-r border-slate-100">{p.itemCode || "-"}</td>
                          <td className="px-3 py-2 text-slate-500 border-r border-slate-100">{p.hsnCode || "-"}</td>
                          <td className="px-3 py-2 text-right text-slate-900 font-semibold border-r border-slate-100">{p.quantity}</td>
                          <td className="px-3 py-2 text-slate-600 border-r border-slate-100">{p.unit || "No"}</td>
                          <td className="px-3 py-2 text-right text-slate-700 border-r border-slate-100">₹{(p.unitPrice || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 text-right font-bold text-slate-950">₹{(p.totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination View More for Products */}
              {filteredProductsHistory.length > productsLimit && (
                <div className="mt-4 flex justify-center pb-2">
                  <button
                    type="button"
                    onClick={() => setProductsLimit((prev) => prev + 20)}
                    className="rounded-[4px] bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-4 py-2 transition"
                  >
                    View More
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {selectedMaterial ? (
        <MaterialDetailsModal
          item={selectedMaterial}
          onClose={() => setSelectedMaterialKey("")}
        />
      ) : null}

      {classificationItem ? (
        <InventoryClassificationModal
          item={classificationItem}
          draft={classificationDraft}
          saving={classificationSaving}
          onChange={(name, value) => setClassificationDraft((current) => ({
            ...current,
            [name]: value
          }))}
          onSave={handleSaveClassification}
          onClose={() => setClassificationItem(null)}
        />
      ) : null}

      {selectedHistoryEntry ? (
        <HistoryDetailsModal
          entry={selectedHistoryEntry}
          onOpenPdf={openEntryPdf}
          onDownloadPdf={downloadEntryPdf}
          onEdit={(entryToEdit) => {
            setSelectedHistoryEntry(null);
            handleEditEntry(entryToEdit);
          }}
          onClose={() => setSelectedHistoryEntry(null)}
        />
      ) : null}

      {showMaterialModal && (
        <MaterialFormModal
          mode={materialFormMode}
          formData={materialFormData}
          onChange={(name, value) => setMaterialFormData(prev => ({ ...prev, [name]: value }))}
          onSave={handleSaveMaterial}
          onClose={() => setShowMaterialModal(false)}
          suppliers={suppliers}
          storageLocations={storageLocations}
        />
      )}

      {showMovementModal && (
        <StockMovementModal
          type={movementType}
          formData={movementFormData}
          onChange={(name, value) => setMovementFormData(prev => ({ ...prev, [name]: value }))}
          onSave={handleSaveMovement}
          onClose={() => setShowMovementModal(false)}
          inventoryItems={inventoryItems}
        />
      )}

      {showImportModal && (
        <ExcelImportModal
          onClose={() => setShowImportModal(false)}
          onSuccess={async () => {
            await loadData();
            setShowImportModal(false);
          }}
        />
      )}
    </div>
  );
}

function InputField({ label, ...props }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-stone-700">{label}</label>
      <input
        {...props}
        className="w-full rounded-2xl border border-amber-200 px-4 py-3 text-sm outline-none transition focus:border-amber-400"
      />
    </div>
  );
}

function SelectField({ label, options, ...props }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-stone-700">{label}</label>
      <select
        {...props}
        className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-amber-400"
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function TableInput({ value, onChange, ...props }) {
  return (
    <input
      {...props}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-[1rem] border border-amber-200 bg-white px-4 py-2.5 text-sm outline-none transition placeholder:text-stone-400 focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
    />
  );
}

// eslint-disable-next-line no-unused-vars
function TableSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-[1rem] border border-amber-200 bg-white px-4 py-2.5 text-sm outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
    >
      {options.map(([optionValue, optionLabel]) => (
        <option key={optionValue} value={optionValue}>
          {optionLabel}
        </option>
      ))}
    </select>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-2xl bg-stone-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-400">{label}</p>
      <p className="mt-2 text-sm font-semibold text-stone-800">{value}</p>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function DocumentSummaryRow({ label, value, strong = false }) {
  return (
    <div className={`flex items-center justify-between rounded-2xl bg-white px-4 py-3 ${strong ? "font-bold text-stone-950" : "text-stone-700"}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function InventoryClassificationModal({ item, draft, saving, onChange, onSave, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/45 px-4 py-6">
      <div className="w-full max-w-lg rounded-[2rem] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-stone-200 pb-5">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-sky-700">Stock Classification</p>
            <h3 className="mt-2 text-2xl font-bold text-stone-950">{item.productName || item.itemName || "Material"}</h3>
            <p className="mt-2 text-sm text-stone-500">
              {formatStockCategory(item.stockCategory)} | Current Stock: {Number(item.currentStock) || 0}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
          >
            Close
          </button>
        </div>

        <div className="mt-6 grid gap-4">
          <div>
            <label className="mb-2 block text-sm font-semibold text-stone-700">Stock Type</label>
            <select
              value={draft.stockCategory}
              onChange={(event) => onChange("stockCategory", event.target.value)}
              className="w-full rounded-2xl border border-amber-200 px-4 py-3 text-sm outline-none transition focus:border-amber-400"
            >
              <option value="sales">For Sales</option>
              <option value="consumable">Consumable</option>
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold text-stone-700">Location</label>
            <input
              value={draft.location}
              onChange={(event) => onChange("location", event.target.value)}
              placeholder="Rack / shelf / room (optional)"
              className="w-full rounded-2xl border border-amber-200 px-4 py-3 text-sm outline-none transition focus:border-amber-400"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-stone-300 px-4 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="rounded-2xl bg-sky-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-sky-400 disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MaterialDetailsModal({ item, onClose }) {
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadHistory = async () => {
      try {
        setHistoryLoading(true);
        const res = await axios.get(API_BASE_URL + `/api/material-stock/inventory/${item.productId}/history`);
        if (active) {
          setHistory(Array.isArray(res.data) ? res.data : []);
        }
      } catch (error) {
        if (active) {
          setHistory([]);
        }
      } finally {
        if (active) {
          setHistoryLoading(false);
        }
      }
    };

    if (item.productId) {
      loadHistory();
    } else {
      setHistory([]);
      setHistoryLoading(false);
    }

    return () => {
      active = false;
    };
  }, [item.productId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/45 px-4 py-6">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl">
        <div className="flex flex-col gap-4 border-b border-stone-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-4 items-start">
            {item.imageUrl ? (
              <img src={API_BASE_URL + item.imageUrl} alt={item.itemName || item.productName} className="w-16 h-16 rounded-xl object-cover border" />
            ) : null}
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1E3A8A]">Material Details</p>
              <h3 className="mt-2 text-2xl font-bold text-stone-950">{item.itemName || item.productName || "Material"}</h3>
              <p className="mt-2 text-sm text-stone-500">
                {[item.itemCode || "No item code", item.sku || "No SKU", formatUnit(item.unit)].join(" | ")}
              </p>
              {item.description ? <p className="mt-2 text-sm text-stone-600">{item.description}</p> : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
          >
            Close
          </button>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          <MiniStat label="Available Stock" value={`${item.availableStock} ${formatUnit(item.unit)}`} />
          <MiniStat label="Stock Type" value={formatStockCategory(item.stockCategory)} />
          <MiniStat label="Location" value={item.rackNumber ? `${item.rackNumber}${item.shelfBin ? ` - ${item.shelfBin}` : ""}` : (item.location || "-")} />
          <MiniStat label="Purchase Price" value={formatCurrency(item.purchasePrice)} />
          <MiniStat label="Selling Price" value={formatCurrency(item.sellingPrice || item.listPrice)} />
          <MiniStat label="Last Updated" value={formatDate(item.lastUpdated)} />
        </div>

        <div className="mt-6 border-t border-stone-100 pt-6 grid gap-6 md:grid-cols-3">
          <div className="md:col-span-2">
            <h4 className="text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-3">Classification & Identification</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <MiniStat label="Voltage" value={item.voltage || "-"} />
              <MiniStat label="Manufacturer" value={item.manufacturer || "-"} />
              <MiniStat label="Part Number" value={item.partNumber || "-"} />
              <MiniStat label="Model Number" value={item.modelNumber || "-"} />
              <MiniStat label="Preferred Supplier" value={item.supplier || "-"} />
              <MiniStat label="Min Stock Alert Level" value={item.minimumStock !== undefined ? item.minimumStock : "-"} />
            </div>
            {item.attachments && item.attachments.length > 0 && (
              <div className="mt-4">
                <a
                  href={API_BASE_URL + item.attachments[0].fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-stone-300 rounded-xl hover:bg-stone-50 text-xs font-semibold text-stone-700"
                >
                  📄 Open Datasheet ({item.attachments[0].fileName})
                </a>
              </div>
            )}
          </div>
          <div className="flex flex-col items-center justify-center p-4 border rounded-2xl bg-stone-50">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Material QR Label</p>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${item.productId || item._id}`}
              alt="QR Code"
              className="w-[120px] h-[120px] bg-white p-2 border rounded-xl"
            />
            <button
              type="button"
              onClick={() => {
                const printWindow = window.open("", "_blank");
                printWindow.document.write(`
                  <html>
                    <head>
                      <title>Print QR Label - ${item.itemName || item.productName}</title>
                      <style>
                        body {
                          font-family: system-ui, sans-serif;
                          display: flex;
                          flex-direction: column;
                          align-items: center;
                          justify-content: center;
                          height: 100vh;
                          margin: 0;
                          text-align: center;
                        }
                        .label-card {
                          border: 2px solid #000;
                          padding: 20px;
                          border-radius: 8px;
                          width: 250px;
                        }
                        .title {
                          font-weight: bold;
                          font-size: 16px;
                          margin-bottom: 5px;
                        }
                        .code {
                          font-family: monospace;
                          font-size: 12px;
                          color: #555;
                          margin-bottom: 15px;
                        }
                        img {
                          width: 150px;
                          height: 150px;
                        }
                      </style>
                    </head>
                    <body onload="window.print(); window.close();">
                      <div class="label-card">
                        <div class="title">${item.itemName || item.productName}</div>
                        <div class="code">${item.partNumber || item.itemCode}</div>
                        <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${item.productId || item._id}" alt="QR" />
                      </div>
                    </body>
                  </html>
                `);
                printWindow.document.close();
              }}
              className="mt-3 px-4 py-2 bg-[#1E3A8A] hover:bg-[#152e72] text-xs font-bold text-white rounded-xl transition shadow-sm"
            >
              🖨️ Print QR Label
            </button>
          </div>
        </div>

        {item.specifications && item.specifications.length > 0 && (
          <div className="mt-6 border-t border-stone-100 pt-6">
            <h4 className="text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-3">Technical Specifications</h4>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {item.specifications.map((spec, idx) => (
                <div key={idx} className="rounded-2xl bg-blue-50/50 border border-blue-100 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1E3A8A]">{spec.name}</p>
                  <p className="mt-2 text-sm font-semibold text-stone-800">{spec.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {item.remarks && (
          <div className="mt-6 border-t border-stone-100 pt-6">
            <h4 className="text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-2">Remarks / Notes</h4>
            <div className="rounded-2xl bg-stone-50 border border-stone-200 p-4 text-sm text-stone-700 leading-relaxed whitespace-pre-line">
              {item.remarks}
            </div>
          </div>
        )}

        <div className="mt-6 border-t border-stone-100 pt-6">
          <h4 className="text-lg font-bold text-stone-950">Stock Movement History</h4>
          <div className="mt-3 overflow-auto max-h-[300px] border border-stone-200 rounded-2xl scrollbar-thin scrollbar-thumb-stone-200">
            <table className="w-full min-w-[760px]">
              <thead className="bg-stone-100 text-left sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Quantity</th>
                  <th className="px-4 py-3">Previous</th>
                  <th className="px-4 py-3">New</th>
                  <th className="px-4 py-3">Performed By</th>
                  <th className="px-4 py-3">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-sm text-stone-500">
                      Loading stock history...
                    </td>
                  </tr>
                ) : history.length ? history.map((entry) => (
                  <tr key={entry._id} className="border-t border-stone-200">
                    <td className="px-4 py-3 text-sm text-stone-700">{formatDate(entry.createdAt)}</td>
                    <td className={`px-4 py-3 text-sm font-semibold ${entry.transactionType === "IN" ? "text-emerald-700" : "text-rose-700"}`}>{entry.transactionType === "IN" ? "Stock In" : "Stock Out"}</td>
                    <td className="px-4 py-3 text-sm text-stone-700 font-bold">{entry.quantity} {formatUnit(entry.unit)}</td>
                    <td className="px-4 py-3 text-sm text-stone-500">{entry.previousStock}</td>
                    <td className="px-4 py-3 text-sm text-stone-900 font-semibold">{entry.newStock}</td>
                    <td className="px-4 py-3 text-sm text-stone-700">{entry.performedBy || "-"}</td>
                    <td className="px-4 py-3 text-sm text-stone-700">{entry.remarks || `${entry.referenceType} ${entry.referenceId}`}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-sm text-stone-500">
                      No stock history found for this product.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function HistoryDetailsModal({ entry, onClose, onEdit, onOpenPdf, onDownloadPdf }) {
  const primaryEntry = entry.primaryEntry || entry;
  const groupEntries = Array.isArray(entry.entries) && entry.entries.length ? entry.entries : [primaryEntry];
  const totalQuantity = groupEntries.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const totalAmount = groupEntries.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
  const displayTotal = Number(primaryEntry.finalTotal) || totalAmount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/45 px-4 py-6">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-stone-200 pb-5">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-700">Entry Details</p>
            <h3 className="mt-2 text-2xl font-bold text-stone-950">{primaryEntry.documentNumber || primaryEntry.poNumber || "Document"}</h3>
            <p className="mt-2 text-sm text-stone-500">
              {formatDate(primaryEntry.transactionDate)} | {entryLabelMap[primaryEntry.entryType] || primaryEntry.entryType || "-"}
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              <span className="inline-flex rounded-full bg-amber-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-amber-800">
                Billing: {primaryEntry.billingStatus === "billed" ? "Billed" : "Not Billed"}
              </span>
              <span className="inline-flex rounded-full bg-emerald-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-800">
                Payment: {primaryEntry.paymentStatus === "done" ? "Done" : "Not Done"}
              </span>
            </div>
          </div>
          <div className="flex gap-3">
            {onOpenPdf && (
              <button
                type="button"
                onClick={() => onOpenPdf(primaryEntry._id)}
                className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
              >
                View PDF
              </button>
            )}
            {onDownloadPdf && (
              <button
                type="button"
                onClick={() => onDownloadPdf(primaryEntry._id)}
                className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
              >
                Download PDF
              </button>
            )}
            <button
              type="button"
              onClick={() => onEdit(primaryEntry)}
              className="rounded-full border border-sky-300 px-4 py-2 text-sm font-semibold text-sky-800 transition hover:bg-sky-50"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
            >
              Close
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {primaryEntry.entryType === "order" ? (
            <MiniStat
              label="Order Type"
              value={primaryEntry.orderType === "supplier_order" ? "Order From Supplier" : "Quotation / Proforma To Customer"}
            />
          ) : null}
          {primaryEntry.entryType !== "order" ? (
            <MiniStat label="Document No" value={primaryEntry.documentNumber || "-"} />
          ) : null}
          <MiniStat label="PO No" value={primaryEntry.poNumber || "-"} />
          {primaryEntry.entryType === "order" ? (
            <MiniStat label="PO Date" value={formatDate(primaryEntry.transactionDate)} />
          ) : null}
          <MiniStat label="Items" value={groupEntries.length} />
          <MiniStat label="Quantity" value={totalQuantity} />
          <MiniStat label="Total" value={formatCurrency(displayTotal)} />
          {primaryEntry.entryType !== "order" && primaryEntry.roundOffEnabled ? (
            <MiniStat label="Round Off" value={`${Number(primaryEntry.roundOffAmount) >= 0 ? "+" : ""}${formatCurrency(primaryEntry.roundOffAmount)}`} />
          ) : null}
          <MiniStat label="Party" value={primaryEntry.counterpartyName || "-"} />
          <MiniStat label="Reference" value={primaryEntry.reference || "-"} />
          <MiniStat label="Payment Mode" value={formatPaymentMode(primaryEntry.paymentMode)} />
          <MiniStat label="Delivery Note" value={primaryEntry.deliveryNote || "-"} />
          <MiniStat label="Dispatch Through" value={primaryEntry.dispatchThrough || "-"} />
          <MiniStat label="Delivery At" value={primaryEntry.deliveryAt || "-"} />
          <MiniStat label="Location" value={primaryEntry.location || "-"} />
          <MiniStat label="GST %" value={primaryEntry.gst ?? 18} />
        </div>

        <div className="mt-6 rounded-[1.5rem] bg-stone-50 p-5">
          <div className="mb-4 flex items-center justify-between gap-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Products</p>
            <p className="text-sm font-semibold text-stone-700">{groupEntries.length} item{groupEntries.length > 1 ? "s" : ""}</p>
          </div>
          <div className="overflow-auto max-h-[300px] border border-stone-200 rounded-2xl scrollbar-thin scrollbar-thumb-stone-200">
            <table className="w-full min-w-[780px]">
              <thead className="bg-white text-left sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-sm">Material</th>
                  <th className="px-4 py-3 text-sm">Item Code</th>
                  <th className="px-4 py-3 text-sm">HSN Code</th>
                  <th className="px-4 py-3 text-sm">Description</th>
                  <th className="px-4 py-3 text-sm">Qty</th>
                  <th className="px-4 py-3 text-sm">Unit</th>
                  <th className="px-4 py-3 text-sm">Price</th>
                  <th className="px-4 py-3 text-sm">Total</th>
                </tr>
              </thead>
              <tbody>
                {groupEntries.map((item) => (
                  <tr key={item._id} className="border-t border-stone-200">
                    <td className="px-4 py-3 text-sm font-semibold text-stone-900">{item.itemName || "-"}</td>
                    <td className="px-4 py-3 text-sm text-stone-700">{item.itemCode || item.sku || "-"}</td>
                    <td className="px-4 py-3 text-sm text-stone-700">{item.hsnCode || "-"}</td>
                    <td className="px-4 py-3 text-sm text-stone-700">{item.description || "-"}</td>
                    <td className="px-4 py-3 text-sm text-stone-700">{item.quantity || 0}</td>
                    <td className="px-4 py-3 text-sm text-stone-700">{formatUnit(item.unit)}</td>
                    <td className="px-4 py-3 text-sm text-stone-700">{formatCurrency(item.unitPrice)}</td>
                    <td className="px-4 py-3 text-sm text-stone-700">{formatCurrency(item.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-[1.5rem] bg-stone-50 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Contact Details</p>
            <p className="mt-2 text-sm text-stone-700">{primaryEntry.counterpartyPhone || "-"}</p>
            <p className="mt-1 text-sm text-stone-700">{primaryEntry.counterpartyEmail || "-"}</p>
            <p className="mt-1 text-sm text-stone-700">{primaryEntry.counterpartyGst || "-"}</p>
          </div>
          <div className="rounded-[1.5rem] bg-stone-50 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Address</p>
            <p className="mt-2 text-sm text-stone-700">{primaryEntry.counterpartyAddress || "-"}</p>
          </div>
        </div>

        <div className="mt-4 rounded-[1.5rem] bg-stone-50 px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Notes</p>
          <p className="mt-2 text-sm text-stone-700">{primaryEntry.notes || "-"}</p>
        </div>
      </div>
    </div>
  );
}

const renderStockStatusBadge = (item) => {
  const stock = Number(item.currentStock) || 0;
  const minStock = item.minimumStock !== undefined ? Number(item.minimumStock) : 5;
  
  if (stock <= 0) {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
        ● Out of Stock
      </span>
    );
  } else if (stock <= minStock) {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
        ● Low Stock
      </span>
    );
  } else {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        ● In Stock
      </span>
    );
  }
};

function MaterialFormModal({ mode, formData, onChange, onSave, onClose, suppliers, storageLocations = [] }) {
  const isView = mode === "view";
  const title = mode === "add" ? "Add New Material" : mode === "edit" ? "Edit Material Details" : "View Material Details";

  const locationsByArea = useMemo(() => {
    const groups = {};
    storageLocations.forEach(loc => {
      const areaName = loc.area && loc.area.name ? `Area ${loc.area.name}` : "Other Racks";
      if (!groups[areaName]) {
        groups[areaName] = [];
      }
      groups[areaName].push(loc);
    });
    return groups;
  }, [storageLocations]);

  const predefinedList = SUBCATEGORY_SPECS[formData.subCategory] || [];
  const customSpecs = (formData.specifications || []).filter(s => !predefinedList.includes(s.name));

  const handleSpecChange = (specName, val) => {
    let newSpecs = [...(formData.specifications || [])];
    const idx = newSpecs.findIndex(s => s.name === specName);
    if (idx >= 0) {
      newSpecs[idx].value = val;
    } else {
      newSpecs.push({ name: specName, value: val });
    }
    onChange("specifications", newSpecs);
  };

  const handleCustomSpecNameChange = (indexInCustom, newName) => {
    let currentCustomCount = 0;
    const newSpecs = (formData.specifications || []).map(s => {
      if (!predefinedList.includes(s.name)) {
        if (currentCustomCount === indexInCustom) {
          currentCustomCount++;
          return { ...s, name: newName };
        }
        currentCustomCount++;
      }
      return s;
    });
    onChange("specifications", newSpecs);
  };

  const handleCustomSpecValueChange = (indexInCustom, newValue) => {
    let currentCustomCount = 0;
    const newSpecs = (formData.specifications || []).map(s => {
      if (!predefinedList.includes(s.name)) {
        if (currentCustomCount === indexInCustom) {
          currentCustomCount++;
          return { ...s, value: newValue };
        }
        currentCustomCount++;
      }
      return s;
    });
    onChange("specifications", newSpecs);
  };

  const removeCustomSpec = (indexInCustom) => {
    let currentCustomCount = 0;
    const newSpecs = (formData.specifications || []).filter(s => {
      if (!predefinedList.includes(s.name)) {
        if (currentCustomCount === indexInCustom) {
          currentCustomCount++;
          return false;
        }
        currentCustomCount++;
      }
      return true;
    });
    onChange("specifications", newSpecs);
  };

  const addCustomSpec = () => {
    const newSpecs = [...(formData.specifications || []), { name: `Custom Spec ${customSpecs.length + 1}`, value: "" }];
    onChange("specifications", newSpecs);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/45 px-4 py-6">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-stone-200 pb-5 mb-6">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1E3A8A]">{mode.toUpperCase()} MATERIAL</p>
            <h3 className="mt-2 text-2xl font-bold text-stone-950">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
          >
            Close
          </button>
        </div>

        <div className="mt-4 space-y-6">
          {/* Section 1: Basic Details */}
          <div>
            <h4 className="text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-3">Basic Details</h4>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Material Name *</label>
                <input
                  type="text"
                  required
                  disabled={isView}
                  value={formData.productName}
                  onChange={(e) => onChange("productName", e.target.value)}
                  placeholder="e.g. MCCB 100A 3 Pole"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Manufacturer</label>
                <input
                  type="text"
                  disabled={isView}
                  value={formData.manufacturer || ""}
                  onChange={(e) => onChange("manufacturer", e.target.value)}
                  placeholder="e.g. Siemens"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Voltage</label>
                <input
                  type="text"
                  disabled={isView}
                  value={formData.voltage || ""}
                  onChange={(e) => onChange("voltage", e.target.value)}
                  placeholder="e.g. 230V, 415V"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Stock Category *</label>
                <select
                  required
                  disabled={isView}
                  value={formData.stockCategory || "sales"}
                  onChange={(e) => onChange("stockCategory", e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                >
                  <option value="sales">Sales</option>
                  <option value="consumable">Consumable</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Part/Item Code</label>
                <input
                  type="text"
                  disabled={isView}
                  value={formData.partNumber}
                  onChange={(e) => onChange("partNumber", e.target.value)}
                  placeholder="e.g. SCH-MCCB-100"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Model Number</label>
                <input
                  type="text"
                  disabled={isView}
                  value={formData.modelNumber}
                  onChange={(e) => onChange("modelNumber", e.target.value)}
                  placeholder="e.g. NSX100F"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Category</label>
                <select
                  disabled={isView}
                  value={formData.category || ""}
                  onChange={(e) => onChange("category", e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                >
                  <option value="">-- Select Category --</option>
                  <option value="MCCB">MCCB</option>
                  <option value="ACB">ACB</option>
                  <option value="MCB">MCB</option>
                  <option value="Contactors">Contactors</option>
                  <option value="Relays">Relays</option>
                  <option value="Current Transformers (CT)">Current Transformers (CT)</option>
                  <option value="Potential Transformers (PT)">Potential Transformers (PT)</option>
                  <option value="Control Transformers">Control Transformers</option>
                  <option value="Capacitors">Capacitors</option>
                  <option value="Switch Disconnectors">Switch Disconnectors</option>
                  <option value="Shunt Coils">Shunt Coils</option>
                  <option value="UV Coils">UV Coils</option>
                  <option value="Rotary Handles">Rotary Handles</option>
                  <option value="Terminal Connectors">Terminal Connectors</option>
                  <option value="Speed Controllers">Speed Controllers</option>
                  <option value="Soft Starters">Soft Starters</option>
                  <option value="Accessories">Accessories</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Unit</label>
                <select
                  disabled={isView}
                  value={formData.unit}
                  onChange={(e) => onChange("unit", e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                >
                  {unitOptions.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Inventory Details */}
          <div className="border-t border-stone-100 pt-6">
            <h4 className="text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-3">Inventory Details</h4>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Opening Stock</label>
                <input
                  type="number"
                  disabled={isView || mode === "edit"}
                  value={formData.openingStock}
                  onChange={(e) => onChange("openingStock", e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              {mode !== "add" && (
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Current Stock</label>
                  <input
                    type="number"
                    disabled={isView}
                    value={formData.currentStock}
                    onChange={(e) => onChange("currentStock", e.target.value)}
                    placeholder="0"
                    className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                  />
                </div>
              )}
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Minimum Stock</label>
                <input
                  type="number"
                  disabled={isView}
                  value={formData.minimumStock}
                  onChange={(e) => onChange("minimumStock", e.target.value)}
                  placeholder="5"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Location / Area</label>
                <select
                  disabled={isView}
                  value={formData.rackNumber}
                  onChange={(e) => onChange("rackNumber", e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                >
                  <option value="">-- Select Location / Area --</option>
                  {Object.entries(locationsByArea).map(([areaName, locs]) => (
                    <optgroup key={areaName} label={areaName}>
                      {locs.map(loc => (
                        <option key={loc._id} value={loc.name}>{loc.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Shelf / Bin</label>
                <input
                  type="text"
                  disabled={isView}
                  value={formData.shelfBin}
                  onChange={(e) => onChange("shelfBin", e.target.value)}
                  placeholder="e.g. A-1"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">HSN Code</label>
                <input
                  type="text"
                  disabled={isView}
                  value={formData.hsnCode}
                  onChange={(e) => onChange("hsnCode", e.target.value)}
                  placeholder="e.g. 8536"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Pricing & Supplier Details */}
          <div className="border-t border-stone-100 pt-6">
            <h4 className="text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-3">Pricing & Supplier Details</h4>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Purchase Price (₹)</label>
                <input
                  type="number"
                  disabled={isView}
                  value={formData.purchasePrice}
                  onChange={(e) => onChange("purchasePrice", e.target.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Selling Price (₹)</label>
                <input
                  type="number"
                  disabled={isView}
                  value={formData.sellingPrice}
                  onChange={(e) => onChange("sellingPrice", e.target.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Supplier Name</label>
                <input
                  type="text"
                  disabled={isView}
                  value={formData.supplier}
                  onChange={(e) => onChange("supplier", e.target.value)}
                  placeholder="Preferred supplier"
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                  list="suppliers-list"
                />
                <datalist id="suppliers-list">
                  {suppliers.map(s => <option key={s._id} value={s.companyName || s.name} />)}
                </datalist>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Purchase Date</label>
                <input
                  type="date"
                  disabled={isView}
                  value={formData.purchaseDate}
                  onChange={(e) => onChange("purchaseDate", e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Remarks & File Attachments */}
          <div className="border-t border-stone-100 pt-6">
            <h4 className="text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-3">Additional Details & Attachments</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">Remarks / Internal Notes</label>
                <textarea
                  disabled={isView}
                  value={formData.remarks}
                  onChange={(e) => onChange("remarks", e.target.value)}
                  rows={4}
                  placeholder="Write any additional details, storage notes, or manufacturer specifications..."
                  className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                />
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Product Image Upload</label>
                  {!isView ? (
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => onChange("imageFile", e.target.files[0])}
                      className="w-full text-xs text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-[#1E3A8A] hover:file:bg-blue-100"
                    />
                  ) : formData.imageUrl ? (
                    <div className="mt-1">
                      <img src={API_BASE_URL + formData.imageUrl} alt="Product" className="max-h-24 rounded-lg border" />
                    </div>
                  ) : (
                    <span className="text-xs text-stone-400">No image uploaded</span>
                  )}
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Datasheet PDF Upload</label>
                  {!isView ? (
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => onChange("datasheetFile", e.target.files[0])}
                      className="w-full text-xs text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-[#1E3A8A] hover:file:bg-blue-100"
                    />
                  ) : formData.attachments && formData.attachments.length > 0 ? (
                    <div className="mt-1">
                      <a
                        href={API_BASE_URL + formData.attachments[0].fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-[#1E3A8A] font-semibold hover:underline flex items-center gap-1"
                      >
                        📄 Download Datasheet ({formData.attachments[0].fileName})
                      </a>
                    </div>
                  ) : (
                    <span className="text-xs text-stone-400">No datasheet uploaded</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Technical Specifications */}
          <div className="border-t border-stone-100 pt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-[#1E3A8A] uppercase tracking-wider">Technical Specifications</h4>
              {!isView && (
                <button
                  type="button"
                  onClick={addCustomSpec}
                  className="px-3 py-1.5 bg-[#1E3A8A] hover:bg-[#152e72] text-xs font-bold text-white rounded-xl transition shadow-sm"
                >
                  + Add Custom Specification
                </button>
              )}
            </div>

            {/* Technical Specifications Predefined */}
            {formData.subCategory && SUBCATEGORY_SPECS[formData.subCategory] && SUBCATEGORY_SPECS[formData.subCategory].length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-4 bg-stone-50 p-4 rounded-2xl border border-stone-100">
                {SUBCATEGORY_SPECS[formData.subCategory].map(specName => {
                  const currentVal = formData.specifications?.find(s => s.name === specName)?.value || "";
                  return (
                    <div key={specName}>
                      <label className="mb-2 block text-xs font-semibold text-stone-700">{specName}</label>
                      <input
                        type="text"
                        disabled={isView}
                        value={currentVal}
                        onChange={(e) => handleSpecChange(specName, e.target.value)}
                        placeholder={`Enter ${specName.toLowerCase()}`}
                        className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Technical Specifications Custom */}
            {customSpecs.length > 0 ? (
              <div className="space-y-4">
                {customSpecs.map((spec, customIdx) => (
                  <div key={customIdx} className="flex gap-4 items-end bg-stone-50/50 p-4 rounded-2xl border border-stone-200/60 relative pr-12">
                    <div className="flex-1">
                      <label className="mb-2 block text-xs font-semibold text-stone-700">Spec Name / Heading *</label>
                      <input
                        type="text"
                        required
                        disabled={isView}
                        value={spec.name}
                        onChange={(e) => handleCustomSpecNameChange(customIdx, e.target.value)}
                        placeholder="e.g. Voltage, Pole, Breaking Capacity"
                        className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="mb-2 block text-xs font-semibold text-stone-700">Spec Value *</label>
                      <input
                        type="text"
                        required
                        disabled={isView}
                        value={spec.value}
                        onChange={(e) => handleCustomSpecValueChange(customIdx, e.target.value)}
                        placeholder="e.g. 415V, 3 Pole, 36kA"
                        className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] disabled:bg-stone-50 disabled:text-stone-500"
                      />
                    </div>
                    {!isView && (
                      <button
                        type="button"
                        onClick={() => removeCustomSpec(customIdx)}
                        className="absolute right-3 bottom-3.5 p-2 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-xl transition"
                        title="Remove Specification"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-stone-400 italic">No custom specifications added yet. Click "+ Add Custom Specification" above to add new specification boxes with custom headings (e.g. Voltage, Pole).</p>
            )}
          </div>
        </div>

        <div className="mt-8 border-t border-stone-200 pt-5 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-stone-300 hover:bg-stone-50 text-sm font-semibold text-stone-700 transition"
          >
            Cancel
          </button>
          {!isView && (
            <>
              {mode === "add" && (
                <button
                  type="button"
                  onClick={() => onSave(true)}
                  className="px-4 py-2.5 rounded-xl border border-[#1E3A8A] text-[#1E3A8A] hover:bg-blue-50 text-sm font-semibold transition"
                >
                  Save & Add Another
                </button>
              )}
              <button
                type="button"
                onClick={() => onSave(false)}
                className="px-5 py-2.5 rounded-xl bg-[#1E3A8A] hover:bg-[#152e72] text-sm font-semibold text-white transition shadow-sm"
              >
                Save Changes
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StockMovementModal({ type, formData, onChange, onSave, onClose, inventoryItems }) {
  const isStockIn = type === "IN";
  const title = isStockIn ? "Record Stock In (Receipt)" : "Record Stock Out (Issue)";
  const colorClass = isStockIn ? "text-emerald-700" : "text-rose-700";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/45 px-4 py-6">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-stone-200 pb-5">
          <div>
            <p className={`text-sm font-semibold uppercase tracking-[0.25em] ${colorClass}`}>{isStockIn ? "Receipt" : "Issue"}</p>
            <h3 className="mt-2 text-2xl font-bold text-stone-950">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
          >
            Close
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label className="mb-2 block text-xs font-semibold text-stone-700">Select Material *</label>
            <select
              required
              value={formData.productId}
              onChange={(e) => onChange("productId", e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] bg-white focus:ring-1 focus:ring-[#1E3A8A]"
            >
              <option value="">-- Choose Material --</option>
              {inventoryItems.map(item => (
                <option key={item.productId || item._id} value={item.productId || item._id}>
                  {item.productName} {item.modelNumber ? `(${item.modelNumber})` : ""} [Stock: {item.currentStock ?? 0}]
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold text-stone-700">Quantity *</label>
            <input
              type="number"
              required
              min="0.01"
              step="0.01"
              value={formData.quantity}
              onChange={(e) => onChange("quantity", e.target.value)}
              placeholder="0"
              className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
            />
          </div>

          {isStockIn ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Purchase Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.purchaseDate}
                    onChange={(e) => onChange("purchaseDate", e.target.value)}
                    className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Purchase Price (per unit)</label>
                  <input
                    type="number"
                    value={formData.purchasePrice}
                    onChange={(e) => onChange("purchasePrice", e.target.value)}
                    placeholder="0.00"
                    className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Supplier</label>
                  <input
                    type="text"
                    value={formData.supplier}
                    onChange={(e) => onChange("supplier", e.target.value)}
                    placeholder="Preferred supplier"
                    className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Invoice Number</label>
                  <input
                    type="text"
                    value={formData.invoiceNumber}
                    onChange={(e) => onChange("invoiceNumber", e.target.value)}
                    placeholder="e.g. INV-1024"
                    className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Issued To</label>
                  <input
                    type="text"
                    value={formData.issuedTo}
                    onChange={(e) => onChange("issuedTo", e.target.value)}
                    placeholder="Receiver name"
                    className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => onChange("department", e.target.value)}
                    placeholder="e.g. Electrical Dept"
                    className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Project Name</label>
                  <input
                    type="text"
                    value={formData.projectName}
                    onChange={(e) => onChange("projectName", e.target.value)}
                    placeholder="Project name / panel ID"
                    className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold text-stone-700">Engineer Name</label>
                  <input
                    type="text"
                    value={formData.engineerName}
                    onChange={(e) => onChange("engineerName", e.target.value)}
                    placeholder="Overseeing engineer"
                    className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="mb-2 block text-xs font-semibold text-stone-700">Remarks / Internal Notes</label>
            <input
              type="text"
              value={formData.remarks}
              onChange={(e) => onChange("remarks", e.target.value)}
              placeholder="Write any additional remarks..."
              className="w-full px-4 py-2.5 text-sm border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
            />
          </div>
        </div>

        <div className="mt-8 border-t border-stone-200 pt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-stone-300 hover:bg-stone-50 text-sm font-semibold text-stone-700 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition shadow-sm ${isStockIn ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
          >
            Save Movement
          </button>
        </div>
      </div>
    </div>
  );
}

function ExcelImportModal({ onClose, onSuccess }) {
  const [activeTab, setActiveTab] = useState("excel"); // "excel" | "json"
  const [importLoading, setImportLoading] = useState(false);
  
  // Excel Tab States
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState("");
  const [parsedRecords, setParsedRecords] = useState([]);
  const [excelError, setExcelError] = useState("");
  const [importSummary, setImportSummary] = useState(null);

  // JSON Tab States
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState("");

  const handleDownloadSample = () => {
    try {
      const headers = [[
        "Stock Item", "Category", "Sub Category", "Brand", "Manufacturer", 
        "Part Number", "Model Number", "Location / Area", "Shelf Bin", "Unit", 
        "Opening Stock", "Minimum Stock", "Purchase Price", "Selling Price", 
        "Remarks", "Supplier", "Pole", "Breaking Capacity", "Coil Voltage", "CT Ratio"
      ]];
      const rows = [
        ["MCCB 100A 3 Pole", "Electrical", "MCCB", "Schneider", "Schneider Electric", "LV429630", "NSX100F", "Rack 1", "Bin A", "Nos", 10, 2, 4500, 5200, "Standard trip unit", "Genmech Supplier A", "3 Pole", "36 kA", "", ""],
        ["MCB 16A Single Pole", "Electrical", "MCB", "Siemens", "Siemens Ltd", "5SL61167RC", "5SL6", "Rack 2", "Bin B", "Nos", 50, 10, 180, 220, "C curve", "Genmech Supplier B", "Single Pole", "10 kA", "", ""],
        ["Contactor 9A 230V AC", "Electrical", "Contactor", "ABB", "ABB India", "1SBL137001R1310", "AX09", "Rack 1", "Bin C", "Nos", 15, 3, 650, 750, "Coil voltage 230V", "Genmech Supplier A", "", "", "230V AC", ""],
        ["CT 100/5A Class 1.0", "Electrical", "Current Transformer", "Kappa", "Kappa Transformers", "KCT-30", "KCT-30-100", "Rack 3", "Bin A", "Nos", 8, 2, 450, 520, "Resin cast", "Genmech Supplier C", "", "", "", "100/5A"]
      ];
      const ws = XLSX.utils.aoa_to_sheet([...headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Inventory Template");
      XLSX.writeFile(wb, "genmech_inventory_template.xlsx");
    } catch (err) {
      alert("Failed to generate Excel template: " + err.message);
    }
  };

  const processFile = (file) => {
    setFileName(file.name);
    setExcelError("");
    setImportSummary(null);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const records = [];

        workbook.SheetNames.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          if (rows.length < 1) return;

          const headers = rows[0].map(h => String(h || "").trim());
          
          // Identify index of standard headers
          const nameIdx = headers.findIndex(h => /stock\s*item|product|item\s*name|name/i.test(h));
          const categoryIdx = headers.findIndex(h => /^category$/i.test(h));
          const subCategoryIdx = headers.findIndex(h => /^sub\s*category$/i.test(h));
          const brandIdx = headers.findIndex(h => /^brand$/i.test(h));
          const manufacturerIdx = headers.findIndex(h => /^manufacturer$/i.test(h));
          const partNumIdx = headers.findIndex(h => /^part\s*number$/i.test(h));
          const modelNumIdx = headers.findIndex(h => /^model\s*number$/i.test(h));
          const rackIdx = headers.findIndex(h => /^rack(\s*number)?$|^location(\s*area|\s*or\s*area|\s*\/\s*area)?$|^area$/i.test(h));
          const binIdx = headers.findIndex(h => /^shelf(\s*bin)?$|^bin$/i.test(h));
          const unitIdx = headers.findIndex(h => /^unit$/i.test(h));
          const qtyIdx = headers.findIndex(h => /opening\s*stock|quantity|qty|nos/i.test(h));
          const minStockIdx = headers.findIndex(h => /minimum\s*stock|min\s*stock/i.test(h));
          const purchasePriceIdx = headers.findIndex(h => /purchase\s*price/i.test(h));
          const sellingPriceIdx = headers.findIndex(h => /selling\s*price/i.test(h));
          const remarksIdx = headers.findIndex(h => /remarks|notes|description/i.test(h));
          const supplierIdx = headers.findIndex(h => /supplier/i.test(h));

          const standardIndices = [
            nameIdx, categoryIdx, subCategoryIdx, brandIdx, manufacturerIdx,
            partNumIdx, modelNumIdx, rackIdx, binIdx, unitIdx, qtyIdx,
            minStockIdx, purchasePriceIdx, sellingPriceIdx, remarksIdx, supplierIdx
          ].filter(idx => idx !== -1);

          const specColumns = [];
          headers.forEach((header, idx) => {
            if (header && !standardIndices.includes(idx)) {
              specColumns.push({ name: header, index: idx });
            }
          });

          // Detect Rack Number from sheet name or title
          const rackMatch = sheetName.match(/Rack\s*[1-8]/i) || sheetName.match(/Rack\s*\d+/i);
          const sheetRack = rackMatch ? rackMatch[0] : "";

          for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            if (!row || row.length === 0) continue;

            const itemName = nameIdx !== -1 && row[nameIdx] !== undefined ? String(row[nameIdx]).trim() : "";
            const category = categoryIdx !== -1 && row[categoryIdx] !== undefined ? String(row[categoryIdx]).trim() : "";
            const subCategory = subCategoryIdx !== -1 && row[subCategoryIdx] !== undefined ? String(row[subCategoryIdx]).trim() : "";
            const brand = brandIdx !== -1 && row[brandIdx] !== undefined ? String(row[brandIdx]).trim() : "";
            const manufacturer = manufacturerIdx !== -1 && row[manufacturerIdx] !== undefined ? String(row[manufacturerIdx]).trim() : "";
            const partNumber = partNumIdx !== -1 && row[partNumIdx] !== undefined ? String(row[partNumIdx]).trim() : "";
            const modelNumber = modelNumIdx !== -1 && row[modelNumIdx] !== undefined ? String(row[modelNumIdx]).trim() : "";
            const rackVal = rackIdx !== -1 && row[rackIdx] !== undefined ? String(row[rackIdx]).trim() : "";
            const binVal = binIdx !== -1 && row[binIdx] !== undefined ? String(row[binIdx]).trim() : "";
            const unit = unitIdx !== -1 && row[unitIdx] !== undefined ? String(row[unitIdx]).trim() : "Nos";
            const qtyVal = qtyIdx !== -1 && row[qtyIdx] !== undefined ? row[qtyIdx] : "";
            const minStockVal = minStockIdx !== -1 && row[minStockIdx] !== undefined ? row[minStockIdx] : "";
            const purchasePriceVal = purchasePriceIdx !== -1 && row[purchasePriceIdx] !== undefined ? row[purchasePriceIdx] : "";
            const sellingPriceVal = sellingPriceIdx !== -1 && row[sellingPriceIdx] !== undefined ? row[sellingPriceIdx] : "";
            const remarks = remarksIdx !== -1 && row[remarksIdx] !== undefined ? String(row[remarksIdx]).trim() : "";
            const supplier = supplierIdx !== -1 && row[supplierIdx] !== undefined ? String(row[supplierIdx]).trim() : "";

            if (!itemName && !qtyVal && !rackVal) continue;

            const openingStock = qtyVal === "" ? 0 : Number(qtyVal);
            const minimumStock = minStockVal === "" ? 1 : Number(minStockVal);
            const purchasePrice = purchasePriceVal === "" ? 0 : Number(purchasePriceVal);
            const sellingPrice = sellingPriceVal === "" ? 0 : Number(sellingPriceVal);
            const rackNumber = rackVal || sheetRack || "";

            // Populate specifications
            const specifications = [];
            specColumns.forEach(col => {
              const val = row[col.index];
              if (val !== undefined && String(val).trim() !== "") {
                specifications.push({
                  name: col.name,
                  value: String(val).trim()
                });
              }
            });

            records.push({
              id: Math.random().toString(36).substr(2, 9),
              productName: itemName,
              category,
              subCategory,
              brand,
              manufacturer,
              partNumber,
              modelNumber,
              rackNumber,
              shelfBin: binVal,
              unit,
              openingStock: Number.isNaN(openingStock) ? qtyVal : openingStock,
              minimumStock: Number.isNaN(minimumStock) ? minStockVal : minimumStock,
              purchasePrice: Number.isNaN(purchasePrice) ? purchasePriceVal : purchasePrice,
              sellingPrice: Number.isNaN(sellingPrice) ? sellingPriceVal : sellingPrice,
              remarks,
              supplier,
              specifications
            });
          }
        });

        if (records.length === 0) {
          setExcelError("No valid rows containing material information could be found in the sheet(s).");
        } else {
          setParsedRecords(records);
        }
      } catch (err) {
        setExcelError("Failed to parse Excel file: " + err.message);
      }
    };

    reader.onerror = () => {
      setExcelError("Error reading file.");
    };

    reader.readAsArrayBuffer(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragOver(true);
    } else if (e.type === "dragleave") {
      setDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext === "xlsx" || ext === "xls") {
        processFile(file);
      } else {
        setExcelError("Invalid file format. Please upload an Excel sheet (.xlsx, .xls).");
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleEditCell = (id, field, value) => {
    setParsedRecords(prev =>
      prev.map(row => {
        if (row.id !== id) return row;
        let val = value;
        if (field === "openingStock" || field === "minimumStock" || field === "purchasePrice" || field === "sellingPrice") {
          const parsedNum = Number(value);
          val = Number.isNaN(parsedNum) ? value : parsedNum;
        }
        return { ...row, [field]: val };
      })
    );
  };

  const handleRemoveRow = (id) => {
    setParsedRecords(prev => prev.filter(row => row.id !== id));
  };

  const getValidationErrors = (row) => {
    const errors = [];
    if (!row.productName || !String(row.productName).trim()) {
      errors.push("Stock Item Name is required");
    }
    if (row.openingStock === undefined || row.openingStock === "" || Number.isNaN(Number(row.openingStock))) {
      errors.push("Quantity must be a valid number");
    }
    if (row.minimumStock === undefined || row.minimumStock === "" || Number.isNaN(Number(row.minimumStock))) {
      errors.push("Minimum Stock must be a valid number");
    }
    if (row.purchasePrice !== undefined && row.purchasePrice !== "" && Number.isNaN(Number(row.purchasePrice))) {
      errors.push("Purchase Price must be a valid number");
    }
    if (row.sellingPrice !== undefined && row.sellingPrice !== "" && Number.isNaN(Number(row.sellingPrice))) {
      errors.push("Selling Price must be a valid number");
    }
    return errors;
  };

  const handleImportExcelData = async () => {
    const validRecords = parsedRecords.filter(row => getValidationErrors(row).length === 0);
    if (validRecords.length === 0) {
      alert("No valid rows to import. Please resolve validation errors.");
      return;
    }

    try {
      setImportLoading(true);
      // Map frontend fields to match backend input fields expected in controller
      const payload = validRecords.map(row => ({
        productName: row.productName,
        category: row.category || "",
        subCategory: row.subCategory || "",
        brand: row.brand || "",
        manufacturer: row.manufacturer || "",
        partNumber: row.partNumber || "",
        modelNumber: row.modelNumber || "",
        rackNumber: row.rackNumber || "",
        shelfBin: row.shelfBin || "",
        unit: row.unit || "Nos",
        openingStock: Number(row.openingStock) || 0,
        currentStock: Number(row.openingStock) || 0,
        minimumStock: Number(row.minimumStock) || 1,
        purchasePrice: Number(row.purchasePrice) || 0,
        sellingPrice: Number(row.sellingPrice) || 0,
        remarks: row.remarks || "",
        supplier: row.supplier || "",
        specifications: row.specifications || []
      }));

      const res = await axios.post(API_BASE_URL + "/api/material-stock/inventory/import", payload);
      setImportSummary({
        success: true,
        inserted: res.data.inserted || 0,
        updated: res.data.updated || 0,
        total: validRecords.length,
        errors: parsedRecords.length - validRecords.length
      });
      setParsedRecords([]);
      setFileName("");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to process Excel import");
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportJsonData = async () => {
    setJsonError("");
    try {
      setImportLoading(true);
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) {
        setJsonError("JSON must be an array of inventory items.");
        setImportLoading(false);
        return;
      }
      const res = await axios.post(API_BASE_URL + "/api/material-stock/inventory/import", parsed);
      alert(`Import completed successfully! Inserted: ${res.data.inserted}, Updated: ${res.data.updated}`);
      onSuccess();
    } catch (err) {
      setJsonError(err.message || "Invalid JSON syntax or import failed");
    } finally {
      setImportLoading(false);
    }
  };

  const handleReset = () => {
    setFileName("");
    setParsedRecords([]);
    setExcelError("");
    setImportSummary(null);
  };

  const totalErrorsCount = parsedRecords.reduce((sum, row) => sum + (getValidationErrors(row).length > 0 ? 1 : 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/45 px-4 py-6">
      <div className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-stone-200 pb-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1E3A8A]">Inventory Importer</p>
            <h3 className="mt-2 text-2xl font-bold text-stone-950">Bulk Import Materials</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
          >
            Close
          </button>
        </div>

        {/* Tab Controls */}
        <div className="mt-4 flex gap-4 border-b border-stone-100 pb-2">
          <button
            type="button"
            onClick={() => setActiveTab("excel")}
            className={`pb-2 text-sm font-bold border-b-2 transition ${activeTab === "excel" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-stone-400 hover:text-stone-600"}`}
          >
            Excel Import
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("json")}
            className={`pb-2 text-sm font-bold border-b-2 transition ${activeTab === "json" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-stone-400 hover:text-stone-600"}`}
          >
            Advanced (JSON)
          </button>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === "excel" ? (
            <div className="space-y-6">
              {importSummary && (
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-950">
                  <h4 className="font-bold text-lg">✓ Import Completed Successfully!</h4>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="block text-stone-500 font-medium">New Products Created</span>
                      <span className="text-xl font-bold text-emerald-800">{importSummary.inserted}</span>
                    </div>
                    <div>
                      <span className="block text-stone-500 font-medium">Stock Levels Updated</span>
                      <span className="text-xl font-bold text-emerald-800">{importSummary.updated}</span>
                    </div>
                    <div>
                      <span className="block text-stone-500 font-medium">Total Imported Rows</span>
                      <span className="text-xl font-bold text-stone-900">{importSummary.total}</span>
                    </div>
                    {importSummary.errors > 0 && (
                      <div>
                        <span className="block text-rose-500 font-medium">Excluded Invalid Rows</span>
                        <span className="text-xl font-bold text-rose-600">{importSummary.errors}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setImportSummary(null);
                        onSuccess();
                      }}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-xs font-bold text-white uppercase tracking-wider rounded-xl transition shadow-sm"
                    >
                      Dismiss & Refresh Catalog
                    </button>
                  </div>
                </div>
              )}

              {parsedRecords.length === 0 ? (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-stone-50 border border-stone-200 p-4 rounded-2xl">
                    <div>
                      <h4 className="font-bold text-stone-800 text-sm">Inventory Import Sheet Template</h4>
                      <p className="text-xs text-stone-500 mt-1">Get the pre-formatted Excel structure with name, category, brand, and custom specs.</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadSample}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-stone-300 hover:bg-stone-50 rounded-xl text-xs font-bold text-stone-700 transition"
                    >
                      📥 Download Sample Excel
                    </button>
                  </div>

                  <div
                    onDragEnter={handleDrag}
                    onDragOver={handleDrag}
                    onDragLeave={handleDrag}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-3xl p-10 text-center transition flex flex-col items-center justify-center min-h-[220px] ${dragOver ? "border-[#1E3A8A] bg-blue-50/20" : "border-stone-300 hover:border-stone-400 bg-stone-50/50"}`}
                  >
                    <div className="p-3 bg-blue-50 text-[#1E3A8A] rounded-2xl mb-4">
                      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2a4 4 0 00-4-4H3m18 0h-2a4 4 0 00-4 4v2m-3-11v8m0 0l-3-3m3 3l3-3" />
                      </svg>
                    </div>
                    <p className="text-sm font-bold text-stone-800">Drag and drop your Excel file here</p>
                    <p className="text-xs text-stone-500 mt-1">Supports .xlsx and .xls formats</p>
                    <div className="mt-4">
                      <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-[#1E3A8A] hover:bg-[#152e72] text-xs font-bold uppercase tracking-wider text-white rounded-xl shadow-sm transition">
                        Browse Files
                        <input
                          type="file"
                          accept=".xlsx, .xls"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>

                  {excelError && (
                    <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 p-3 text-xs font-semibold">
                      ⚠️ {excelError}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h4 className="font-bold text-stone-800 text-sm">Preview Records ({parsedRecords.length} rows)</h4>
                      <p className="text-xs text-stone-500 mt-0.5">File: <span className="font-semibold text-stone-700">{fileName}</span></p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleReset}
                        className="px-3 py-1.5 border border-stone-300 hover:bg-stone-50 rounded-xl text-xs font-bold text-stone-600 transition"
                      >
                        Reset / Upload Different
                      </button>
                    </div>
                  </div>

                  {totalErrorsCount > 0 && (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 p-3 text-xs">
                      <span className="font-bold">⚠️ Validation Warnings</span>: We detected <span className="font-bold text-rose-600">{totalErrorsCount} row(s)</span> with malformed data. You can edit their cells directly in the table below to correct them, or proceed to import only the valid rows (invalid rows will be excluded).
                    </div>
                  )}

                  {/* Editable Preview Table */}
                  <div className="overflow-auto border border-stone-200 rounded-2xl max-h-[360px] scrollbar-thin scrollbar-thumb-stone-200">
                    <table className="w-full text-left border-collapse min-w-[1400px]">
                      <thead className="bg-stone-50 text-stone-700 border-b border-stone-200 sticky top-0 z-10 text-xs font-bold uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3 text-center">S.No</th>
                          <th className="px-4 py-3">Stock Item *</th>
                          <th className="px-4 py-3">Category/Sub Category</th>
                          <th className="px-4 py-3">Brand/Mfg</th>
                          <th className="px-4 py-3">Part/Model</th>
                          <th className="px-4 py-3">Rack/Shelf</th>
                          <th className="px-4 py-3 text-right">Qty *</th>
                          <th className="px-4 py-3 text-right">Min Stock *</th>
                          <th className="px-4 py-3 text-right">Pur. Price</th>
                          <th className="px-4 py-3 text-right">Sel. Price</th>
                          <th className="px-4 py-3">Specifications</th>
                          <th className="px-4 py-3 text-center">Status</th>
                          <th className="px-4 py-3 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100 text-sm">
                        {parsedRecords.map((row, index) => {
                          const errors = getValidationErrors(row);
                          const isValid = errors.length === 0;

                          return (
                            <tr key={row.id} className={`hover:bg-stone-50/50 transition ${!isValid ? "bg-rose-50/20" : ""}`}>
                              <td className="px-4 py-2.5 text-center font-medium text-stone-400">{index + 1}</td>
                              <td className="px-4 py-2.5">
                                <input
                                  type="text"
                                  value={row.productName}
                                  onChange={(e) => handleEditCell(row.id, "productName", e.target.value)}
                                  className={`w-48 bg-transparent px-2 py-1 rounded border outline-none text-sm transition focus:bg-white ${!row.productName ? "border-rose-300 focus:border-rose-500 focus:ring-1 focus:ring-rose-500" : "border-transparent focus:border-[#1E3A8A]"}`}
                                  placeholder="Item name"
                                />
                              </td>
                              <td className="px-4 py-2.5 space-y-1">
                                <input
                                  type="text"
                                  value={row.category}
                                  onChange={(e) => handleEditCell(row.id, "category", e.target.value)}
                                  className="w-32 bg-transparent px-2 py-0.5 rounded border border-transparent focus:border-[#1E3A8A] outline-none text-xs transition focus:bg-white"
                                  placeholder="Category"
                                />
                                <input
                                  type="text"
                                  value={row.subCategory}
                                  onChange={(e) => handleEditCell(row.id, "subCategory", e.target.value)}
                                  className="w-32 bg-transparent px-2 py-0.5 rounded border border-transparent focus:border-[#1E3A8A] outline-none text-xs transition focus:bg-white"
                                  placeholder="Sub Category"
                                />
                              </td>
                              <td className="px-4 py-2.5 space-y-1">
                                <input
                                  type="text"
                                  value={row.brand}
                                  onChange={(e) => handleEditCell(row.id, "brand", e.target.value)}
                                  className="w-32 bg-transparent px-2 py-0.5 rounded border border-transparent focus:border-[#1E3A8A] outline-none text-xs transition focus:bg-white"
                                  placeholder="Brand"
                                />
                                <input
                                  type="text"
                                  value={row.manufacturer}
                                  onChange={(e) => handleEditCell(row.id, "manufacturer", e.target.value)}
                                  className="w-32 bg-transparent px-2 py-0.5 rounded border border-transparent focus:border-[#1E3A8A] outline-none text-xs transition focus:bg-white"
                                  placeholder="Manufacturer"
                                />
                              </td>
                              <td className="px-4 py-2.5 space-y-1">
                                <input
                                  type="text"
                                  value={row.partNumber}
                                  onChange={(e) => handleEditCell(row.id, "partNumber", e.target.value)}
                                  className="w-32 bg-transparent px-2 py-0.5 rounded border border-transparent focus:border-[#1E3A8A] outline-none text-xs transition focus:bg-white"
                                  placeholder="Part Number"
                                />
                                <input
                                  type="text"
                                  value={row.modelNumber}
                                  onChange={(e) => handleEditCell(row.id, "modelNumber", e.target.value)}
                                  className="w-32 bg-transparent px-2 py-0.5 rounded border border-transparent focus:border-[#1E3A8A] outline-none text-xs transition focus:bg-white"
                                  placeholder="Model Number"
                                />
                              </td>
                              <td className="px-4 py-2.5 space-y-1">
                                <input
                                  type="text"
                                  value={row.rackNumber}
                                  onChange={(e) => handleEditCell(row.id, "rackNumber", e.target.value)}
                                  className="w-24 bg-transparent px-2 py-0.5 rounded border border-transparent focus:border-[#1E3A8A] outline-none text-xs transition focus:bg-white"
                                  placeholder="Rack"
                                />
                                <input
                                  type="text"
                                  value={row.shelfBin}
                                  onChange={(e) => handleEditCell(row.id, "shelfBin", e.target.value)}
                                  className="w-24 bg-transparent px-2 py-0.5 rounded border border-transparent focus:border-[#1E3A8A] outline-none text-xs transition focus:bg-white"
                                  placeholder="Shelf/Bin"
                                />
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <input
                                  type="text"
                                  value={row.openingStock}
                                  onChange={(e) => handleEditCell(row.id, "openingStock", e.target.value)}
                                  className={`w-16 text-right bg-transparent px-2 py-1 rounded border outline-none text-sm transition focus:bg-white ${Number.isNaN(Number(row.openingStock)) ? "border-rose-300 focus:border-rose-500 focus:ring-1 focus:ring-rose-500" : "border-transparent focus:border-[#1E3A8A]"}`}
                                />
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <input
                                  type="text"
                                  value={row.minimumStock}
                                  onChange={(e) => handleEditCell(row.id, "minimumStock", e.target.value)}
                                  className={`w-16 text-right bg-transparent px-2 py-1 rounded border outline-none text-sm transition focus:bg-white ${Number.isNaN(Number(row.minimumStock)) ? "border-rose-300 focus:border-rose-500 focus:ring-1 focus:ring-rose-500" : "border-transparent focus:border-[#1E3A8A]"}`}
                                />
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <input
                                  type="text"
                                  value={row.purchasePrice}
                                  onChange={(e) => handleEditCell(row.id, "purchasePrice", e.target.value)}
                                  className={`w-20 text-right bg-transparent px-2 py-1 rounded border outline-none text-sm transition focus:bg-white ${Number.isNaN(Number(row.purchasePrice)) ? "border-rose-300 focus:border-rose-500 focus:ring-1 focus:ring-rose-500" : "border-transparent focus:border-[#1E3A8A]"}`}
                                />
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <input
                                  type="text"
                                  value={row.sellingPrice}
                                  onChange={(e) => handleEditCell(row.id, "sellingPrice", e.target.value)}
                                  className={`w-20 text-right bg-transparent px-2 py-1 rounded border outline-none text-sm transition focus:bg-white ${Number.isNaN(Number(row.sellingPrice)) ? "border-rose-300 focus:border-rose-500 focus:ring-1 focus:ring-rose-500" : "border-transparent focus:border-[#1E3A8A]"}`}
                                />
                              </td>
                              <td className="px-4 py-2.5">
                                {row.specifications && row.specifications.length > 0 ? (
                                  <div className="flex flex-wrap gap-1 max-w-[200px]">
                                    {row.specifications.map((spec, sIdx) => (
                                      <span key={sIdx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-800 border border-blue-100" title={`${spec.name}: ${spec.value}`}>
                                        {spec.name}: {spec.value}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-stone-400 italic">-</span>
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                {isValid ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800">
                                    ✓ Valid
                                  </span>
                                ) : (
                                  <span
                                    title={errors.join(", ")}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-800 cursor-help"
                                  >
                                    ❌ Invalid
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveRow(row.id)}
                                  className="p-1 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-rose-600 transition"
                                  title="Exclude row"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3">
                    <span className="text-xs text-stone-500">
                      Only valid rows will be processed. Excluded or invalid rows can be corrected or imported later.
                    </span>
                    <button
                      type="button"
                      disabled={importLoading || (parsedRecords.length - totalErrorsCount === 0)}
                      onClick={handleImportExcelData}
                      className="px-5 py-2.5 rounded-xl bg-[#1E3A8A] hover:bg-[#152e72] text-xs font-bold uppercase tracking-wider text-white transition shadow-sm disabled:opacity-50"
                    >
                      {importLoading ? "Processing Import..." : `Import ${parsedRecords.length - totalErrorsCount} Valid Record(s)`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-stone-500 leading-relaxed">
                Paste a raw JSON Array of material items directly. This format maps directly to database schemas.
              </p>
              
              <div>
                <label className="mb-2 block text-xs font-semibold text-stone-700">JSON Array Payload *</label>
                <textarea
                  required
                  rows={10}
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  placeholder="[ { &quot;productName&quot;: &quot;MCCB&quot;, &quot;openingStock&quot;: 10 } ]"
                  className="w-full font-mono text-xs px-4 py-2.5 border border-stone-200 rounded-xl outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]"
                />
              </div>

              {jsonError && (
                <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 p-3 text-xs font-semibold">
                  ⚠️ {jsonError}
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  disabled={importLoading || !jsonText.trim()}
                  onClick={handleImportJsonData}
                  className="px-5 py-2.5 rounded-xl bg-[#1E3A8A] hover:bg-[#152e72] text-xs font-bold uppercase tracking-wider text-white transition shadow-sm disabled:opacity-50"
                >
                  {importLoading ? "Importing JSON..." : "Process JSON Import"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CheckboxInput({ label, ...props }) {
  return (
    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none py-1.5 px-3 bg-white rounded-[4px] border border-slate-200 h-9">
      <input {...props} type="checkbox" className="h-4 w-4 rounded-[4px] border-slate-300 text-blue-600 focus:ring-blue-500" />
      <span className="truncate">{label}</span>
    </label>
  );
}

function SummaryRow({ label, value, strong = false }) {
  return (
    <div className={`flex items-center justify-between rounded-[4px] bg-white px-3 py-2 border border-slate-200 ${strong ? "font-bold text-slate-900 text-base" : "text-sm text-slate-600"}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
